import { useState, useRef, useCallback, useEffect, createContext, useContext } from "react";

// ─── THEMES ─────────────────────────────────────────────────────
const themes = {
  dark: {
    bg: "#0c0c18", surface: "#1c1c32", surfaceHi: "#28284a",
    border: "#3a3a5c", accent: "#60a5fa", accentCyan: "#22d3ee",
    accentDim: "rgba(96,165,250,0.12)", accentBorder: "rgba(96,165,250,0.35)",
    fail: "#f87171", failDim: "rgba(248,113,113,0.10)", warn: "#fbbf24",
    text: "#f0f0f8", textDim: "#a0a0c0", textMuted: "#6e6e90",
    botBg: "rgba(96,165,250,0.08)", botBorder: "rgba(96,165,250,0.20)",
    userBg: "rgba(167,139,250,0.10)", userBorder: "rgba(167,139,250,0.25)",
    inputBg: "#24243e", shadow: "0 4px 24px rgba(0,0,0,0.5)", checkColor: "#fff",
    success: "#4ade80", successDim: "rgba(74,222,128,0.12)",
    rowAlt: "rgba(255,255,255,0.03)",
  },
  light: {
    bg: "#f8fafc", surface: "#ffffff", surfaceHi: "#f1f5f9",
    border: "#e2e8f0", accent: "#2563eb", accentCyan: "#0891b2",
    accentDim: "rgba(37,99,235,0.06)", accentBorder: "rgba(37,99,235,0.2)",
    fail: "#ef4444", failDim: "rgba(239,68,68,0.05)", warn: "#f97316",
    text: "#1e293b", textDim: "#64748b", textMuted: "#94a3b8",
    botBg: "rgba(37,99,235,0.04)", botBorder: "rgba(37,99,235,0.12)",
    userBg: "rgba(139,92,246,0.05)", userBorder: "rgba(139,92,246,0.12)",
    inputBg: "#f1f5f9", shadow: "0 1px 3px rgba(0,0,0,0.08)", checkColor: "#fff",
    success: "#22c55e", successDim: "rgba(34,197,94,0.06)",
    rowAlt: "rgba(0,0,0,0.02)",
  },
};
const ThemeCtx = createContext(themes.dark);
const useTheme = () => useContext(ThemeCtx);
const mono = "'JetBrains Mono', monospace";
const sans = "'DM Sans', -apple-system, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif";

// ─── API ────────────────────────────────────────────────────────
const API_URL = "/api/claude";

async function fetchWithRetry(url, opts, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      const r = await fetch(url, opts);
      if (r.status === 429 || r.status >= 500) {
        if (i < retries) { await new Promise(ok => setTimeout(ok, 1000 * (i + 1))); continue; }
      }
      return r;
    } catch (err) {
      if (i < retries) { await new Promise(ok => setTimeout(ok, 1000 * (i + 1))); continue; }
      throw err;
    }
  }
}

async function callClaude(messages, systemPrompt) {
  try {
    const r = await fetchWithRetry(API_URL, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 4096, system: systemPrompt, messages }),
    });
    const d = await r.json();
    if (d.error) return `Error: ${d.error?.message || JSON.stringify(d.error)}`;
    return d.content?.[0]?.text || "Couldn't process that.";
  } catch { return "Connection issue — try again."; }
}

// Detect placeholder serial numbers / asset tags that should never pass.
// Catches all-zeros, all-same-character, all-X, blank, and obviously-fake patterns
// regardless of separators (dashes, spaces, dots).
function isPlaceholderSerial(value) {
  if (!value) return false;
  const s = String(value).replace(/[\s\-_./]/g, "");
  if (!s) return false;
  if (s.length < 3) return true;
  if (/^0+$/.test(s)) return true;                // all zeros
  if (/^(.)\1+$/i.test(s)) return true;           // all same char
  if (/^x+$/i.test(s)) return true;               // all X
  if (/^(?:n\/?a|none|null|unknown|tbd|test|sample|placeholder)$/i.test(s)) return true;
  return false;
}

// Scan the AI's message for any token that looks like a serial/asset tag and
// reject if it's a placeholder. Forces deterministic fail even when AI is lenient.
function postCheckSerials(message) {
  if (!message) return null;
  // Find tokens after "serial", "s/n", "sn:", "asset", "tag" labels, or quoted strings
  const patterns = [
    /\b(?:serial(?:\s*(?:no|number|#))?|s\/n|sn|asset(?:\s*(?:tag|id|#))?|tag)\s*[:#=]?\s*([A-Z0-9][A-Z0-9\-_./ ]{2,30})/gi,
    /["']([A-Z0-9][A-Z0-9\-_./ ]{2,30})["']/g,
  ];
  for (const re of patterns) {
    let m;
    while ((m = re.exec(message)) !== null) {
      const candidate = m[1].trim();
      if (isPlaceholderSerial(candidate)) {
        return `Serial/asset value "${candidate}" is a placeholder (all zeros, repeated char, or blank) — reject and capture a real serial.`;
      }
    }
  }
  return null;
}

async function analyzePhoto(b64, prompt) {
  try {
    const mt = b64.includes("image/png") ? "image/png" : "image/jpeg";
    // Wrap the PM's custom prompt with explicit JSON response requirements
    const wrappedPrompt = `You are a photo verification assistant for a field ops technician. Analyze the attached photo against this specific verification criteria:

"${prompt}"

Respond with ONLY a JSON object (no markdown, no backticks, no explanation), exactly in this format:
{"pass": true/false, "message": "one sentence explaining what you see and why it passes or fails"}

Guidelines:
- Set pass:true ONLY if the photo clearly demonstrates what the criteria asks for
- Set pass:false if the photo is unclear, unrelated, or doesn't satisfy the criteria
- Keep the message under 150 characters
- Be specific about what you see in the photo
- If the criteria mentions an IP address, only consider it passing if that IP is clearly visible
- If the criteria asks about physical work (cables, labels, cleanup, etc.), judge the visible quality of that work

SERIAL NUMBERS / ASSET TAGS — be strict:
- A serial/asset tag photo MUST show a real, legible value to pass
- ALWAYS fail (pass:false) if the visible serial/asset/MAC is any of:
  - all zeros (e.g. "0000000000")
  - the same character or digit repeated (e.g. "1111", "AAAA", "XXXXXX")
  - all X's, "N/A", "none", "TEST", "SAMPLE", or other placeholder text
  - blank, blurred beyond reading, or shorter than the manufacturer's expected format
- When you see a serial/asset value, quote it back inside the message field exactly (e.g. \`Serial: ABC1234\`) so we can audit it
- Do not assume a placeholder is "fine for now" — fail it

Example responses:
{"pass": true, "message": "All cables are neatly bundled with velcro straps and properly labeled at both ends."}
{"pass": false, "message": "Cables are loose and unlabeled. Work does not meet criteria."}
{"pass": true, "message": "Screen clearly shows IP address 192.168.200.1 matching the requirement."}
{"pass": false, "message": "Serial label reads '0000000000' — placeholder value, capture the real device serial."}`;

    const r = await fetchWithRetry(API_URL, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, temperature: 0,
        messages: [{ role: "user", content: [
          { type: "image", source: { type: "base64", media_type: mt, data: b64.split(",")[1] } },
          { type: "text", text: wrappedPrompt },
        ] }],
      }),
    });
    const d = await r.json();
    if (d.error) return { pass: false, message: `AI error: ${d.error?.message || "unknown"}` };
    const text = (d.content?.[0]?.text || "").replace(/```json|```/g, "").trim();
    let result = null;
    const jsonMatch = text.match(/\{[\s\S]*?"pass"[\s\S]*?\}/);
    if (jsonMatch) {
      try { result = JSON.parse(jsonMatch[0]); } catch {}
    }
    if (!result) {
      try { result = JSON.parse(text); } catch {
        const lower = text.toLowerCase();
        const pass = /\b(pass|yes|correct|verified|ok|good|valid)\b/.test(lower) && !/\b(fail|no|incorrect|invalid|not)\b/.test(lower);
        result = { pass, message: text.slice(0, 200) || "Photo analyzed." };
      }
    }
    // Server-side guard: even if AI says pass, force fail on placeholder serials
    if (result.pass) {
      const serialIssue = postCheckSerials(result.message);
      if (serialIssue) return { pass: false, message: serialIssue };
    }
    return result;
  } catch (e) { return { pass: false, message: `Analysis error: ${e.message || "network issue"}` }; }
}

const now = () => new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// ─── IMAGE COMPRESSION ─────────────────────────────────────────
// Android sends raw full-res JPEGs (4-8MB+), iPhone HEIC → JPEG via browser but still large.
// Compress to max 1600px longest edge, JPEG quality 0.75 (~200-400KB output).
function compressImage(dataUrl, maxDim = 1600, quality = 0.75) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width <= maxDim && height <= maxDim) {
        // Check if already small enough by data size (~1MB threshold)
        if (dataUrl.length < 1_400_000) { resolve(dataUrl); return; }
      }
      if (width > height) {
        if (width > maxDim) { height = Math.round(height * maxDim / width); width = maxDim; }
      } else {
        if (height > maxDim) { width = Math.round(width * maxDim / height); height = maxDim; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl); // fallback to original on error
    img.src = dataUrl;
  });
}

// ─── PDF PAGE IMAGE EXTRACTOR ───────────────────────────────────
async function extractPdfPageImages(b64Data) {
  // Load pdf.js if not already loaded
  if (!window.pdfjsLib) {
    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        resolve();
      };
      script.onerror = () => reject(new Error("Failed to load PDF renderer"));
      document.head.appendChild(script);
    });
  }
  const raw = atob(b64Data);
  const uint8 = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) uint8[i] = raw.charCodeAt(i);
  const pdf = await window.pdfjsLib.getDocument({ data: uint8 }).promise;
  const pages = [];
  // Cap pages to avoid browser OOM and Anthropic message-size blow-up on huge SOWs.
  // Reference image extraction is best-effort — we still parse the full SOW text via the document upload.
  const MAX_PAGES = 25;
  const scale = 1.2; // PNG ignores the toDataURL quality arg, so keep dimensions modest
  const numPages = Math.min(pdf.numPages, MAX_PAGES);
  for (let i = 1; i <= numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
    // JPEG keeps file size predictable; quality 0.7 still reads diagrams fine
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    pages.push({ page: i, dataUrl, b64: dataUrl.split(",")[1] });
  }
  return pages;
}

// ─── REFERENCE IMAGE MAPPER ─────────────────────────────────────
async function mapImagesToSteps(pageImages, steps) {
  // Send page images + step list to Claude to identify which pages have reference images for which steps.
  // Pages are now JPEG (see extractPdfPageImages) so the media_type matches.
  const imageContent = pageImages.map((p) => ([
    { type: "image", source: { type: "base64", media_type: "image/jpeg", data: p.b64 } },
    { type: "text", text: `[Page ${p.page}]` }
  ])).flat();
  const stepSummary = steps.map(s => `Step ${s.id}: "${s.title}" — ${s.instruction || ""}`).join("\n");
  const resp = await fetchWithRetry(API_URL, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514", max_tokens: 2048,
      system: `You analyze PDF page images from a Scope of Work document. Identify pages that contain useful REFERENCE IMAGES (photos, diagrams, network topology, equipment placement, cable routing, rack layouts, etc.) that would help a field technician. Ignore pages that are purely text, tables of contents, or contract boilerplate.

For each page with a useful reference image, indicate which checklist step(s) it relates to.

Respond with ONLY a JSON array. No markdown, no backticks. Example:
[{"page":2,"description":"Rack layout showing switch placement","step_ids":[3,4]},{"page":5,"description":"Cable routing diagram from MDF to IDF","step_ids":[6]}]

If NO pages contain useful reference images, respond with: []`,
      messages: [{ role: "user", content: [
        ...imageContent,
        { type: "text", text: `Here are the checklist steps:\n${stepSummary}\n\nWhich pages contain reference images useful for the tech? Return ONLY JSON.` }
      ]}]
    }),
  });
  const d = await resp.json();
  const text = d.content?.[0]?.text || "[]";
  try { return JSON.parse(text.replace(/```json|```/g, "").trim()); } catch { return []; }
}

// ─── SOW PARSER ─────────────────────────────────────────────────
async function parseSOW(fileData, fileName, isBase64) {
  const sysPrompt = `You are a SOW parser for Code Blue Technology Operations. You extract focused, high-value checklists from Scope of Work documents for experienced field technicians.

IMPORTANT: Do NOT create a step for every single task in the SOW. Techs are experienced professionals — they don't need to be told obvious things. Instead, consolidate related tasks into logical groups and only create steps for items that:
- Require VERIFICATION (photo proof, IP check, visual confirmation)
- Are CRITICAL and would cause a callback if missed
- Involve CONFIGURATION that must be exact (IPs, VLANs, credentials)
- Are easy to FORGET or commonly missed in the field

Combine routine tasks into a single step with a checklist of confirms. For example, instead of 5 separate steps for cable management, patching, labeling, etc. — make ONE "Network Infrastructure" step with confirms for each.

Aim for 5-12 steps total, not 20+. Each step should represent a meaningful checkpoint, not a single task.

You MUST respond with ONLY a JSON object. No markdown. No backticks. No explanation. Just pure JSON.

The JSON must have this exact structure:
{"title":"Job template name","description":"Brief description","steps":[{"id":1,"title":"Short Title","type":"instruction","instruction":"Detailed instructions","requiresPhoto":false,"photoLabel":null,"aiCheck":false,"aiPrompt":null,"hardGate":false,"expectedIP":null,"confirms":null,"icon":"emoji"}]}

Step types:
- "instruction" (just do it)
- "confirm" (verify with checkboxes)
- "photo" (take a photo)
- "ip_check" (verify an IP)
- "inventory" (capture a list of items with unknown quantity — devices, cables, ports, etc.)
- "verify_inventory" (verify every item from a previous inventory step)

INVENTORY STEPS: When the SOW mentions unknown quantities ("all devices", "each cable", "every port", "document hardware"), generate an "inventory" step:
{"id":3,"title":"Inventory All Devices","type":"inventory","instruction":"List every device installed in the cabinet...","inventoryFields":[{"key":"device_name","label":"Device Name","type":"text","required":true},{"key":"role","label":"Network Role","type":"text","required":true}],"minItems":1,"icon":"📋"}

inventoryFields defines what attributes to capture per item. Choose fields based on SOW context (device name, IP, PDU outlet, port mapping, cable label, etc.).

VERIFY_INVENTORY STEPS: Later, when the SOW requires checking/verifying those items, generate a "verify_inventory" step:
{"id":8,"title":"Verify All Devices Labeled","type":"verify_inventory","instruction":"Verify every device has labels...","referencesStep":3,"requiresPhoto":true,"photoLabel":"Photo of labeled devices","confirms_per_item":["Front label applied","Rear label applied"],"icon":"✅"}

referencesStep must point to the id of the earlier inventory step. confirms_per_item lists what must be verified for EACH inventoried item.

For photo steps set requiresPhoto:true and photoLabel to describe what photo is needed.
For confirm steps set confirms to an array of checkbox labels — use these to group related subtasks.
For ip_check steps set hardGate:true and expectedIP to the required IP.
For aiCheck:true steps, write an aiPrompt that tells vision AI what to verify in the photo.

Prioritize quality over quantity. A tight 8-step checklist that catches real issues beats a 25-step list techs will rush through.`;

  let userContent;
  if (isBase64) {
    userContent = [
      { type: "document", source: { type: "base64", media_type: "application/pdf", data: fileData } },
      { type: "text", text: `Parse this SOW document (${fileName}) and return ONLY the JSON checklist. No markdown, no backticks, no explanation - just the JSON object.` }
    ];
  } else {
    userContent = `Parse this SOW document (${fileName}) and return ONLY the JSON checklist. No markdown, no backticks, no explanation - just the JSON object.\n\n${fileData}`;
  }

  try {
    const r = await fetchWithRetry(API_URL, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 8192, system: sysPrompt, messages: [{ role: "user", content: userContent }] }),
    });
    const d = await r.json();
    if (d.error) return { error: `API Error: ${d.error?.message || JSON.stringify(d.error)}` };
    const response = d.content?.[0]?.text || "";
    const stopReason = d.stop_reason;
    const parsed = extractJsonObject(response);
    if (!parsed) {
      const truncated = stopReason === "max_tokens" ? " (response was truncated — try a smaller SOW or split it)" : "";
      const preview = response.slice(0, 300).replace(/\s+/g, " ");
      return { error: `AI didn't return valid JSON${truncated}. First 300 chars: "${preview}${response.length > 300 ? "…" : ""}"` };
    }
    if (!parsed.steps || !Array.isArray(parsed.steps) || parsed.steps.length === 0) {
      return { error: "AI returned a template with no steps. Try uploading again or a different SOW." };
    }
    return parsed;
  } catch (e) {
    return { error: `Failed to parse SOW: ${e.message}` };
  }
}

// Robustly extract a JSON object from AI text that may have leading explanation,
// trailing prose, or surrounding ```json fences. Returns null if no valid object found.
function extractJsonObject(text) {
  if (!text) return null;
  const stripped = text.replace(/```json|```/g, "").trim();
  // Try direct parse first
  try { return JSON.parse(stripped); } catch {}
  // Find the first { and try parses ending at every matching }
  const start = stripped.indexOf("{");
  if (start === -1) return null;
  // Walk forward, tracking brace depth, to find a balanced closing brace
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < stripped.length; i++) {
    const ch = stripped[i];
    if (escape) { escape = false; continue; }
    if (ch === "\\" && inString) { escape = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) {
        try { return JSON.parse(stripped.slice(start, i + 1)); } catch { return null; }
      }
    }
  }
  return null;
}

// ─── COMPONENTS ─────────────────────────────────────────────────
function Md({ children: text }) {
  const c = useTheme();
  if (!text) return null;
  return <>{text.split("\n").map((ln, i) => {
    const parts = ln.split(/(\*\*.*?\*\*)/g).map((s, j) =>
      s.startsWith("**") && s.endsWith("**") ? <strong key={j} style={{ color: c.text, fontWeight: 700 }}>{s.slice(2, -2)}</strong> : <span key={j}>{s}</span>
    );
    return <span key={i}>{parts}{i < text.split("\n").length - 1 && <br />}</span>;
  })}</>;
}

function Bubble({ from, children, status, time, isThinking }) {
  const c = useTheme();
  const isBot = from === "bot";
  return (
    <div style={{ display: "flex", justifyContent: isBot ? "flex-start" : "flex-end", marginBottom: 10, paddingLeft: isBot ? 0 : 40, paddingRight: isBot ? 40 : 0 }}>
      <div style={{ maxWidth: "100%", padding: isThinking ? "11px 18px" : "12px 16px", borderRadius: isBot ? "4px 18px 18px 18px" : "18px 4px 18px 18px", background: isBot ? c.botBg : c.userBg, border: `1px solid ${isBot ? c.botBorder : c.userBorder}` }}>
        {isBot && !isThinking && <div style={{ fontSize: 10, fontFamily: mono, color: c.accent, marginBottom: 5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>CB-OPS {status && <span style={{ color: status === "pass" ? c.accent : status === "fail" ? c.fail : c.warn }}>● {status.toUpperCase()}</span>}</div>}
        {isThinking ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 10, fontFamily: mono, color: c.accent, fontWeight: 700, letterSpacing: 1.2 }}>CB-OPS</span>
            <div style={{ display: "flex", gap: 4 }}>{[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: c.accent, opacity: .3, animation: `bk 1.2s ease ${i*.15}s infinite` }} />)}</div>
            <style>{`@keyframes bk{0%,100%{opacity:.2;transform:scale(.8)}50%{opacity:.7;transform:scale(1.1)}}`}</style>
          </div>
        ) : (
          <div style={{ fontSize: 16, lineHeight: 1.65, color: c.text, fontFamily: sans, whiteSpace: "pre-wrap" }}>{typeof children === "string" ? <Md>{children}</Md> : children}</div>
        )}
        {time && !isThinking && <div style={{ fontSize: 10, color: c.textMuted, marginTop: 5, fontFamily: mono }}>{time}</div>}
      </div>
    </div>
  );
}

function PhotoMsg({ src, time }) {
  const c = useTheme();
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10, paddingLeft: 40 }}>
      <div style={{ borderRadius: 18, overflow: "hidden", border: `1px solid ${c.userBorder}`, background: c.userBg, padding: 3 }}>
        <img src={src} alt="" style={{ maxWidth: 260, maxHeight: 200, borderRadius: 15, display: "block" }} />
        {time && <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono, padding: "4px 8px" }}>{time}</div>}
      </div>
    </div>
  );
}

function ConfirmBtn({ label, done, onClick }) {
  const c = useTheme();
  return (
    <button onClick={onClick} disabled={done} style={{
      display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "12px 14px", marginBottom: 5,
      background: done ? c.accentDim : c.inputBg, border: `1.5px solid ${done ? c.accentBorder : c.border}`,
      borderRadius: 12, cursor: done ? "default" : "pointer", transition: "all 0.15s",
    }}>
      <div style={{ width: 22, height: 22, borderRadius: 6, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: done ? c.accent : "transparent", border: done ? "none" : `2px solid ${c.textDim}`, color: c.checkColor, fontSize: 13, fontWeight: 700 }}>{done ? "✓" : ""}</div>
      <span style={{ fontSize: 15, color: done ? c.accent : c.textDim, fontFamily: sans, fontWeight: 500 }}>{label}</span>
    </button>
  );
}

function InventoryForm({ step, items, onAdd, onRemove, onComplete, isComplete }) {
  const c = useTheme();
  const fields = step.inventoryFields || [];
  const [draft, setDraft] = useState(() => Object.fromEntries(fields.map(f => [f.key, ""])));
  const canAdd = fields.filter(f => f.required).every(f => draft[f.key]?.trim());

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd({ ...draft });
    setDraft(Object.fromEntries(fields.map(f => [f.key, ""])));
  };

  return (
    <div style={{ marginBottom: 8 }}>
      {items.length > 0 && (
        <div style={{ marginBottom: 8, maxHeight: 200, overflow: "auto" }}>
          {items.map((item, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: c.accentDim, border: `1px solid ${c.accentBorder}`, borderRadius: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: c.accent, fontFamily: "'JetBrains Mono', monospace", minWidth: 20 }}>{idx + 1}</span>
              <div style={{ flex: 1, fontSize: 13, color: c.text }}>
                {fields.map(f => item[f.key] ? <span key={f.key} style={{ marginRight: 10 }}><strong>{f.label}:</strong> {item[f.key]}</span> : null)}
              </div>
              {!isComplete && <button onClick={() => onRemove(idx)} style={{ background: "none", border: "none", color: c.fail, cursor: "pointer", fontSize: 16, padding: 0 }}>×</button>}
            </div>
          ))}
        </div>
      )}
      {!isComplete && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
            {fields.map(f => (
              <input key={f.key} value={draft[f.key]} onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                placeholder={f.label + (f.required ? " *" : "")}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); }}}
                style={{ flex: "1 1 140px", padding: "10px 12px", background: c.inputBg, border: `1.5px solid ${c.border}`, borderRadius: 10, color: c.text, fontSize: 14, fontFamily: "-apple-system, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif", outline: "none" }} />
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleAdd} disabled={!canAdd} style={{
              flex: 1, padding: "10px 14px", borderRadius: 10, border: "none",
              background: canAdd ? c.accent : c.inputBg, color: canAdd ? "#fff" : c.textMuted,
              fontSize: 14, fontWeight: 700, cursor: canAdd ? "pointer" : "not-allowed",
              fontFamily: "'JetBrains Mono', monospace",
            }}>+ ADD ITEM</button>
            {items.length >= (step.minItems || 1) && (
              <button onClick={onComplete} style={{
                flex: 1, padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${c.accentBorder}`,
                background: c.accentDim, color: c.accent,
                fontSize: 14, fontWeight: 700, cursor: "pointer",
                fontFamily: "'JetBrains Mono', monospace",
              }}>DONE ({items.length} items)</button>
            )}
          </div>
        </>
      )}
      {isComplete && (
        <div style={{ padding: "8px 12px", background: c.accentDim, borderRadius: 10, textAlign: "center" }}>
          <span style={{ fontSize: 13, color: c.accent, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>INVENTORY LOCKED — {items.length} ITEMS</span>
        </div>
      )}
    </div>
  );
}

function VerifyInventoryUI({ step, items, cfs, onConfirm }) {
  const c = useTheme();
  const checks = step.confirms_per_item || [];
  if (!items.length) return <div style={{ padding: 12, color: c.warn, fontSize: 14 }}>No inventory items to verify. Complete the inventory step first.</div>;

  const fields = [];
  // Get field keys from the first item
  const itemKeys = Object.keys(items[0] || {});

  return (
    <div style={{ marginBottom: 8, maxHeight: 300, overflow: "auto" }}>
      {items.map((item, idx) => {
        const allDone = checks.every(label => cfs[`${step.id}_item${idx}_${label}`]);
        return (
          <div key={idx} style={{ marginBottom: 8, background: allDone ? c.accentDim : c.inputBg, border: `1px solid ${allDone ? c.accentBorder : c.border}`, borderRadius: 12, padding: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, fontFamily: "'JetBrains Mono', monospace", color: c.accent }}>{idx + 1}.</span>
              {itemKeys.map(k => <span key={k} style={{ marginRight: 8 }}>{item[k]}</span>)}
              {allDone && <span style={{ color: c.accent, marginLeft: "auto" }}>✓</span>}
            </div>
            {checks.map(label => {
              const key = `${step.id}_item${idx}_${label}`;
              const done = !!cfs[key];
              return (
                <button key={label} onClick={() => !done && onConfirm(key)} disabled={done} style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 10px", marginBottom: 3,
                  background: done ? "rgba(74,222,128,0.05)" : "transparent", border: `1px solid ${done ? c.accentBorder : c.border}`,
                  borderRadius: 8, cursor: done ? "default" : "pointer",
                }}>
                  <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: done ? c.accent : "transparent", border: done ? "none" : `2px solid ${c.textDim}`, color: c.checkColor, fontSize: 11, fontWeight: 700 }}>{done ? "✓" : ""}</div>
                  <span style={{ fontSize: 13, color: done ? c.accent : c.textDim }}>{label}</span>
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function ThemeToggle({ mode, onToggle }) {
  const c = useTheme();
  return (
    <button onClick={onToggle} style={{ width: 40, height: 24, borderRadius: 12, border: `1.5px solid ${c.border}`, background: c.surfaceHi, cursor: "pointer", position: "relative", flexShrink: 0 }}>
      <div style={{ width: 18, height: 18, borderRadius: 9, position: "absolute", top: 2, left: mode === "dark" ? 18 : 2, background: c.accent, transition: "left 0.25s ease", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>{mode === "dark" ? "🌙" : "☀️"}</div>
    </button>
  );
}

// ─── SOW UPLOAD SCREEN ──────────────────────────────────────────
function SOWUpload({ onTemplateParsed, theme: c, mode, onToggleMode }) {
  const [parsing, setParsing] = useState(false);
  const [parseStage, setParseStage] = useState(""); // "parsing" | "extracting" | "mapping"
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    setParseStage("parsing");
    setError(null);

    try {
      let template;
      let pdfB64 = null;
      if (file.name.endsWith(".txt") || file.name.endsWith(".md")) {
        const text = await file.text();
        if (text.length < 50) { setError("File too short."); setParsing(false); return; }
        template = await parseSOW(text, file.name, false);
      } else if (file.name.endsWith(".pdf")) {
        // Anthropic refuses base64 documents over ~32MB. Block early with a clear message.
        if (file.size > 30 * 1024 * 1024) {
          setError(`PDF is ${(file.size / 1024 / 1024).toFixed(1)}MB — Anthropic's API limit is 32MB. Trim or split the SOW and try again.`);
          setParsing(false); return;
        }
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = () => reject(new Error("Failed to read file"));
          reader.readAsDataURL(file);
        });
        pdfB64 = b64;
        template = await parseSOW(b64, file.name, true);
      } else {
        const arrayBuffer = await file.arrayBuffer();
        if (!window.mammoth) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js";
            script.onload = resolve;
            script.onerror = () => reject(new Error("Failed to load document parser"));
            document.head.appendChild(script);
          });
        }
        const result = await window.mammoth.extractRawText({ arrayBuffer });
        const text = result.value;
        if (text.length < 50) { setError("Couldn't extract enough text from the document."); setParsing(false); return; }
        template = await parseSOW(text, file.name, false);
      }

      if (template.error) { setError(template.error); setParsing(false); return; }
      template.sow_filename = file.name;
      if (file.name.endsWith(".txt") || file.name.endsWith(".md")) {
        template.sow_content = await file.text();
      } else if (!file.name.endsWith(".pdf")) {
        try { template.sow_content = (await window.mammoth?.extractRawText({ arrayBuffer: await file.arrayBuffer() }))?.value; } catch {}
      }

      // PDF reference image extraction
      if (pdfB64 && template.steps?.length > 0) {
        try {
          setParseStage("extracting");
          const pageImages = await extractPdfPageImages(pdfB64);
          if (pageImages.length > 0) {
            setParseStage("mapping");
            const mappings = await mapImagesToSteps(pageImages, template.steps);
            // Attach page images as referenceImages on steps
            if (mappings.length > 0) {
              // Store page data temporarily — will be uploaded to KV when template is saved
              template._pendingRefImages = mappings.map(m => ({
                page: m.page,
                description: m.description,
                step_ids: m.step_ids,
                dataUrl: pageImages.find(p => p.page === m.page)?.dataUrl,
              })).filter(m => m.dataUrl);
              // Also embed descriptions into steps for immediate preview
              for (const m of mappings) {
                for (const sid of m.step_ids || []) {
                  const step = template.steps.find(s => s.id === sid);
                  if (step) {
                    if (!step.referenceImages) step.referenceImages = [];
                    step.referenceImages.push({ page: m.page, description: m.description });
                  }
                }
              }
            }
          }
        } catch (imgErr) {
          console.warn("Reference image extraction failed (non-fatal):", imgErr);
        }
      }

      onTemplateParsed(template);
    } catch (err) {
      setError(`Error: ${err.message}`);
    }
    setParsing(false);
    setParseStage("");
  };

  return (
    <div style={{ minHeight: "100dvh", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: sans }}>
      <div style={{ width: "100%", maxWidth: 480 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, boxShadow: c.shadow }}>⚡</div>
            <div>
              <div style={{ fontSize: 18, fontFamily: mono, color: c.accent, fontWeight: 800 }}>CB-OPS</div>
              <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono, letterSpacing: 2 }}>SOW TEMPLATE BUILDER</div>
            </div>
          </div>
          <ThemeToggle mode={mode} onToggle={onToggleMode} />
        </div>

        <div style={{ background: c.surface, borderRadius: 20, padding: 24, border: `1px solid ${c.border}` }}>
          <h2 style={{ fontSize: 18, color: c.text, fontFamily: sans, fontWeight: 700, margin: "0 0 8px" }}>Upload a Scope of Work</h2>
          <p style={{ fontSize: 14, color: c.textDim, margin: "0 0 20px", lineHeight: 1.5 }}>
            CB-OPS will read the SOW and automatically generate a step-by-step checklist with instructions, photo requirements, and verification gates.
          </p>

          <div
            onClick={() => !parsing && fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            style={{
              padding: parsing ? "40px 20px" : "48px 20px",
              border: `2px dashed ${dragOver ? c.accent : c.border}`,
              borderRadius: 16,
              background: dragOver ? c.accentDim : c.inputBg,
              cursor: parsing ? "wait" : "pointer",
              textAlign: "center",
              transition: "all 0.2s",
            }}
          >
            {parsing ? (
              <>
                <div style={{ fontSize: 28, marginBottom: 12 }}>🔍</div>
                <div style={{ fontSize: 15, color: c.accent, fontWeight: 600, marginBottom: 4 }}>
                  {parseStage === "extracting" ? "Extracting images..." : parseStage === "mapping" ? "Mapping images to steps..." : "Analyzing SOW..."}
                </div>
                <div style={{ fontSize: 13, color: c.textDim }}>
                  {parseStage === "extracting" ? "Rendering PDF pages to extract reference images" : parseStage === "mapping" ? "AI is matching diagrams & photos to checklist steps" : "AI is reading the document and building your checklist"}
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: 4, marginTop: 12 }}>
                  {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: c.accent, opacity: .3, animation: `bk 1.2s ease ${i*.2}s infinite` }} />)}
                </div>
                <style>{`@keyframes bk{0%,100%{opacity:.2;transform:scale(.8)}50%{opacity:.7;transform:scale(1.1)}}`}</style>
              </>
            ) : (
              <>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📄</div>
                <div style={{ fontSize: 16, color: c.text, fontWeight: 600, marginBottom: 4 }}>Drop SOW here or tap to upload</div>
                <div style={{ fontSize: 13, color: c.textDim }}>.docx, .pdf, or .txt</div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".docx,.pdf,.txt,.md,.doc" style={{ display: "none" }}
            onChange={(e) => handleFile(e.target.files?.[0])} />

          {error && (
            <div style={{ marginTop: 16, padding: 14, background: c.failDim, border: `1px solid rgba(239,68,68,0.2)`, borderRadius: 12, color: c.fail, fontSize: 14, fontFamily: sans }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 20, padding: 16, background: c.inputBg, borderRadius: 12, border: `1px solid ${c.border}` }}>
            <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>HOW IT WORKS</div>
            <div style={{ fontSize: 14, color: c.textDim, lineHeight: 1.6 }}>
              1. Upload your SOW document{"\n"}
              2. AI reads it and extracts every step{"\n"}
              3. Generates verification checks, photo requirements, IP gates{"\n"}
              4. Creates a reusable job template{"\n"}
              5. Techs select the template and get walked through it
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SUGGESTED STEPS LIBRARY ────────────────────────────────────
const SUGGESTED_STEPS = [
  { title: "Verify Internet Connectivity", type: "confirm", instruction: "Run a speed test from a device on the network. Verify upload/download speeds meet minimum requirements.", confirms: ["Speed test completed", "Meets minimum requirements"], icon: "🌐", requiresPhoto: false },
  { title: "Test All Phone Lines", type: "confirm", instruction: "Test each phone line for dial tone, inbound calls, and outbound calls.", confirms: ["Dial tone verified", "Inbound calls working", "Outbound calls working"], icon: "📞", requiresPhoto: false },
  { title: "Verify Music Playing Throughout Club", type: "confirm", instruction: "Walk through the club and verify music is playing in all zones. Check Rockbot portal for status.", confirms: ["Music playing in all zones", "Rockbot portal checked"], icon: "🎵", requiresPhoto: false },
  { title: "Check Advertisement TVs and Rockbot Portal", type: "confirm", instruction: "Verify all advertisement TVs are powered on and displaying content. Check Rockbot portal.", confirms: ["All TVs displaying content", "Rockbot portal verified"], icon: "📺", requiresPhoto: true, photoLabel: "Photo of TV displays" },
  { title: "Verify Printer IP Address", type: "ip_check", instruction: "Verify the printer is accessible at the expected IP address (192.168.200.25).", expectedIP: "192.168.200.25", hardGate: true, icon: "🖨️", requiresPhoto: false },
  { title: "Verify All Cameras Online", type: "confirm", instruction: "Check that all security cameras are online and the manager has access to the camera system.", confirms: ["All cameras online", "Manager has access"], icon: "📹", requiresPhoto: true, photoLabel: "Screenshot of camera dashboard" },
  { title: "Clean Up Work Area", type: "confirm", instruction: "Clean up all tools, packaging, and debris from the work area. Leave the site as clean as you found it.", confirms: ["Work area cleaned", "No debris remaining"], icon: "🧹", requiresPhoto: true, photoLabel: "Photo of clean work area" },
  { title: "Call WOM/PM to Verify and Close Out", type: "confirm", instruction: "Call the Wellness Operations Manager or Project Manager to verify all work is complete and get sign-off.", confirms: ["PM/WOM contacted", "Sign-off received"], icon: "📋", requiresPhoto: false },
  { title: "Take Final Photos of Completed Work", type: "photo", instruction: "Take photos of all completed work for documentation.", requiresPhoto: true, photoLabel: "Final photos of completed work", aiCheck: false, icon: "📸" },
];

// ─── TEMPLATE EDITOR SCREEN ─────────────────────────────────────
function TemplateEditor({ template, onAccept, onBack, theme: c }) {
  const [title, setTitle] = useState(template.title || "");
  const [description, setDescription] = useState(template.description || "");
  const [ticketNumber, setTicketNumber] = useState(template.ticket_number || "");
  const [steps, setSteps] = useState(() =>
    (template.steps || []).map((s, i) => ({ ...s, _key: `sow_${i}`, enabled: true }))
  );
  const [showSuggested, setShowSuggested] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [newConfirm, setNewConfirm] = useState("");
  const [addingCustom, setAddingCustom] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [customInstruction, setCustomInstruction] = useState("");
  const [customPhoto, setCustomPhoto] = useState(false);
  const [customAiCheck, setCustomAiCheck] = useState(false);
  const [customAiPrompt, setCustomAiPrompt] = useState("");
  const [customHardGate, setCustomHardGate] = useState(false);
  const [customExpectedIP, setCustomExpectedIP] = useState("");
  const [customConfirms, setCustomConfirms] = useState([]);
  const [customConfirmInput, setCustomConfirmInput] = useState("");
  const [pendingRefImages, setPendingRefImages] = useState(template._pendingRefImages || []);
  const [uploadingRefStepIdx, setUploadingRefStepIdx] = useState(null);
  const fileInputRef = useRef(null);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const ns = [...steps];
    [ns[i], ns[j]] = [ns[j], ns[i]];
    setSteps(ns);
  };

  const toggle = (i) => setSteps(s => s.map((st, idx) => idx === i ? { ...st, enabled: !st.enabled } : st));

  const updateStep = (i, field, value) => setSteps(s => s.map((st, idx) => idx === i ? { ...st, [field]: value } : st));
  const updateStepMulti = (i, updates) => setSteps(s => s.map((st, idx) => idx === i ? { ...st, ...updates } : st));

  const addSuggested = (sg) => {
    const maxId = steps.reduce((m, s) => Math.max(m, s.id || 0), 0);
    setSteps(prev => [...prev, { ...sg, id: maxId + 1, _key: `sug_${Date.now()}`, enabled: true }]);
  };

  const addCustomStep = () => {
    if (!customTitle.trim()) return;
    const maxId = steps.reduce((m, s) => Math.max(m, s.id || 0), 0);
    const step = {
      id: maxId + 1, title: customTitle.trim(),
      type: customHardGate ? "ip_check" : customConfirms.length > 0 ? "confirm" : customPhoto ? "photo" : "instruction",
      instruction: customInstruction.trim() || customTitle.trim(),
      confirms: customConfirms.length > 0 ? customConfirms : undefined,
      requiresPhoto: customPhoto, photoLabel: customPhoto ? "Photo required" : undefined,
      aiCheck: customAiCheck, aiPrompt: customAiCheck ? customAiPrompt : undefined,
      hardGate: customHardGate, expectedIP: customHardGate ? customExpectedIP : undefined,
      icon: customPhoto ? "📷" : customHardGate ? "🚫" : "📋",
      _key: `cust_${Date.now()}`, enabled: true,
    };
    setSteps(prev => [...prev, step]);
    setCustomTitle(""); setCustomInstruction(""); setCustomPhoto(false);
    setCustomAiCheck(false); setCustomAiPrompt(""); setCustomHardGate(false);
    setCustomExpectedIP(""); setCustomConfirms([]); setCustomConfirmInput("");
    setAddingCustom(false);
  };

  const [saveStatus, setSaveStatus] = useState(""); // "" | "saving" | "saved" | "error"
  const saveTimer = useRef(null);

  const getCleanSteps = useCallback(() => {
    return steps.filter(s => s.enabled).map((s, i) => {
      const { _key, enabled, ...rest } = s;
      return { ...rest, id: i + 1 };
    });
  }, [steps]);

  const addReferenceImage = async (stepIndex, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      const step = steps[stepIndex];
      const previewId = `ref_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const newRef = {
        id: previewId,
        description: "Manual reference image",
        url: dataUrl,
        page: 0,
      };
      setSteps(prev => prev.map((st, idx) => idx === stepIndex ? {
        ...st,
        referenceImages: [...(st.referenceImages || []), newRef],
      } : st));

      if (template.id) {
        try {
          const resp = await fetch("/api/ref-images", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              template_id: template.id,
              page: 0,
              description: "Manual reference image",
              step_ids: [step.id],
              data: dataUrl,
            }),
          });
          const body = await resp.json();
          if (body.url) {
            setSteps(prev => prev.map((st, idx) => idx === stepIndex ? {
              ...st,
              referenceImages: (st.referenceImages || []).map(r => r.id === previewId ? { ...r, id: body.id, url: body.url } : r),
            } : st));
          }
        } catch (err) {
          console.error("Reference image upload failed", err);
        }
      } else {
        setPendingRefImages(prev => [...prev, {
          id: previewId,
          page: 0,
          description: "Manual reference image",
          step_ids: [step.id],
          dataUrl,
        }] );
      }
    };
    reader.readAsDataURL(file);
  };

  const removeReferenceImage = (stepIndex, refId) => {
    setSteps(prev => prev.map((st, idx) => idx === stepIndex ? {
      ...st,
      referenceImages: (st.referenceImages || []).filter(r => r.id !== refId),
    } : st));
    setPendingRefImages(prev => prev.filter(r => r.id !== refId));
  };

  // Autosave for existing templates — debounce 2s after last change
  useEffect(() => {
    if (!template.id) return; // new templates don't autosave
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        const res = await fetch(`/api/jobs/${template.id}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title, description, steps: getCleanSteps(), ticket_number: ticketNumber || null }),
        });
        if (!res.ok) throw new Error("Save failed");
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 3000);
      }
    }, 2000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [title, description, ticketNumber, steps, template.id, getCleanSteps]);

  const finalize = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current); // cancel pending autosave
    onAccept({ ...template, title, description, ticket_number: ticketNumber, steps: getCleanSteps(), _pendingRefImages: pendingRefImages });
  };

  const enabledCount = steps.filter(s => s.enabled).length;
  const suggestedAlreadyAdded = new Set(steps.map(s => s.title));

  return (
    <div style={{ minHeight: "100dvh", background: c.bg, padding: 20, fontFamily: sans, overflow: "auto", height: "100dvh" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      <div style={{ maxWidth: 520, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button onClick={onBack} style={{ background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.textDim, padding: "6px 14px", cursor: "pointer", fontSize: 14 }}>← Back</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: c.accent, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5 }}>EDIT TEMPLATE</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {saveStatus === "saving" && <span style={{ fontSize: 10, color: c.textMuted, fontFamily: mono }}>Saving...</span>}
            {saveStatus === "saved" && <span style={{ fontSize: 10, color: c.accent, fontFamily: mono, fontWeight: 700 }}>✓ Saved</span>}
            {saveStatus === "error" && <span style={{ fontSize: 10, color: c.fail, fontFamily: mono, fontWeight: 700 }}>Save failed</span>}
            <div style={{ fontSize: 12, color: c.accent, fontFamily: mono, fontWeight: 700 }}>{enabledCount} active</div>
          </div>
        </div>

        {/* Title & Description */}
        <div style={{ background: c.surface, borderRadius: 16, padding: 16, border: `1px solid ${c.border}`, marginBottom: 16 }}>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Template title"
            style={{ width: "100%", padding: "10px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 10, color: c.text, fontSize: 17, fontWeight: 700, fontFamily: sans, outline: "none", marginBottom: 8, boxSizing: "border-box" }} />
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input value={ticketNumber} onChange={e => setTicketNumber(e.target.value)} placeholder="Ticket # (e.g. T20260331.0001)"
              style={{ flex: 1, padding: "10px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 10, color: c.text, fontSize: 14, fontFamily: mono, outline: "none", boxSizing: "border-box" }} />
          </div>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" rows={2}
            style={{ width: "100%", padding: "10px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 10, color: c.textDim, fontSize: 14, fontFamily: sans, outline: "none", resize: "none", boxSizing: "border-box" }} />
        </div>

        {/* Steps */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {steps.map((step, i) => (
            <div key={step._key} style={{
              background: c.surface, borderRadius: 12, padding: "12px 14px",
              border: `1px solid ${step.enabled ? c.border : "rgba(255,255,255,0.03)"}`,
              opacity: step.enabled ? 1 : 0.45, transition: "opacity 0.2s",
            }}>
              {editingIdx === i ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <input value={step.title} onChange={e => updateStep(i, "title", e.target.value)}
                    style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 14, fontWeight: 600, outline: "none", boxSizing: "border-box" }} />
                  <textarea value={step.instruction} onChange={e => updateStep(i, "instruction", e.target.value)} rows={3}
                    style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box" }} />

                  {/* Requirement toggles */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button onClick={() => updateStep(i, "requiresPhoto", !step.requiresPhoto)} style={{
                      padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                      background: step.requiresPhoto ? "rgba(99,102,241,0.1)" : c.inputBg,
                      border: `1px solid ${step.requiresPhoto ? "rgba(99,102,241,0.3)" : c.border}`,
                      color: step.requiresPhoto ? "rgba(99,102,241,0.8)" : c.textMuted,
                    }}>📷 PHOTO {step.requiresPhoto ? "ON" : "OFF"}</button>
                    <button onClick={() => updateStep(i, "aiCheck", !step.aiCheck)} style={{
                      padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                      background: step.aiCheck ? c.accentDim : c.inputBg,
                      border: `1px solid ${step.aiCheck ? c.accentBorder : c.border}`,
                      color: step.aiCheck ? c.accent : c.textMuted,
                    }}>🤖 AI CHECK {step.aiCheck ? "ON" : "OFF"}</button>
                    <button onClick={() => {
                      const newHG = !step.hardGate;
                      // Hard gate requires AI check + photo — set all in one update
                      if (newHG) {
                        updateStepMulti(i, { hardGate: true, aiCheck: true, requiresPhoto: true });
                      } else {
                        updateStep(i, "hardGate", false);
                      }
                    }} style={{
                      padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                      background: step.hardGate ? c.failDim : c.inputBg,
                      border: `1px solid ${step.hardGate ? "rgba(239,68,68,0.3)" : c.border}`,
                      color: step.hardGate ? c.fail : c.textMuted,
                    }}>🚫 HARD GATE {step.hardGate ? "ON" : "OFF"}</button>
                  </div>

                  {/* AI prompt (shown when AI check is on) */}
                  {step.aiCheck && (
                    <input value={step.aiPrompt || ""} onChange={e => updateStep(i, "aiPrompt", e.target.value)} placeholder="AI prompt: what should vision AI verify?"
                      style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                  )}

                  {/* Photo label (shown when photo required) */}
                  {step.requiresPhoto && (
                    <input value={step.photoLabel || ""} onChange={e => updateStep(i, "photoLabel", e.target.value)} placeholder="Photo label: what photo is needed?"
                      style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                  )}

                  {/* Hard gate IP (shown when hard gate is on) */}
                  {step.hardGate && (
                    <input value={step.expectedIP || ""} onChange={e => updateStep(i, "expectedIP", e.target.value)} placeholder="Expected IP (e.g. 192.168.200.1)"
                      style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
                  )}

                  {/* Confirm items editor */}
                  <div>
                    <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, marginBottom: 4 }}>CONFIRM CHECKS</div>
                    {(step.confirms || []).map((cf, ci) => (
                      <div key={ci} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                        <span style={{ fontSize: 12, color: c.accent }}>✓</span>
                        <span style={{ flex: 1, fontSize: 13, color: c.text }}>{cf}</span>
                        <button onClick={() => updateStep(i, "confirms", step.confirms.filter((_, x) => x !== ci))} style={{ background: "none", border: "none", color: c.fail, fontSize: 13, cursor: "pointer", padding: "0 4px" }}>×</button>
                      </div>
                    ))}
                    <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                      <input value={editingIdx === i ? newConfirm : ""} onChange={e => setNewConfirm(e.target.value)} placeholder="Add confirm item..."
                        onKeyDown={e => { if (e.key === "Enter" && newConfirm.trim()) { updateStep(i, "confirms", [...(step.confirms || []), newConfirm.trim()]); setNewConfirm(""); }}}
                        style={{ flex: 1, padding: "6px 8px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 6, color: c.text, fontSize: 12, outline: "none" }} />
                      <button onClick={() => { if (newConfirm.trim()) { updateStep(i, "confirms", [...(step.confirms || []), newConfirm.trim()]); setNewConfirm(""); }}}
                        style={{ padding: "6px 10px", borderRadius: 6, border: "none", background: c.accentDim, color: c.accent, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+</button>
                    </div>
                  </div>

                  <button onClick={() => { setEditingIdx(null); setNewConfirm(""); }} style={{ alignSelf: "flex-end", padding: "6px 16px", borderRadius: 8, border: "none", background: c.accent, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Done</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  {/* Reorder */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                    <button onClick={() => move(i, -1)} disabled={i === 0} style={{ background: "none", border: "none", color: i === 0 ? c.textMuted : c.textDim, fontSize: 14, cursor: i === 0 ? "default" : "pointer", padding: 0, lineHeight: 1 }}>▲</button>
                    <button onClick={() => move(i, 1)} disabled={i === steps.length - 1} style={{ background: "none", border: "none", color: i === steps.length - 1 ? c.textMuted : c.textDim, fontSize: 14, cursor: i === steps.length - 1 ? "default" : "pointer", padding: 0, lineHeight: 1 }}>▼</button>
                  </div>
                  {/* Show/hide toggle */}
                  <button onClick={() => toggle(i)} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", padding: 0, flexShrink: 0, opacity: step.enabled ? 1 : 0.4 }} title={step.enabled ? "Hide step" : "Show step"}>{step.enabled ? (step.icon || "📋") : "👁️"}</button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: c.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{step.title}</div>
                    <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono }}>{step.type}{step.requiresPhoto ? " • 📷" : ""}{step.aiCheck ? " • 🤖" : ""}{step.hardGate ? " • 🚫" : ""}{step.confirms?.length ? ` • ${step.confirms.length} checks` : ""}{step.referenceImages?.length ? ` • 🖼️ ${step.referenceImages.length} ref` : ""}</div>
                  </div>
                  <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                    <button onClick={() => { setEditingIdx(i); setNewConfirm(""); }} style={{ background: "none", border: "none", color: c.textDim, fontSize: 14, cursor: "pointer", padding: "2px 4px" }} title="Edit">✏️</button>
                    <button onClick={() => toggle(i)} style={{
                      padding: "3px 8px", borderRadius: 6, border: `1px solid ${step.enabled ? c.accentBorder : c.border}`,
                      background: step.enabled ? c.accentDim : c.inputBg,
                      color: step.enabled ? c.accent : c.textMuted, fontSize: 10, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                    }}>{step.enabled ? "ON" : "OFF"}</button>
                  </div>
                </div>
              )}
              {editingIdx === i && (
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, fontWeight: 700 }}>Reference images</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {(step.referenceImages || []).map((ref, ri) => (
                      <div key={ref.id || ri} style={{ display: "flex", flexDirection: "column", gap: 4, width: 120, position: "relative" }}>
                        <img src={ref.url} alt={ref.description || "Reference image"}
                          style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 10, border: `1px solid ${c.border}` }} />
                        <button onClick={() => removeReferenceImage(i, ref.id || ri)} style={{ position: "absolute", top: 6, right: 6, width: 22, height: 22, borderRadius: 999, border: "none", background: "rgba(0,0,0,0.5)", color: "#fff", fontSize: 12, cursor: "pointer" }}>×</button>
                        <div style={{ fontSize: 11, color: c.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 4 }}>{ref.description || "Manual reference"}</div>
                      </div>
                    ))}
                    <label style={{ width: 120, minHeight: 80, borderRadius: 10, border: `1px dashed ${c.border}`, background: c.inputBg, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: c.textDim, fontSize: 12, textAlign: "center", padding: 10 }}>
                      + Upload
                      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                        const file = e.target.files?.[0];
                        if (file) addReferenceImage(i, file);
                        e.target.value = "";
                      }} />
                    </label>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Custom Step */}
        {addingCustom ? (
          <div style={{ background: c.surface, borderRadius: 14, padding: 14, border: `1px solid ${c.accentBorder}`, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: c.accent, fontFamily: mono, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>NEW STEP</div>
            <input value={customTitle} onChange={e => setCustomTitle(e.target.value)} placeholder="Step title *"
              style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 14, outline: "none", marginBottom: 6, boxSizing: "border-box" }} />
            <textarea value={customInstruction} onChange={e => setCustomInstruction(e.target.value)} placeholder="Instructions for the tech" rows={2}
              style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none", resize: "none", marginBottom: 8, boxSizing: "border-box" }} />

            {/* Requirement toggles */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
              <button onClick={() => setCustomPhoto(p => !p)} style={{
                padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                background: customPhoto ? "rgba(99,102,241,0.1)" : c.inputBg,
                border: `1px solid ${customPhoto ? "rgba(99,102,241,0.3)" : c.border}`,
                color: customPhoto ? "rgba(99,102,241,0.8)" : c.textMuted,
              }}>📷 PHOTO {customPhoto ? "ON" : "OFF"}</button>
              <button onClick={() => setCustomAiCheck(a => !a)} style={{
                padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                background: customAiCheck ? c.accentDim : c.inputBg,
                border: `1px solid ${customAiCheck ? c.accentBorder : c.border}`,
                color: customAiCheck ? c.accent : c.textMuted,
              }}>🤖 AI CHECK {customAiCheck ? "ON" : "OFF"}</button>
              <button onClick={() => setCustomHardGate(g => !g)} style={{
                padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                background: customHardGate ? c.failDim : c.inputBg,
                border: `1px solid ${customHardGate ? "rgba(239,68,68,0.3)" : c.border}`,
                color: customHardGate ? c.fail : c.textMuted,
              }}>🚫 HARD GATE {customHardGate ? "ON" : "OFF"}</button>
            </div>

            {/* AI prompt — what should AI verify in the photo? */}
            {customAiCheck && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: c.textDim, fontFamily: mono, marginBottom: 4 }}>What should the AI verify in the photo?</div>
                <input value={customAiPrompt} onChange={e => setCustomAiPrompt(e.target.value)}
                  placeholder="e.g. Verify all cables are neatly bundled and labeled"
                  style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 12, outline: "none", boxSizing: "border-box" }} />
              </div>
            )}

            {/* Expected IP — for hard gate */}
            {customHardGate && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: c.textDim, fontFamily: mono, marginBottom: 4 }}>Required IP address</div>
                <input value={customExpectedIP} onChange={e => setCustomExpectedIP(e.target.value)}
                  placeholder="e.g. 192.168.200.1"
                  style={{ width: "100%", padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 12, fontFamily: mono, outline: "none", boxSizing: "border-box" }} />
              </div>
            )}

            {/* Confirm items */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, color: c.textDim, fontFamily: mono, marginBottom: 4 }}>Checklist items (tech must check each one)</div>
              {customConfirms.map((cf, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 12, color: c.accent }}>✓</span>
                  <span style={{ flex: 1, fontSize: 13, color: c.text }}>{cf}</span>
                  <button onClick={() => setCustomConfirms(prev => prev.filter((_, x) => x !== i))} style={{ background: "none", border: "none", color: c.fail, fontSize: 13, cursor: "pointer", padding: "0 4px" }}>×</button>
                </div>
              ))}
              <div style={{ display: "flex", gap: 4 }}>
                <input value={customConfirmInput} onChange={e => setCustomConfirmInput(e.target.value)} placeholder="Add checklist item..."
                  onKeyDown={e => { if (e.key === "Enter" && customConfirmInput.trim()) { setCustomConfirms(prev => [...prev, customConfirmInput.trim()]); setCustomConfirmInput(""); }}}
                  style={{ flex: 1, padding: "6px 8px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 6, color: c.text, fontSize: 12, outline: "none" }} />
                <button onClick={() => { if (customConfirmInput.trim()) { setCustomConfirms(prev => [...prev, customConfirmInput.trim()]); setCustomConfirmInput(""); }}}
                  style={{ padding: "6px 10px", borderRadius: 6, border: "none", background: c.accentDim, color: c.accent, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>+</button>
              </div>
            </div>

            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={addCustomStep} disabled={!customTitle.trim()} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: customTitle.trim() ? c.accent : c.inputBg, color: customTitle.trim() ? "#fff" : c.textMuted, fontSize: 13, fontWeight: 700, cursor: customTitle.trim() ? "pointer" : "not-allowed" }}>ADD STEP</button>
              <button onClick={() => { setAddingCustom(false); setCustomTitle(""); setCustomInstruction(""); setCustomPhoto(false); setCustomAiCheck(false); setCustomAiPrompt(""); setCustomHardGate(false); setCustomExpectedIP(""); setCustomConfirms([]); setCustomConfirmInput(""); }}
                style={{ padding: "10px 14px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.inputBg, color: c.textDim, fontSize: 13, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAddingCustom(true)} style={{ width: "100%", padding: 12, borderRadius: 12, border: `2px dashed ${c.border}`, background: "transparent", color: c.textDim, fontSize: 14, cursor: "pointer", marginBottom: 12 }}>+ Add Custom Step</button>
        )}

        {/* Suggested Steps */}
        <button onClick={() => setShowSuggested(s => !s)} style={{
          width: "100%", padding: 12, borderRadius: 12, border: `1px solid ${c.border}`,
          background: showSuggested ? c.accentDim : c.surface, color: showSuggested ? c.accent : c.text,
          fontSize: 14, fontWeight: 600, cursor: "pointer", marginBottom: showSuggested ? 8 : 16,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        }}>
          <span>💡</span> Suggested Steps {showSuggested ? "▲" : "▼"}
        </button>
        {showSuggested && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {SUGGESTED_STEPS.filter(sg => !suggestedAlreadyAdded.has(sg.title)).map((sg, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12 }}>
                <span style={{ fontSize: 16 }}>{sg.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, color: c.text, fontWeight: 600 }}>{sg.title}</div>
                  <div style={{ fontSize: 11, color: c.textDim }}>{sg.instruction?.slice(0, 80)}...</div>
                </div>
                <button onClick={() => addSuggested(sg)} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: c.accentDim, color: c.accent, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer" }}>+ ADD</button>
              </div>
            ))}
            {SUGGESTED_STEPS.filter(sg => !suggestedAlreadyAdded.has(sg.title)).length === 0 && (
              <div style={{ padding: 12, textAlign: "center", color: c.textDim, fontSize: 13 }}>All suggested steps already added</div>
            )}
          </div>
        )}

        <button onClick={finalize} disabled={enabledCount === 0} style={{
          width: "100%", padding: 16, borderRadius: 14, border: "none",
          background: enabledCount > 0 ? `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})` : c.inputBg,
          color: enabledCount > 0 ? "#fff" : c.textMuted,
          fontSize: 17, fontWeight: 700, fontFamily: mono, cursor: enabledCount > 0 ? "pointer" : "not-allowed",
          boxShadow: enabledCount > 0 ? c.shadow : "none", letterSpacing: 0.5,
        }}>USE TEMPLATE ({enabledCount} steps) →</button>
      </div>
    </div>
  );
}

// ─── ADMIN SUBMISSION VIEWER ────────────────────────────────────
function AdminDashboard({ theme: c, mode, onToggleMode, actingPm, onChangeActingPm }) {
  const [showMineOnly, setShowMineOnly] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState({ tech: "", club: "", template: "", status: "", from: "", to: "" });
  const [selected, setSelected] = useState(null); // expanded session detail
  const [photos, setPhotos] = useState([]); // photos for selected session
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [showOverrides, setShowOverrides] = useState(false);
  const [calls, setCalls] = useState([]);
  const [showCalls, setShowCalls] = useState(false);
  const [showTeam, setShowTeam] = useState(false);
  const [showTemplatesManager, setShowTemplatesManager] = useState(false);
  const [allTemplates, setAllTemplates] = useState([]); // includes archived
  const [templateMgrSearch, setTemplateMgrSearch] = useState("");
  const [templateMgrIncludeArchived, setTemplateMgrIncludeArchived] = useState(false);
  const [sessionsIncludeArchived, setSessionsIncludeArchived] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(() => new Set());
  const [lastTemplateIdx, setLastTemplateIdx] = useState(null);
  const [selectedSessionIds, setSelectedSessionIds] = useState(() => new Set());
  const [lastSessionIdx, setLastSessionIdx] = useState(null);
  const [teamRoster, setTeamRoster] = useState([]);
  const [pmContacts, setPmContacts] = useState({});
  const [teamSearch, setTeamSearch] = useState("");
  const [teamEditing, setTeamEditing] = useState(null); // { name, email, phone, teams_webhook } (legacy modal — no longer used)
  const [teamRowEdits, setTeamRowEdits] = useState({}); // { name: { email, phone, teams_webhook } } — pending changes
  const [teamSavingRow, setTeamSavingRow] = useState(null); // name currently saving
  const [teamSavedRow, setTeamSavedRow] = useState(null); // name just saved (for flash)
  const [activeSessions, setActiveSessions] = useState([]);
  const PAGE_SIZE = 20;

  const loadSessions = useCallback(async (pg = 0) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.tech) params.set("tech", filters.tech);
    if (filters.club) params.set("club", filters.club);
    if (filters.ticket) params.set("ticket", filters.ticket);
    if (filters.template) params.set("template", filters.template);
    if (filters.status) params.set("status", filters.status);
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    if (showMineOnly && actingPm) params.set("pm_name", actingPm);
    if (sessionsIncludeArchived) params.set("include_archived", "1");
    params.set("limit", PAGE_SIZE);
    params.set("offset", pg * PAGE_SIZE);
    try {
      const res = await fetch(`/api/admin/sessions?${params}`);
      const data = await res.json();
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
      setPage(pg);
    } catch {}
    setLoading(false);
  }, [filters, showMineOnly, actingPm, sessionsIncludeArchived]);

  useEffect(() => { loadSessions(0); }, [loadSessions]);
  // Active Jobs panel respects the acting PM when showMineOnly is on
  useEffect(() => {
    const activeParams = new URLSearchParams({ status: "in_progress", limit: "50" });
    if (showMineOnly && actingPm) activeParams.set("pm_name", actingPm);
    fetch(`/api/admin/sessions?${activeParams}`).then(r => r.json()).then(d => setActiveSessions(d.sessions || [])).catch(() => {});
  }, [showMineOnly, actingPm]);
  useEffect(() => {
    fetch("/api/admin/templates").then(r => r.json()).then(d => setTemplates(d || [])).catch(() => {});
    fetch("/api/notifications?limit=50").then(r => r.json()).then(d => setNotifications(d || [])).catch(() => {});
    fetch("/api/overrides").then(r => r.json()).then(d => setOverrides(d || [])).catch(() => {});
    fetch("/api/calls").then(r => r.json()).then(d => setCalls(d || [])).catch(() => {});
    // Load all templates (including archived) for the Templates Manager
    fetch("/api/jobs?include_archived=1").then(r => r.json()).then(d => {
      if (Array.isArray(d)) setAllTemplates(d);
    }).catch(() => {});
    // Load team roster + contacts for the Team Directory panel
    fetch("/api/resources").then(r => r.json()).then(d => {
      if (d.roster && Array.isArray(d.roster)) {
        setTeamRoster([...d.roster].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      }
    }).catch(() => {});
    fetch("/api/pm-contacts").then(r => r.json()).then(d => {
      if (Array.isArray(d)) {
        const map = {};
        for (const c of d) map[c.name] = c;
        setPmContacts(map);
      }
    }).catch(() => {});
    // Auto-open specific session if URL has ?session=xxx
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session");
    if (sid) {
      fetch(`/api/admin/sessions/${sid}`).then(r => r.json()).then(data => {
        if (data && !data.error) {
          setSelected(data);
          setPhotos(data.photos || []);
        }
      }).catch(() => {});
    }
    // Request browser notification permission so PM gets desktop alerts
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Real-time notification polling — every 8 seconds, check for new notifications
  // and fire a browser push notification for high-priority items
  const lastSeenNotifIdRef = useRef(null);
  const notifSoundRef = useRef(null);
  useEffect(() => {
    const poll = async () => {
      try {
        const res = await fetch("/api/notifications?limit=20");
        const data = await res.json();
        if (!Array.isArray(data)) return;
        // On first load, just remember the latest ID
        if (lastSeenNotifIdRef.current === null) {
          lastSeenNotifIdRef.current = data[0]?.id || "";
          setNotifications(data);
          return;
        }
        // Find new notifications since last check
        const newOnes = [];
        for (const n of data) {
          if (n.id === lastSeenNotifIdRef.current) break;
          newOnes.push(n);
        }
        if (newOnes.length > 0) {
          lastSeenNotifIdRef.current = data[0].id;
          setNotifications(data);
          // Fire browser notifications for high/medium severity
          for (const n of newOnes) {
            if ((n.severity === "high" || n.severity === "medium" || n.type === "ai_alert" || n.type === "override_request" || n.type === "call_pm" || n.type === "hard_gate") && "Notification" in window && Notification.permission === "granted") {
              try {
                new Notification(`🚨 ${n.tech_name || "Tech"} — ${n.club_number || ""}`, {
                  body: n.message || "",
                  icon: "/favicon.ico",
                  tag: n.id,
                  requireInteraction: n.severity === "high",
                });
              } catch {}
            }
          }
          // Play a sound if sound element exists
          if (notifSoundRef.current && newOnes.some(n => n.severity === "high")) {
            try { notifSoundRef.current.play(); } catch {}
          }
        }
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 8000);
    return () => clearInterval(iv);
  }, []);

  const markRead = async (id) => {
    await fetch(`/api/notifications/${id}/read`, { method: "POST" }).catch(() => {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: 1 } : n));
  };

  const unreadCount = notifications.filter(n => !n.read).length;
  const pendingOverrides = overrides.filter(o => o.status === "pending");

  const handleOverride = async (id, action) => {
    try {
      await fetch(`/api/overrides/${id}/${action}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [`${action === "approve" ? "approved" : "rejected"}_by`]: "PM" }),
      });
      setOverrides(prev => prev.map(o => o.id === id ? { ...o, status: action === "approve" ? "approved" : "rejected", resolved_by: "PM", resolved_at: new Date().toISOString() } : o));
    } catch {}
  };

  const loadDetail = async (session) => {
    setSelected(session);
    try {
      const res = await fetch(`/api/admin/sessions/${session.id}`);
      const data = await res.json();
      setSelected(data);
      setPhotos(data.photos || []);
    } catch {}
  };

  // Auto-refresh in-progress session detail every 5s
  useEffect(() => {
    if (!selected || selected.status !== "in_progress") return;
    const iv = setInterval(async () => {
      try {
        const res = await fetch(`/api/admin/sessions/${selected.id}`);
        const data = await res.json();
        if (data && !data.error) {
          setSelected(prev => ({ ...prev, ...data }));
          setPhotos(data.photos || []);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(iv);
  }, [selected?.id, selected?.status]);

  const fmtDate = (d) => {
    if (!d) return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString() + " " + dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Export all sessions by paginating the admin sessions API
  const exportSessionsCsv = async () => {
    if (exportingCsv) return;
    setExportingCsv(true);
    try {
      const params = new URLSearchParams();
      if (filters.tech) params.set("tech", filters.tech);
      if (filters.club) params.set("club", filters.club);
      if (filters.ticket) params.set("ticket", filters.ticket);
      if (filters.template) params.set("template", filters.template);
      if (filters.status) params.set("status", filters.status);
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      if (showMineOnly && actingPm) params.set("pm_name", actingPm);
      if (sessionsIncludeArchived) params.set("include_archived", "1");

      const all = [];
      const pageSize = 200;
      let offset = 0;
      let total = null;
      while (true) {
        params.set("limit", String(pageSize));
        params.set("offset", String(offset));
        const res = await fetch(`/api/admin/sessions?${params}`);
        if (!res.ok) {
          const err = await res.text();
          throw new Error(`Failed to load sessions: ${res.status} ${err}`);
        }
        const data = await res.json();
        const batch = data.sessions || [];
        total = total === null ? (data.total || 0) : total;
        if (!batch.length) break;
        all.push(...batch);
        offset += batch.length;
        if (total && all.length >= total) break;
        if (batch.length < pageSize) break;
      }

      if (all.length === 0) { alert("No sessions to export"); return; }

      const headers = ["ID", "Template ID", "Ticket", "Job Title", "Tech Name", "Club", "Status", "Started At", "Completed At", "Updated At", "PM Name", "Created By PM", "Archived", "Photo Count"];
      const rows = all.map(s => [
        s.id,
        s.template_id || "",
        s.ticket_number || "",
        s.job_title || "",
        s.tech_name || "",
        s.club_number || "",
        s.status || "",
        s.started_at || "",
        s.completed_at || "",
        s.updated_at || "",
        s.pm_name || "",
        s.created_by_pm || "",
        s.archived ? "yes" : "no",
        s.photo_count || 0,
      ]);
      const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `sessions-export-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Export failed:", err);
      alert("Export failed. Check console for details.");
    } finally {
      setExportingCsv(false);
    }
  };

  const exportPDF = async (session) => {
    if (pdfBusy) return;
    setPdfBusy(true);
    setPdfError(null);
    try {
      const { jsPDF } = await import("jspdf");
      const doc = new jsPDF();
      let y = 20;

    doc.setFontSize(20);
    doc.setTextColor(0, 100, 50);
    doc.text("CB-OPS Job Report", 20, y); y += 12;
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(`Job: ${session.job_title || "Untitled"}`, 20, y); y += 7;
    doc.text(`Tech: ${session.tech_name || "—"}`, 20, y); y += 7;
    doc.text(`Club: ${session.club_number || "—"}`, 20, y); y += 7;
    doc.text(`Status: ${session.status || "—"}`, 20, y); y += 7;
    doc.text(`Started: ${fmtDate(session.started_at)}`, 20, y); y += 7;
    doc.text(`Completed: ${fmtDate(session.completed_at)}`, 20, y); y += 12;

    // Steps
    const steps = session.steps || [];
    const resultSteps = session.results?.steps || [];
    if (steps.length > 0) {
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text("Steps", 20, y); y += 8;
      doc.setFontSize(10);
      for (const step of steps) {
        if (y > 270) { doc.addPage(); y = 20; }
        const rs = resultSteps.find(r => r.id === step.id);
        const pass = rs ? rs.pass !== false : true;
        doc.setTextColor(pass ? 0 : 200, pass ? 120 : 0, 0);
        doc.text(`${pass ? "PASS" : "FAIL"} — Step ${step.id}: ${step.title}`, 24, y); y += 5;
        doc.setTextColor(80, 80, 80);
        const instrLines = doc.splitTextToSize(step.instruction || "", 160);
        doc.text(instrLines, 28, y); y += instrLines.length * 4.5 + 3;
      }
      y += 5;
    }

    // Inventory
    if (session.results?.inventory) {
      const invKeys = Object.keys(session.results.inventory);
      if (invKeys.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text("Inventory", 20, y); y += 8;
        doc.setFontSize(10);
        for (const key of invKeys) {
          const refStep = steps.find(s => s.id === parseInt(key));
          doc.setTextColor(0, 100, 50);
          doc.text(`${refStep?.title || `Step ${key}`}:`, 24, y); y += 5;
          doc.setTextColor(60, 60, 60);
          for (const item of session.results.inventory[key]) {
            if (y > 270) { doc.addPage(); y = 20; }
            doc.text(`  - ${Object.values(item).filter(Boolean).join(" | ")}`, 28, y); y += 5;
          }
          y += 3;
        }
      }
    }

    // Photos
    if (photos.length > 0) {
      doc.addPage(); y = 20;
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text("Photos", 20, y); y += 10;
      for (const photo of photos) {
        if (y > 200) { doc.addPage(); y = 20; }
        try {
          const res = await fetch(photo.url);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const mime = (blob.type || photo.content_type || "image/jpeg").toLowerCase();
          const fmt = mime.includes("png") ? "PNG" : mime.includes("webp") ? "WEBP" : "JPEG";
          const b64 = await new Promise((res2, rej2) => {
            const fr = new FileReader();
            fr.onload = () => res2(fr.result);
            fr.onerror = () => rej2(new Error("FileReader failed"));
            fr.readAsDataURL(blob);
          });
          doc.addImage(b64, fmt, 20, y, 80, 60);
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          doc.text(`Step ${photo.step_id} — ${photo.ai_pass === 1 ? "AI: PASS" : photo.ai_pass === 0 ? "AI: FAIL" : "No AI check"} ${photo.ai_message ? "— " + photo.ai_message : ""}`, 105, y + 10);
          doc.text(fmtDate(photo.created_at), 105, y + 16);
          y += 68;
        } catch (e) {
          doc.setFontSize(9);
          doc.setTextColor(120, 120, 120);
          doc.text(`[Photo ${photo.id} — skipped: ${e?.message || "load failed"}]`, 20, y); y += 8;
        }
      }
    }

      doc.save(`CB-OPS_${session.job_title || "Job"}_${session.club_number || ""}_${session.tech_name || ""}.pdf`.replace(/\s+/g, "_"));
    } catch (err) {
      console.error("PDF export failed:", err);
      setPdfError(err?.message || String(err));
    } finally {
      setPdfBusy(false);
    }
  };

  // ─── Detail View ───
  if (selected) {
    const stepList = selected.steps || [];
    const resultSteps = selected.results?.steps || [];
    return (
      <div style={{ minHeight: "100dvh", background: c.bg, fontFamily: sans }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
        <div style={{ maxWidth: 800, margin: "0 auto", padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
            <button onClick={() => { setSelected(null); setPhotos([]); }} style={{ background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.textDim, padding: "6px 14px", cursor: "pointer", fontSize: 14 }}>← Back</button>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, color: c.text, fontWeight: 700 }}>{selected.job_title || "Untitled"}</div>
              <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono }}>{selected.tech_name} • {selected.club_number}</div>
            </div>
            <button onClick={() => exportPDF(selected)} disabled={pdfBusy} style={{ padding: "8px 16px", borderRadius: 10, border: "none", background: pdfBusy ? c.inputBg : `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})`, color: pdfBusy ? c.textDim : "#fff", fontSize: 13, fontWeight: 700, fontFamily: mono, cursor: pdfBusy ? "wait" : "pointer", opacity: pdfBusy ? 0.7 : 1 }}>{pdfBusy ? "GENERATING…" : "PDF EXPORT"}</button>
          </div>
          {pdfError && (
            <div style={{ background: "#fee", border: "1px solid #fcc", borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 13, color: "#900", fontFamily: mono }}>
              PDF export error: {pdfError}
            </div>
          )}

          {/* Live indicator for in-progress */}
          {selected.status === "in_progress" && (
            <div style={{ background: c.accentDim, border: `1.5px solid ${c.accentBorder}`, borderRadius: 14, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 5, background: c.accent, animation: "pulse 2s ease infinite" }} />
                <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
                <span style={{ fontSize: 11, color: c.accent, fontWeight: 700, fontFamily: mono, letterSpacing: 1.5 }}>LIVE — updates every 5s</span>
              </div>
              <div style={{ fontSize: 14, color: c.text, fontWeight: 700 }}>
                Current step: {(selected.results?.current_step || (selected.results?.step_idx != null ? selected.results.step_idx + 1 : "?"))}/{selected.results?.total_steps || stepList.length}
                {selected.results?.step_title && <span style={{ color: c.accent }}> — {selected.results.step_title}</span>}
              </div>
              <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, marginTop: 4 }}>
                {selected.results?.confirmations_count || Object.keys(selected.results?.confirmations || selected.results?.cfs || {}).length} confirmations ·
                {" "}{selected.results?.inventory_items || 0} inventory items ·
                {" "}{photos.length} photos
              </div>
              <button onClick={() => {
                // Open the template editor with this session's template for live editing
                fetch(`/api/jobs/${selected.template_id}`).then(r => r.json()).then(tpl => {
                  if (tpl && !tpl.error) {
                    window.location.href = `/?edit_template=${tpl.id}`;
                  }
                }).catch(() => {});
              }} style={{
                marginTop: 10, padding: "8px 14px", borderRadius: 10, border: `1px solid ${c.accentBorder}`,
                background: c.surface, color: c.accent, fontSize: 12, fontWeight: 700, fontFamily: mono, cursor: "pointer",
              }}>✏️ EDIT CHECKLIST LIVE</button>
            </div>
          )}

          {/* Summary card */}
          <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 16, marginBottom: 16, display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div><div style={{ fontSize: 10, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1 }}>STATUS</div><div style={{ fontSize: 14, color: selected.status === "completed" ? c.accent : c.warn, fontWeight: 700 }}>{(selected.status || "—").toUpperCase()}</div></div>
            <div><div style={{ fontSize: 10, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1 }}>STARTED</div><div style={{ fontSize: 13, color: c.text }}>{fmtDate(selected.started_at)}</div></div>
            <div><div style={{ fontSize: 10, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1 }}>COMPLETED</div><div style={{ fontSize: 13, color: c.text }}>{fmtDate(selected.completed_at)}</div></div>
            <div><div style={{ fontSize: 10, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1 }}>PHOTOS</div><div style={{ fontSize: 13, color: c.text }}>{photos.length}</div></div>
          </div>

          {/* Step timeline */}
          <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>STEP TIMELINE</div>
          {stepList.map((step, i) => {
            const rs = resultSteps.find(r => r.id === step.id);
            const pass = rs ? rs.pass !== false : null;
            const stepPhotos = photos.filter(p => p.step_id === step.id);
            return (
              <div key={i} style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 24 }}>
                  <div style={{ width: 24, height: 24, borderRadius: 12, background: pass === true ? c.accent : pass === false ? c.fail : c.inputBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#fff", fontWeight: 700, flexShrink: 0 }}>{pass === true ? "✓" : pass === false ? "✗" : "·"}</div>
                  {i < stepList.length - 1 && <div style={{ width: 2, flex: 1, background: c.border, marginTop: 4 }} />}
                </div>
                <div style={{ flex: 1, background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, padding: 14, marginBottom: 2 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16 }}>{step.icon || "📋"}</span>
                    <span style={{ fontSize: 14, color: c.text, fontWeight: 700 }}>{step.title}</span>
                    {step.type !== "instruction" && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: c.inputBg, color: c.textDim, fontFamily: mono }}>{step.type?.toUpperCase()}</span>}
                  </div>
                  <div style={{ fontSize: 13, color: c.textDim, lineHeight: 1.5 }}>{step.instruction}</div>
                  {/* Reference images from SOW */}
                  {step.referenceImages?.length > 0 && (
                    <div style={{ marginTop: 8, padding: 8, background: c.inputBg, borderRadius: 8, border: `1px solid ${c.border}` }}>
                      <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>REFERENCE FROM SOW</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {step.referenceImages.map((ref, ri) => (
                          <div key={ri} style={{ maxWidth: 200 }}>
                            {ref.url && <img src={ref.url} alt={ref.description || `Page ${ref.page}`}
                              onClick={() => window.open(ref.url, "_blank")}
                              style={{ width: "100%", maxHeight: 150, objectFit: "contain", borderRadius: 6, border: `1px solid ${c.border}`, cursor: "pointer" }} />}
                            <div style={{ fontSize: 10, color: c.textDim, marginTop: 3 }}>{ref.description || `Page ${ref.page}`}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Step photos */}
                  {stepPhotos.length > 0 && (
                    <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                      {stepPhotos.map(p => (
                        <div key={p.id} style={{ position: "relative" }}>
                          <img src={p.url} alt="" style={{ width: 100, height: 75, objectFit: "cover", borderRadius: 8, border: `2px solid ${p.ai_pass === 1 ? c.accent : p.ai_pass === 0 ? c.fail : c.border}` }} />
                          {p.ai_pass !== null && (
                            <div style={{ position: "absolute", top: 4, right: 4, width: 18, height: 18, borderRadius: 9, background: p.ai_pass === 1 ? c.accent : c.fail, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 700 }}>{p.ai_pass === 1 ? "✓" : "✗"}</div>
                          )}
                          {p.ai_message && <div style={{ fontSize: 10, color: c.textDim, marginTop: 2, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.ai_message}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Inventory data */}
          {selected.results?.inventory && Object.keys(selected.results.inventory).length > 0 && (
            <>
              <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginTop: 20, marginBottom: 10 }}>INVENTORY DATA</div>
              {Object.entries(selected.results.inventory).map(([stepId, items]) => {
                const refStep = stepList.find(s => s.id === parseInt(stepId));
                return (
                  <div key={stepId} style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, padding: 14, marginBottom: 10 }}>
                    <div style={{ fontSize: 13, color: c.accent, fontWeight: 700, fontFamily: mono, marginBottom: 8 }}>{refStep?.title || `Step ${stepId}`} — {items.length} items</div>
                    {items.map((item, idx) => (
                      <div key={idx} style={{ display: "flex", gap: 8, padding: "6px 10px", background: idx % 2 === 0 ? c.inputBg : "transparent", borderRadius: 6, fontSize: 13, color: c.text }}>
                        <span style={{ fontWeight: 700, color: c.accent, fontFamily: mono, minWidth: 20 }}>{idx + 1}</span>
                        {Object.entries(item).map(([k, v]) => <span key={k} style={{ marginRight: 12 }}><strong style={{ color: c.textDim }}>{k}:</strong> {v}</span>)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </>
          )}

          {/* All photos gallery */}
          {photos.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginTop: 20, marginBottom: 10 }}>ALL PHOTOS ({photos.length})</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
                {photos.map(p => (
                  <div key={p.id} style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 10, overflow: "hidden" }}>
                    <img src={p.url} alt="" style={{ width: "100%", height: 120, objectFit: "cover" }} />
                    <div style={{ padding: "6px 8px" }}>
                      <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono }}>Step {p.step_id}</div>
                      {p.ai_pass !== null && <div style={{ fontSize: 11, color: p.ai_pass === 1 ? c.accent : c.fail, fontWeight: 700 }}>{p.ai_pass === 1 ? "AI: PASS" : "AI: FAIL"}</div>}
                      {p.ai_message && <div style={{ fontSize: 10, color: c.textDim, marginTop: 2 }}>{p.ai_message}</div>}
                      <div style={{ fontSize: 10, color: c.textMuted, marginTop: 2 }}>{fmtDate(p.created_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {/* Chat History */}
          {selected.results?.chat_history?.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginTop: 20, marginBottom: 10 }}>CHAT HISTORY ({selected.results.chat_history.length} messages)</div>
              <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, padding: 12, maxHeight: 400, overflow: "auto" }}>
                {selected.results.chat_history.map((msg, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, justifyContent: msg.from === "user" ? "flex-end" : "flex-start" }}>
                    <div style={{
                      maxWidth: "80%", padding: "8px 12px", borderRadius: msg.from === "user" ? "12px 4px 12px 12px" : "4px 12px 12px 12px",
                      background: msg.from === "user" ? "rgba(99,102,241,0.07)" : c.inputBg,
                      border: `1px solid ${msg.from === "user" ? "rgba(99,102,241,0.15)" : c.border}`,
                    }}>
                      {msg.photo ? <span style={{ fontSize: 12, color: c.textDim }}>[Photo uploaded]</span> : <div style={{ fontSize: 13, color: c.text, whiteSpace: "pre-wrap", lineHeight: 1.4 }}>{msg.content}</div>}
                      <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono, marginTop: 3 }}>{msg.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Confirmations Timeline */}
          {selected.results?.confirmations && Object.keys(selected.results.confirmations).length > 0 && (
            <>
              <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginTop: 20, marginBottom: 10 }}>CONFIRMATIONS</div>
              <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, padding: 12 }}>
                {Object.entries(selected.results.confirmations).map(([key, ts]) => (
                  <div key={key} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${c.border}`, fontSize: 12 }}>
                    <span style={{ color: c.text }}>{key.replace(/_/g, " ")}</span>
                    <span style={{ color: c.textDim, fontFamily: mono }}>{typeof ts === "string" && ts.includes("T") ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "✓"}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ─── List View ───
  return (
    <div style={{ minHeight: "100dvh", background: c.bg, fontFamily: sans }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      <div style={{ maxWidth: 900, margin: "0 auto", padding: 20 }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, boxShadow: c.shadow }}>⚡</div>
            <div>
              <div style={{ fontSize: 18, fontFamily: mono, color: c.accent, fontWeight: 800 }}>CB-OPS</div>
              <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono, letterSpacing: 2 }}>ADMIN • SUBMISSIONS</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={() => { setShowTemplatesManager(s => !s); setShowTeam(false); setShowCalls(false); setShowOverrides(false); setShowNotifs(false); }} title="Manage Templates" style={{ position: "relative", padding: "6px 10px", borderRadius: 8, border: `1px solid ${showTemplatesManager ? c.accentBorder : c.border}`, background: showTemplatesManager ? c.accentDim : c.inputBg, color: c.text, fontSize: 16, cursor: "pointer" }}>
              📁
              {allTemplates.length > 0 && <span style={{ position: "absolute", top: -4, right: -4, minWidth: 18, height: 18, padding: "0 4px", borderRadius: 9, background: c.accent, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{allTemplates.filter(t => !t.archived).length}</span>}
            </button>
            <button onClick={() => { setShowTeam(s => !s); setShowTemplatesManager(false); setShowCalls(false); setShowOverrides(false); setShowNotifs(false); }} title="Team Directory" style={{ position: "relative", padding: "6px 10px", borderRadius: 8, border: `1px solid ${showTeam ? c.accentBorder : c.border}`, background: showTeam ? c.accentDim : c.inputBg, color: c.text, fontSize: 16, cursor: "pointer" }}>
              👥
              {teamRoster.length > 0 && <span style={{ position: "absolute", top: -4, right: -4, minWidth: 18, height: 18, padding: "0 4px", borderRadius: 9, background: c.accent, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{teamRoster.length}</span>}
            </button>
            <button onClick={() => { setShowCalls(s => !s); setShowOverrides(false); setShowNotifs(false); setShowTeam(false); setShowTemplatesManager(false); }} style={{ position: "relative", padding: "6px 10px", borderRadius: 8, border: `1px solid ${showCalls ? "rgba(99,102,241,0.3)" : c.border}`, background: showCalls ? "rgba(99,102,241,0.08)" : c.inputBg, color: c.text, fontSize: 16, cursor: "pointer" }}>
              📞
              {calls.length > 0 && <span style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: 9, background: "rgba(99,102,241,0.8)", color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{calls.length}</span>}
            </button>
            <button onClick={() => { setShowOverrides(s => !s); setShowNotifs(false); setShowCalls(false); setShowTeam(false); setShowTemplatesManager(false); }} style={{ position: "relative", padding: "6px 10px", borderRadius: 8, border: `1px solid ${showOverrides ? "rgba(239,68,68,0.3)" : c.border}`, background: showOverrides ? c.failDim : c.inputBg, color: c.text, fontSize: 16, cursor: "pointer" }}>
              🛡️
              {pendingOverrides.length > 0 && <span style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: 9, background: c.fail, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{pendingOverrides.length}</span>}
            </button>
            <button onClick={() => { setShowNotifs(s => !s); setShowOverrides(false); setShowCalls(false); setShowTeam(false); setShowTemplatesManager(false); }} style={{ position: "relative", padding: "6px 10px", borderRadius: 8, border: `1px solid ${showNotifs ? c.accentBorder : c.border}`, background: showNotifs ? c.accentDim : c.inputBg, color: c.text, fontSize: 16, cursor: "pointer" }}>
              🔔
              {unreadCount > 0 && <span style={{ position: "absolute", top: -4, right: -4, width: 18, height: 18, borderRadius: 9, background: c.fail, color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{unreadCount}</span>}
            </button>
            <button onClick={() => window.location.href = "/"} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.inputBg, color: c.textDim, fontSize: 13, cursor: "pointer" }}>← Home</button>
            <ThemeToggle mode={mode} onToggle={onToggleMode} />
          </div>
        </div>

        {/* Signed in as + My/All toggle */}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
          <button onClick={onChangeActingPm} style={{
            padding: "8px 14px", borderRadius: 10, cursor: "pointer",
            border: `1px solid ${actingPm ? c.accentBorder : c.border}`,
            background: actingPm ? c.accentDim : c.surface,
            color: actingPm ? c.accent : c.textDim,
            fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6,
          }}>
            <span>👤</span>
            <span>{actingPm || "Sign in"}</span>
          </button>
          {actingPm && (
            <div style={{ display: "flex", borderRadius: 10, overflow: "hidden", border: `1px solid ${c.border}` }}>
              <button onClick={() => setShowMineOnly(true)} style={{
                padding: "8px 14px", border: "none", cursor: "pointer",
                background: showMineOnly ? c.accent : c.surface,
                color: showMineOnly ? "#fff" : c.textDim,
                fontSize: 12, fontWeight: 700, fontFamily: mono,
              }}>MY JOBS</button>
              <button onClick={() => setShowMineOnly(false)} style={{
                padding: "8px 14px", border: "none", cursor: "pointer",
                background: !showMineOnly ? c.accent : c.surface,
                color: !showMineOnly ? "#fff" : c.textDim,
                fontSize: 12, fontWeight: 700, fontFamily: mono,
              }}>ALL JOBS</button>
            </div>
          )}
        </div>

        {/* Notifications Panel */}
        {showNotifs && (
          <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 16, marginBottom: 20, maxHeight: 500, overflow: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5 }}>NOTIFICATION LOG ({notifications.length})</div>
              {unreadCount > 0 && <span style={{ fontSize: 11, color: c.accent, fontFamily: mono }}>{unreadCount} unread</span>}
            </div>
            {/* Delivery status info */}
            <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 10, padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, lineHeight: 1.5 }}>
              📍 <strong>Current delivery:</strong> in-app (this panel) + browser push (when admin is open). External delivery (Teams/SMS/email) fires for any PM with contact info configured in the 👥 Team Directory.
            </div>
            {notifications.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: c.textDim, fontSize: 13 }}>No notifications yet</div>
            ) : notifications.map(n => (
              <div key={n.id} onClick={() => !n.read && markRead(n.id)} style={{
                display: "flex", gap: 10, padding: "10px 12px", marginBottom: 4, borderRadius: 10, cursor: n.read ? "default" : "pointer",
                background: n.read ? "transparent" : n.type === "hard_gate" ? c.failDim : n.type === "ai_flag" || n.type === "ai_alert" ? "rgba(245,158,11,0.06)" : n.type === "call_pm" ? "rgba(99,102,241,0.06)" : c.accentDim,
                border: `1px solid ${n.read ? c.border : n.type === "hard_gate" ? "rgba(239,68,68,0.2)" : n.type === "ai_flag" || n.type === "ai_alert" ? "rgba(245,158,11,0.2)" : c.accentBorder}`,
              }}>
                <div style={{ fontSize: 18, flexShrink: 0 }}>
                  {n.type === "hard_gate" ? "🚫" :
                   n.type === "ai_flag" || n.type === "ai_alert" ? "⚠️" :
                   n.type === "call_pm" ? "📞" :
                   n.type === "job_complete" ? "✅" :
                   n.type === "step_complete" ? "▶" :
                   n.type === "override_request" ? "🛡️" : "📋"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 2 }}>
                    <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, fontFamily: mono, fontWeight: 700, background: n.severity === "high" ? c.failDim : n.severity === "medium" ? "rgba(245,158,11,0.1)" : c.inputBg, color: n.severity === "high" ? c.fail : n.severity === "medium" ? c.warn : c.textDim }}>{(n.severity || "info").toUpperCase()}</span>
                    <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, fontFamily: mono, fontWeight: 700, background: c.inputBg, color: c.textDim }}>{n.type}</span>
                    {n.pm_name && <span style={{ fontSize: 10, color: c.accent, fontFamily: mono }}>→ {n.pm_name}</span>}
                  </div>
                  <div style={{ fontSize: 13, color: c.text, fontWeight: n.read ? 400 : 600, lineHeight: 1.4 }}>{n.message}</div>
                  <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, display: "flex", gap: 8, marginTop: 3, flexWrap: "wrap" }}>
                    <span>Tech: {n.tech_name || "—"}</span>
                    <span>Club: {n.club_number || "—"}</span>
                    {n.step_title && <span>Step: {n.step_title}</span>}
                    <span>{n.created_at ? new Date(n.created_at + "Z").toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  </div>
                  {/* Delivery status */}
                  <div style={{ fontSize: 10, color: c.textMuted, marginTop: 3, display: "flex", gap: 6 }}>
                    <span>📬 Logged</span>
                    <span>✓ Browser push</span>
                    <span style={{ opacity: 0.5 }}>✗ Teams (not configured)</span>
                    <span style={{ opacity: 0.5 }}>✗ SMS (not configured)</span>
                    <span style={{ opacity: 0.5 }}>✗ Email (not configured)</span>
                  </div>
                </div>
                {!n.read && <div style={{ width: 8, height: 8, borderRadius: 4, background: c.accent, flexShrink: 0, marginTop: 6 }} />}
              </div>
            ))}
          </div>
        )}

        {/* Templates Manager Panel */}
        {showTemplatesManager && (() => {
          const filtered = allTemplates
            .filter(t => templateMgrIncludeArchived || !t.archived)
            .filter(t => !templateMgrSearch.trim() || (t.title || "").toLowerCase().includes(templateMgrSearch.toLowerCase()) || (t.ticket_number || "").toLowerCase().includes(templateMgrSearch.toLowerCase()));
          const toggleArchive = async (t) => {
            try {
              await fetch(`/api/jobs/${t.id}`, {
                method: "PATCH", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ archived: !t.archived }),
              });
              setAllTemplates(prev => prev.map(x => x.id === t.id ? { ...x, archived: !t.archived } : x));
            } catch {}
          };
          const toggleSelect = (id, idx, shift) => {
            setSelectedTemplateIds(prev => {
              const next = new Set(prev);
              if (shift && lastTemplateIdx !== null) {
                const [a, b] = [Math.min(lastTemplateIdx, idx), Math.max(lastTemplateIdx, idx)];
                const shouldAdd = !prev.has(id);
                for (let i = a; i <= b; i++) {
                  const row = filtered[i];
                  if (row) { if (shouldAdd) next.add(row.id); else next.delete(row.id); }
                }
              } else {
                if (next.has(id)) next.delete(id); else next.add(id);
              }
              return next;
            });
            setLastTemplateIdx(idx);
          };
          const allSelected = filtered.length > 0 && filtered.every(t => selectedTemplateIds.has(t.id));
          const bulkArchive = async (archive) => {
            const ids = [...selectedTemplateIds];
            if (ids.length === 0) return;
            if (!confirm(`${archive ? "Archive" : "Unarchive"} ${ids.length} template(s)?`)) return;
            await Promise.all(ids.map(id => fetch(`/api/jobs/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ archived: archive ? 1 : 0 }) })));
            setAllTemplates(prev => prev.map(x => ids.includes(x.id) ? { ...x, archived: archive ? 1 : 0 } : x));
            setSelectedTemplateIds(new Set());
          };
          const bulkDelete = async () => {
            const ids = [...selectedTemplateIds].filter(id => {
              const t = allTemplates.find(x => x.id === id);
              return t && (t.session_count || 0) === 0;
            });
            if (ids.length === 0) { alert("No deletable templates selected (must have 0 sessions)."); return; }
            if (!confirm(`Permanently delete ${ids.length} template(s) with no sessions? Cannot be undone.`)) return;
            await Promise.all(ids.map(id => fetch(`/api/jobs/${id}`, { method: "DELETE" })));
            setAllTemplates(prev => prev.filter(x => !ids.includes(x.id)));
            setSelectedTemplateIds(new Set());
          };
          const hardDelete = async (t) => {
            if (!confirm(`Permanently delete "${t.title}"?\n\nThis cannot be undone. Templates with existing sessions cannot be deleted (archive them instead).`)) return;
            try {
              const res = await fetch(`/api/jobs/${t.id}`, { method: "DELETE" });
              const data = await res.json();
              if (data.error) { alert(data.error); return; }
              setAllTemplates(prev => prev.filter(x => x.id !== t.id));
            } catch (e) { alert("Delete failed: " + e.message); }
          };
          return (
            <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 16, marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5 }}>MANAGE TEMPLATES ({filtered.length} of {allTemplates.length})</div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <input value={templateMgrSearch} onChange={e => setTemplateMgrSearch(e.target.value)} placeholder="Search..."
                    style={{ padding: "6px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 12, outline: "none", width: 150 }} />
                  <label style={{ fontSize: 11, color: c.textDim, fontFamily: mono, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                    <input type="checkbox" checked={templateMgrIncludeArchived} onChange={e => setTemplateMgrIncludeArchived(e.target.checked)} />
                    Show archived
                  </label>
                </div>
              </div>
              <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 10, padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, lineHeight: 1.5 }}>
                📁 Archive templates you're no longer using — they stay in the database (sessions remain intact) but are hidden from the home screen dropdown. Unarchive any time. Permanent delete only works for templates with no sessions.
              </div>

              {/* Bulk action bar */}
              {selectedTemplateIds.size > 0 && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 10px", marginBottom: 8, borderRadius: 8, background: c.accentDim, border: `1px solid ${c.accentBorder}` }}>
                  <div style={{ fontSize: 12, color: c.accent, fontFamily: mono, fontWeight: 700, flex: 1 }}>{selectedTemplateIds.size} selected</div>
                  <button onClick={() => bulkArchive(true)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.inputBg, color: c.text, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer" }}>ARCHIVE</button>
                  <button onClick={() => bulkArchive(false)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.inputBg, color: c.text, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer" }}>UNARCHIVE</button>
                  <button onClick={bulkDelete} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.failDim, color: c.fail, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer" }}>DELETE</button>
                  <button onClick={() => setSelectedTemplateIds(new Set())} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.inputBg, color: c.textDim, fontSize: 11, fontFamily: mono, cursor: "pointer" }}>CLEAR</button>
                </div>
              )}

              {/* Table */}
              <div style={{ maxHeight: 500, overflow: "auto" }}>
                <div style={{ display: "grid", gridTemplateColumns: "32px 2fr 1.2fr 1.2fr 0.8fr 0.7fr 1fr", gap: 8, padding: "8px 10px", fontSize: 10, color: c.textMuted, fontFamily: mono, fontWeight: 700, letterSpacing: 1, borderBottom: `1px solid ${c.border}`, textTransform: "uppercase", position: "sticky", top: 0, background: c.surface, zIndex: 1 }}>
                  <div><input type="checkbox" checked={allSelected} onChange={() => setSelectedTemplateIds(allSelected ? new Set() : new Set(filtered.map(t => t.id)))} /></div>
                  <div>Title</div>
                  <div>Ticket #</div>
                  <div>Owner PM</div>
                  <div>Steps</div>
                  <div>Sessions</div>
                  <div style={{ textAlign: "right" }}>Actions</div>
                </div>
                {filtered.map((t, idx) => (
                  <div key={t.id} style={{
                    display: "grid", gridTemplateColumns: "32px 2fr 1.2fr 1.2fr 0.8fr 0.7fr 1fr", gap: 8,
                    padding: "10px", borderBottom: `1px solid ${c.border}`, alignItems: "center",
                    opacity: t.archived ? 0.55 : 1,
                    background: selectedTemplateIds.has(t.id) ? c.accentDim : (t.archived ? c.inputBg : "transparent"),
                  }}>
                    <div onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedTemplateIds.has(t.id)}
                        onClick={e => { e.stopPropagation(); toggleSelect(t.id, idx, e.shiftKey); }}
                        onChange={() => {}} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: c.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.archived && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 4, background: c.textMuted, color: c.bg, fontFamily: mono, marginRight: 6 }}>ARCHIVED</span>}
                        {t.title || "Untitled"}
                      </div>
                      {t.description && <div style={{ fontSize: 10, color: c.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.description.slice(0, 60)}</div>}
                    </div>
                    <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, overflow: "hidden", textOverflow: "ellipsis" }}>{t.ticket_number || "—"}</div>
                    <div style={{ fontSize: 11, color: c.textDim, overflow: "hidden", textOverflow: "ellipsis" }}>{t.created_by_pm || "—"}</div>
                    <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono }}>{t.step_count || 0}</div>
                    <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono }}>{t.session_count || 0}</div>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <button onClick={() => toggleArchive(t)} style={{
                        padding: "4px 10px", borderRadius: 6, border: `1px solid ${c.border}`,
                        background: c.inputBg, color: c.textDim, fontSize: 10, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                      }}>{t.archived ? "UNARCHIVE" : "ARCHIVE"}</button>
                      {(t.session_count || 0) === 0 && (
                        <button onClick={() => hardDelete(t)} style={{
                          padding: "4px 8px", borderRadius: 6, border: `1px solid ${c.border}`,
                          background: c.failDim, color: c.fail, fontSize: 10, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                        }}>DELETE</button>
                      )}
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && (
                  <div style={{ padding: 40, textAlign: "center", color: c.textDim, fontSize: 13 }}>No templates match filter</div>
                )}
              </div>

              <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${c.border}`, display: "flex", gap: 16 }}>
                <span>Active: {allTemplates.filter(t => !t.archived).length}</span>
                <span>Archived: {allTemplates.filter(t => t.archived).length}</span>
                <span>Total: {allTemplates.length}</span>
              </div>
            </div>
          );
        })()}

        {/* Team Directory Panel */}
        {showTeam && (
          <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 16, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5 }}>TEAM DIRECTORY ({teamRoster.length})</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input value={teamSearch} onChange={e => setTeamSearch(e.target.value)} placeholder="Search name..."
                  style={{ padding: "6px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 12, outline: "none", width: 150 }} />
                <button onClick={() => {
                  fetch("/api/resources?refresh=1").then(r => r.json()).then(d => {
                    if (d.roster) setTeamRoster([...d.roster].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
                  }).catch(() => {});
                  fetch("/api/pm-contacts").then(r => r.json()).then(d => {
                    if (Array.isArray(d)) { const map = {}; for (const c of d) map[c.name] = c; setPmContacts(map); }
                  }).catch(() => {});
                }} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.inputBg, color: c.textDim, fontSize: 11, fontFamily: mono, cursor: "pointer" }}>REFRESH</button>
              </div>
            </div>
            <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 10, padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, lineHeight: 1.5 }}>
              📋 Roster pulled live from main Code Blue dashboard (/api/org). Click any cell to edit — changes save automatically when you tab away or click elsewhere. <strong>Microsoft Teams webhook URL</strong> goes in the last column.
            </div>

            {/* Table headers */}
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1.4fr 1.8fr 1.3fr 1.5fr", gap: 4, padding: "6px 8px", fontSize: 10, color: c.textMuted, fontFamily: mono, fontWeight: 700, letterSpacing: 1, borderBottom: `1px solid ${c.border}`, textTransform: "uppercase", position: "sticky", top: 0, background: c.surface, zIndex: 1 }}>
              <div>Name</div>
              <div>Department</div>
              <div>Email</div>
              <div>Phone</div>
              <div>Microsoft Teams Webhook</div>
            </div>

            {/* Filtered rows — inline editable */}
            <div style={{ maxHeight: 500, overflow: "auto" }}>
              {teamRoster
                .filter(p => !teamSearch.trim() || p.name.toLowerCase().includes(teamSearch.toLowerCase()) || (p.department || "").toLowerCase().includes(teamSearch.toLowerCase()))
                .map(p => {
                  const contact = pmContacts[p.name] || {};
                  const edits = teamRowEdits[p.name] || {};
                  const getVal = (field) => edits[field] !== undefined ? edits[field] : (contact[field] || "");
                  const hasAny = !!(contact.email || contact.phone || contact.teams_webhook);
                  const fullyConfigured = contact.email && contact.phone && contact.teams_webhook;
                  const isSaving = teamSavingRow === p.name;
                  const justSaved = teamSavedRow === p.name;

                  // Save a single field on blur
                  const saveField = async (field, value) => {
                    if ((contact[field] || "") === (value || "")) {
                      setTeamRowEdits(prev => { const n = { ...prev }; if (n[p.name]) { delete n[p.name][field]; if (Object.keys(n[p.name]).length === 0) delete n[p.name]; } return n; });
                      return;
                    }
                    setTeamSavingRow(p.name);
                    const newContact = {
                      email: field === "email" ? (value || null) : (contact.email || null),
                      phone: field === "phone" ? (value || null) : (contact.phone || null),
                      teams_webhook: field === "teams_webhook" ? (value || null) : (contact.teams_webhook || null),
                    };
                    try {
                      await fetch(`/api/pm-contacts/${encodeURIComponent(p.name)}`, {
                        method: "PUT", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(newContact),
                      });
                      setPmContacts(prev => ({ ...prev, [p.name]: { name: p.name, ...newContact } }));
                      setTeamRowEdits(prev => { const n = { ...prev }; delete n[p.name]; return n; });
                      setTeamSavedRow(p.name);
                      setTimeout(() => setTeamSavedRow(cur => cur === p.name ? null : cur), 1500);
                    } catch {}
                    setTeamSavingRow(null);
                  };

                  const inputStyle = {
                    width: "100%", padding: "6px 8px", background: c.inputBg,
                    border: `1px solid ${c.border}`, borderRadius: 6, color: c.text,
                    fontSize: 12, outline: "none", boxSizing: "border-box",
                  };

                  return (
                    <div key={p.name} style={{
                      display: "grid", gridTemplateColumns: "1.6fr 1.4fr 1.8fr 1.3fr 1.5fr", gap: 4,
                      padding: "6px 8px", borderBottom: `1px solid ${c.border}`, alignItems: "center",
                      background: justSaved ? c.accentDim : "transparent",
                      transition: "background 0.4s",
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ color: c.text, fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
                          {fullyConfigured && <span style={{ color: c.accent, fontSize: 10 }} title="Fully configured">●</span>}
                          {hasAny && !fullyConfigured && <span style={{ color: c.warn, fontSize: 10 }} title="Partially configured">●</span>}
                          <span>{p.name}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: 11, color: c.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.department || "—"}</div>
                      <div>
                        <input value={getVal("email")}
                          onChange={e => setTeamRowEdits(prev => ({ ...prev, [p.name]: { ...(prev[p.name] || {}), email: e.target.value } }))}
                          onBlur={e => saveField("email", e.target.value)}
                          placeholder="—" type="email"
                          style={inputStyle} />
                      </div>
                      <div>
                        <input value={getVal("phone")}
                          onChange={e => setTeamRowEdits(prev => ({ ...prev, [p.name]: { ...(prev[p.name] || {}), phone: e.target.value } }))}
                          onBlur={e => saveField("phone", e.target.value)}
                          placeholder="—" type="tel"
                          style={{ ...inputStyle, fontFamily: mono }} />
                      </div>
                      <div>
                        <input value={getVal("teams_webhook")}
                          onChange={e => setTeamRowEdits(prev => ({ ...prev, [p.name]: { ...(prev[p.name] || {}), teams_webhook: e.target.value } }))}
                          onBlur={e => saveField("teams_webhook", e.target.value)}
                          placeholder="https://...webhook.office.com/..."
                          style={{ ...inputStyle, fontFamily: mono, fontSize: 11 }} />
                      </div>
                    </div>
                  );
                })}
              {teamRoster.length === 0 && (
                <div style={{ padding: 40, textAlign: "center", color: c.textDim, fontSize: 13 }}>Loading team roster...</div>
              )}
            </div>

            {/* Stats footer */}
            <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${c.border}`, display: "flex", gap: 16 }}>
              <span>Total: {teamRoster.length}</span>
              <span>With email: {Object.values(pmContacts).filter(c => c.email).length}</span>
              <span>With phone: {Object.values(pmContacts).filter(c => c.phone).length}</span>
              <span>With Teams: {Object.values(pmContacts).filter(c => c.teams_webhook).length}</span>
            </div>
          </div>
        )}

        {/* Overrides Panel */}
        {showOverrides && (
          <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 16, marginBottom: 20, maxHeight: 500, overflow: "auto" }}>
            <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>OVERRIDE REQUESTS</div>
            {overrides.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: c.textDim, fontSize: 13 }}>No override requests</div>
            ) : overrides.map(o => (
              <div key={o.id} style={{
                padding: 14, marginBottom: 8, borderRadius: 12,
                background: o.status === "pending" ? c.failDim : o.status === "approved" ? c.accentDim : c.inputBg,
                border: `1px solid ${o.status === "pending" ? "rgba(239,68,68,0.2)" : o.status === "approved" ? c.accentBorder : c.border}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, color: c.text, fontWeight: 700 }}>{o.step_title || `Step ${o.step_id}`}</div>
                    <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono }}>{o.tech_name} • Club {o.club_number} • {o.template_title}</div>
                  </div>
                  <span style={{
                    fontSize: 10, padding: "3px 8px", borderRadius: 6, fontFamily: mono, fontWeight: 700,
                    background: o.status === "pending" ? "rgba(245,158,11,0.1)" : o.status === "approved" ? c.accentDim : c.failDim,
                    color: o.status === "pending" ? c.warn : o.status === "approved" ? c.accent : c.fail,
                  }}>{o.status.toUpperCase()}</span>
                </div>
                <div style={{ fontSize: 13, color: c.fail, marginBottom: 4 }}>
                  <strong>AI concern:</strong> {o.ai_concern || "—"}
                </div>
                <div style={{ fontSize: 13, color: c.text, marginBottom: 8 }}>
                  <strong>Tech reason:</strong> {o.tech_reason || "—"}
                </div>
                {o.photo_id && (
                  <img src={`/api/photos/${o.photo_id}`} alt="" style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 8, border: `1px solid ${c.border}`, marginBottom: 8 }} />
                )}
                {o.status === "pending" && (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleOverride(o.id, "approve")} style={{
                      flex: 1, padding: "10px 14px", borderRadius: 10, border: "none",
                      background: c.accent, color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                    }}>APPROVE</button>
                    <button onClick={() => handleOverride(o.id, "reject")} style={{
                      flex: 1, padding: "10px 14px", borderRadius: 10, border: "none",
                      background: c.fail, color: "#fff", fontSize: 13, fontWeight: 700, fontFamily: mono, cursor: "pointer",
                    }}>REJECT</button>
                  </div>
                )}
                {o.status !== "pending" && o.resolved_by && (
                  <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono }}>
                    {o.status === "approved" ? "Approved" : "Rejected"} by {o.resolved_by} • {o.resolved_at ? new Date(o.resolved_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Call Log Panel */}
        {showCalls && (
          <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 16, marginBottom: 20, maxHeight: 400, overflow: "auto" }}>
            <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>CALL LOG</div>
            {calls.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: c.textDim, fontSize: 13 }}>No calls logged yet</div>
            ) : calls.map(cl => (
              <div key={cl.id} style={{ display: "flex", gap: 10, padding: "10px 12px", marginBottom: 4, borderRadius: 10, background: c.inputBg, border: `1px solid ${c.border}` }}>
                <div style={{ fontSize: 18, flexShrink: 0 }}>{cl.direction === "pm_to_tech" ? "📲" : "📞"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: c.text, fontWeight: 600 }}>
                    {cl.direction === "pm_to_tech" ? `PM → ${cl.tech_name}` : `${cl.tech_name} → PM`}
                  </div>
                  <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                    <span>Club {cl.club_number || "—"}</span>
                    <span>{cl.step_title || "—"}</span>
                    {cl.tech_phone && <span>📱 {cl.tech_phone}</span>}
                    <span>{cl.created_at ? new Date(cl.created_at + "Z").toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  </div>
                  {cl.context_summary && <div style={{ fontSize: 11, color: c.textDim, marginTop: 3, whiteSpace: "pre-wrap" }}>{cl.context_summary}</div>}
                </div>
                {cl.tech_phone && (
                  <a href={`tel:${cl.tech_phone}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 36, height: 36, borderRadius: 10, background: c.accentDim, border: `1px solid ${c.accentBorder}`, textDecoration: "none", fontSize: 16, flexShrink: 0 }} title={`Call ${cl.tech_name}`}>📱</a>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>FILTERS</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input value={filters.tech} onChange={e => setFilters(f => ({ ...f, tech: e.target.value }))} placeholder="Tech name"
              style={{ flex: "1 1 120px", padding: "8px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none" }} />
            <input value={filters.club} onChange={e => setFilters(f => ({ ...f, club: e.target.value }))} placeholder="Club #"
              style={{ flex: "1 1 100px", padding: "8px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none" }} />
            <input value={filters.ticket || ""} onChange={e => setFilters(f => ({ ...f, ticket: e.target.value }))} placeholder="Ticket #"
              style={{ flex: "1 1 120px", padding: "8px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none", fontFamily: mono }} />
            <select value={filters.template} onChange={e => setFilters(f => ({ ...f, template: e.target.value }))}
              style={{ flex: "1 1 150px", padding: "8px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none" }}>
              <option value="">All templates</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              style={{ flex: "1 1 120px", padding: "8px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none" }}>
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
            </select>
            <input type="date" value={filters.from} onChange={e => setFilters(f => ({ ...f, from: e.target.value }))}
              style={{ flex: "1 1 130px", padding: "8px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none" }} />
            <input type="date" value={filters.to} onChange={e => setFilters(f => ({ ...f, to: e.target.value }))}
              style={{ flex: "1 1 130px", padding: "8px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 13, outline: "none" }} />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: c.textDim, fontFamily: mono, padding: "8px 12px", cursor: "pointer" }}>
              <input type="checkbox" checked={sessionsIncludeArchived} onChange={e => setSessionsIncludeArchived(e.target.checked)} />
              Show archived
            </label>
          </div>
        </div>

        {/* Active Jobs */}
        {activeSessions.length > 0 && (
          <div style={{ background: c.surface, border: `1px solid ${c.accentBorder}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: c.accent, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5 }}>ACTIVE JOBS ({activeSessions.length})</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={async () => {
                  if (!confirm(`Archive ALL ${activeSessions.length} active jobs? This hides them but keeps data intact.`)) return;
                  await Promise.all(activeSessions.map(s => fetch(`/api/admin/sessions/${s.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ archived: 1 }) })));
                  setActiveSessions([]);
                }} style={{ background: c.inputBg, border: `1px solid ${c.border}`, color: c.textDim, fontSize: 10, cursor: "pointer", fontFamily: mono, fontWeight: 700, padding: "4px 10px", borderRadius: 6 }}>CLEAR ALL</button>
                <button onClick={() => {
                  const p = new URLSearchParams({ status: "in_progress", limit: "50" });
                  if (showMineOnly && actingPm) p.set("pm_name", actingPm);
                  fetch(`/api/admin/sessions?${p}`).then(r => r.json()).then(d => setActiveSessions(d.sessions || [])).catch(() => {});
                }}
                  style={{ background: "none", border: "none", color: c.accent, fontSize: 11, cursor: "pointer", fontFamily: mono }}>REFRESH</button>
              </div>
            </div>
            {activeSessions.map(s => {
              const progress = s.results || {};
              return (
                <div key={s.id} role="button" tabIndex={0} onClick={() => loadDetail(s)} style={{
                  display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 12px", marginBottom: 4, borderRadius: 10,
                  background: c.accentDim, border: `1px solid ${c.accentBorder}`, cursor: "pointer", textAlign: "left",
                }}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: c.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#fff", fontWeight: 700, fontFamily: mono, flexShrink: 0 }}>
                    {progress.current_step || "?"}/{progress.total_steps || "?"}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: c.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.job_title || "Untitled"}</div>
                    <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono }}>
                      {s.tech_name || "—"} • Club {s.club_number || "—"}
                      {progress.step_title ? ` • ${progress.step_title}` : ""}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: c.accent, fontFamily: mono, fontWeight: 700, padding: "3px 8px", background: c.accentDim, border: `1px solid ${c.accentBorder}`, borderRadius: 6, flexShrink: 0 }}>LIVE</div>
                  <button onClick={async e => {
                    e.stopPropagation();
                    if (!confirm(`Clear this active job?\n\n${s.tech_name || "—"} • Club ${s.club_number || "—"}\n\nThe session will be archived.`)) return;
                    await fetch(`/api/admin/sessions/${s.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ archived: 1 }) });
                    setActiveSessions(prev => prev.filter(x => x.id !== s.id));
                  }} title="Clear this active job" style={{ fontSize: 12, padding: "4px 8px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.inputBg, color: c.textDim, cursor: "pointer", fontFamily: mono, fontWeight: 700, flexShrink: 0 }}>✕</button>
                </div>
              );
            })}
          </div>
        )}

        {/* Results */}
        <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, marginBottom: 10, display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input type="checkbox"
              checked={sessions.length > 0 && sessions.every(s => selectedSessionIds.has(s.id))}
              onChange={e => setSelectedSessionIds(e.target.checked ? new Set(sessions.map(s => s.id)) : new Set())} />
            <span>{total} submission{total !== 1 ? "s" : ""} {loading && "— loading..."}</span>
            <span style={{ color: c.textMuted, fontSize: 10 }}>Tip: Shift+click for range select</span>
          </div>
          <button onClick={exportSessionsCsv} disabled={exportingCsv} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.accentBorder}`, background: c.accentDim, color: c.accent, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: exportingCsv ? "wait" : "pointer" }}>{exportingCsv ? "EXPORTING…" : "📥 EXPORT CSV"}</button>
        </div>

        {/* Bulk action bar for sessions */}
        {selectedSessionIds.size > 0 && (() => {
          const bulkSessionArchive = async (archive) => {
            const ids = [...selectedSessionIds];
            if (!confirm(`${archive ? "Archive" : "Unarchive"} ${ids.length} session(s)?`)) return;
            await Promise.all(ids.map(id => fetch(`/api/admin/sessions/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ archived: archive ? 1 : 0 }) })));
            setSelectedSessionIds(new Set());
            loadSessions(page);
          };
          const bulkSessionDelete = async () => {
            const ids = [...selectedSessionIds];
            const ok = confirm(
              `⚠️ PERMANENTLY DELETE ${ids.length} session(s)?\n\n` +
              `This cannot be undone. The session record, every photo (D1 + KV blob), and all linked data will be removed forever.\n\n` +
              `If you only want to hide them, click Cancel and use ARCHIVE instead.`
            );
            if (!ok) return;
            const results = await Promise.all(ids.map(id =>
              fetch(`/api/admin/sessions/${id}`, { method: "DELETE" })
                .then(r => r.json().then(d => ({ id, ok: r.ok, ...d })))
                .catch(e => ({ id, ok: false, error: e.message }))
            ));
            const failed = results.filter(r => !r.ok);
            if (failed.length > 0) {
              alert(`${results.length - failed.length} deleted, ${failed.length} failed:\n\n${failed.map(f => `${f.id}: ${f.error || "unknown error"}`).join("\n")}`);
            }
            setSelectedSessionIds(new Set());
            loadSessions(page);
          };
          return (
            <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 12px", marginBottom: 10, borderRadius: 10, background: c.accentDim, border: `1px solid ${c.accentBorder}` }}>
              <div style={{ fontSize: 12, color: c.accent, fontFamily: mono, fontWeight: 700, flex: 1 }}>{selectedSessionIds.size} selected</div>
              <button onClick={() => bulkSessionArchive(true)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.inputBg, color: c.text, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer" }}>ARCHIVE</button>
              <button onClick={() => bulkSessionArchive(false)} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.inputBg, color: c.text, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer" }}>UNARCHIVE</button>
              <button onClick={bulkSessionDelete} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.fail}`, background: c.failDim, color: c.fail, fontSize: 11, fontWeight: 700, fontFamily: mono, cursor: "pointer" }}>🗑 DELETE</button>
              <button onClick={() => setSelectedSessionIds(new Set())} style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.inputBg, color: c.textDim, fontSize: 11, fontFamily: mono, cursor: "pointer" }}>CLEAR</button>
            </div>
          );
        })()}

        {sessions.map((s, idx) => (
          <div key={s.id} role="button" tabIndex={0} onClick={() => loadDetail(s)} style={{
            display: "flex", alignItems: "center", gap: 14, width: "100%", padding: "14px 16px",
            background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, cursor: "pointer",
            marginBottom: 8, textAlign: "left", opacity: s.archived ? 0.55 : 1,
            boxShadow: selectedSessionIds.has(s.id) ? `0 0 0 2px ${c.accent} inset` : "none",
          }}>
            <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
              <input type="checkbox" checked={selectedSessionIds.has(s.id)}
                onClick={e => {
                  e.stopPropagation();
                  const shift = e.shiftKey;
                  setSelectedSessionIds(prev => {
                    const next = new Set(prev);
                    if (shift && lastSessionIdx !== null) {
                      const [a, b] = [Math.min(lastSessionIdx, idx), Math.max(lastSessionIdx, idx)];
                      const shouldAdd = !prev.has(s.id);
                      for (let i = a; i <= b; i++) {
                        const row = sessions[i];
                        if (row) { if (shouldAdd) next.add(row.id); else next.delete(row.id); }
                      }
                    } else {
                      if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                    }
                    return next;
                  });
                  setLastSessionIdx(idx);
                }}
                onChange={() => {}} />
            </div>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: s.status === "completed" ? c.accentDim : c.inputBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{s.status === "completed" ? "✅" : "⏳"}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, color: c.text, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.ticket_number ? `[${s.ticket_number}] ` : ""}{s.job_title || "Untitled"}</div>
              <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span>{s.tech_name || "—"}</span>
                <span>Club {s.club_number || "—"}</span>
                {s.pm_name && <span style={{ color: c.accent }}>PM: {s.pm_name}</span>}
                <span>{fmtDate(s.completed_at)}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
              {s.photo_count > 0 && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: "rgba(99,102,241,0.1)", color: "rgba(99,102,241,0.8)", fontFamily: mono }}>📷 {s.photo_count}</span>}
              {s.archived && <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 6, background: "rgba(148,163,184,0.15)", color: c.textDim, fontFamily: mono, fontWeight: 700 }}>ARCHIVED</span>}
              <button onClick={e => {
                e.stopPropagation();
                const next = s.archived ? 0 : 1;
                if (!s.archived && !confirm("Archive this session? It will be hidden from default views.")) return;
                fetch(`/api/admin/sessions/${s.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ archived: next }) })
                  .then(r => r.ok ? loadSessions(page) : r.json().then(d => alert(d.error || "Failed")));
              }} style={{ fontSize: 10, padding: "4px 8px", borderRadius: 6, border: `1px solid ${c.border}`, background: c.inputBg, color: c.textDim, cursor: "pointer", fontFamily: mono, fontWeight: 700 }}>
                {s.archived ? "UNARCHIVE" : "ARCHIVE"}
              </button>
              <span style={{ fontSize: 18, color: c.textMuted }}>→</span>
            </div>
          </div>
        ))}

        {sessions.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: 40, color: c.textDim }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
            {showMineOnly && actingPm ? (
              <>
                <div style={{ fontSize: 15, marginBottom: 8 }}>No jobs tagged with "{actingPm}"</div>
                <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 12 }}>Older sessions were created before PM tagging existed. New sessions you create will appear here.</div>
                <button onClick={() => setShowMineOnly(false)} style={{
                  padding: "8px 16px", borderRadius: 8, border: `1px solid ${c.accentBorder}`,
                  background: c.accentDim, color: c.accent, fontSize: 13, fontWeight: 600, cursor: "pointer",
                }}>Show all jobs instead</button>
              </>
            ) : (
              <div style={{ fontSize: 15 }}>No submissions match your filters</div>
            )}
          </div>
        )}

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 20 }}>
            <button disabled={page === 0} onClick={() => loadSessions(page - 1)}
              style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.inputBg, color: page === 0 ? c.textMuted : c.text, cursor: page === 0 ? "not-allowed" : "pointer", fontSize: 13 }}>← Previous</button>
            <span style={{ fontSize: 13, color: c.textDim, fontFamily: mono, alignSelf: "center" }}>Page {page + 1} of {Math.ceil(total / PAGE_SIZE)}</span>
            <button disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => loadSessions(page + 1)}
              style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.inputBg, color: (page + 1) * PAGE_SIZE >= total ? c.textMuted : c.text, cursor: (page + 1) * PAGE_SIZE >= total ? "not-allowed" : "pointer", fontSize: 13 }}>Next →</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN APP ───────────────────────────────────────────────────
export default function CBOPS() {
  const [mode, setMode] = useState(() => {
    try { return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark"; } catch { return "dark"; }
  });
  const c = themes[mode];
  const [screen, setScreen] = useState(() => {
    // Check URL for /job/:id
    const path = window.location.pathname;
    if (path.startsWith("/job/")) return "loading";
    if (path.startsWith("/admin")) return "admin";
    return "home";
  });
  const [jobLink, setJobLink] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [techName, setTechName] = useState("");
  const [club, setClub] = useState("");
  const [techPhone, setTechPhone] = useState("");
  const [ticketNumber, setTicketNumber] = useState("");
  const [messages, setMessages] = useState([]);
  const [stepIdx, setStepIdx] = useState(0);
  const [cfs, setCfs] = useState({});
  const [sRes, setSRes] = useState({});
  const [thinking, setThinking] = useState(false);
  const [input, setInput] = useState("");
  const [auditLog, setAuditLog] = useState([]);
  const [history, setHistory] = useState([]);
  const [inventoryData, setInventoryData] = useState({}); // keyed by step id → array of items
  const [photoIds, setPhotoIds] = useState([]); // track uploaded photo IDs for session
  const [listening, setListening] = useState(false);
  const [handsFree, setHandsFree] = useState(false);
  const [pendingOverride, setPendingOverride] = useState(null); // { id, status } when waiting
  const [overrideReason, setOverrideReason] = useState("");
  const [showOverrideForm, setShowOverrideForm] = useState(false); // show reason input
  const [saving, setSaving] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [actingPm, setActingPm] = useState(() => {
    try { return localStorage.getItem("cb_ops_acting_pm") || ""; } catch { return ""; }
  });
  const [showPmPicker, setShowPmPicker] = useState(false);
  const [pmPickerSearch, setPmPickerSearch] = useState("");
  const [pmContactEdit, setPmContactEdit] = useState(null); // { name, email, phone, teams_webhook }
  const [roster, setRoster] = useState([]);
  const [assignedPm, setAssignedPm] = useState(""); // the PM who OWNS this specific session (from URL or saved)
  const [pmPhone, setPmPhone] = useState(null);
  const [showCallIntent, setShowCallIntent] = useState(false);
  const [callIntent, setCallIntent] = useState("");
  const [pendingCallLog, setPendingCallLog] = useState(null); // { id, startedAt } — set when call is in progress
  const [showCallNotes, setShowCallNotes] = useState(false);
  const [callNotesDraft, setCallNotesDraft] = useState("");
  const [callNotesListening, setCallNotesListening] = useState(false);
  const [pmName, setPmName] = useState("Tink");
  const [sessionStart] = useState(() => new Date().toISOString());
  const [completions, setCompletions] = useState([]);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const endRef = useRef(null);
  const recognitionRef = useRef(null);
  const cfsRef = useRef(cfs); cfsRef.current = cfs;
  const siRef = useRef(stepIdx); siRef.current = stepIdx;
  const srRef = useRef(sRes); srRef.current = sRes;
  const invRef = useRef(inventoryData); invRef.current = inventoryData;

  const steps = activeTemplate?.steps || [];
  const step = steps[stepIdx] || null;
  const scroll = useCallback(() => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80), []);

  // ─── Speech-to-Text ────────────────────────────────────────
  const SpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const hasSpeech = !!SpeechRecognition;

  const toggleListening = useCallback(() => {
    if (!SpeechRecognition) return;
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const rec = new SpeechRecognition();
    rec.continuous = false;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      const text = e.results[0]?.[0]?.transcript || "";
      if (text) setInput(prev => prev ? prev + " " + text : text);
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  }, [listening, SpeechRecognition]);

  // ─── Text-to-Speech ────────────────────────────────────────
  const speak = useCallback((text) => {
    if (!handsFree || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const clean = text.replace(/\*\*/g, "").replace(/\|\|\|.*?\|\|\|/g, "");
    const utt = new SpeechSynthesisUtterance(clean);
    utt.rate = 1.1;
    utt.pitch = 1;
    window.speechSynthesis.speak(utt);
  }, [handsFree]);

  // ─── PM Notifications ─────────────────────────────────────
  const sendNotification = useCallback(async (type, message, extra = {}) => {
    try {
      await fetchWithRetry("/api/notifications", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type, message,
          tech_name: techName, club_number: club,
          template_id: activeTemplate?.id, template_title: activeTemplate?.title,
          step_id: step?.id, step_title: step?.title,
          pm_name: assignedPm || actingPm || activeTemplate?.created_by_pm || null,
          ...extra,
        }),
      });
    } catch {}
  }, [techName, club, activeTemplate, step, assignedPm]);

  const addMsg = useCallback((from, content, extra = {}) => {
    const msg = { from, content, time: now(), id: Date.now() + Math.random(), ...extra };
    setMessages(prev => [...prev, msg]);
    setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    if (from === "bot" && content && typeof content === "string") speak(content);
    return msg;
  }, [speak]);

  // Autosave session to localStorage
  const saveKey = activeTemplate?.id ? `cbops_session_${activeTemplate.id}` : null;
  const progressTimerRef = useRef(null);
  useEffect(() => {
    if (!saveKey || screen !== "chat") return;
    // Stop saving progress once the session is completed — prevents ghost sessions
    if (sessionCompleted) return;
    const data = { activeTemplate, techName, club, techPhone, ticketNumber, stepIdx, cfs, sRes, inventoryData, messages: messages.slice(-100), history: history.slice(-30), screen, savedAt: Date.now() };
    try { localStorage.setItem(saveKey, JSON.stringify(data)); } catch {}

    // Debounced save to D1 — saves FULL state so tech can resume from any device
    if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
    progressTimerRef.current = setTimeout(() => {
      if (!activeTemplate?.id || !techName || sessionCompleted) return;
      const fullState = {
        current_step: stepIdx + 1, total_steps: steps.length, step_title: steps[stepIdx]?.title || "",
        step_idx: stepIdx,
        cfs: cfsRef.current,
        sRes: Object.fromEntries(Object.entries(srRef.current).map(([k, v]) => [k, { pass: v.pass, message: v.message }])), // strip photos
        inventory: invRef.current,
        tech_phone: techPhone || null,
        messages: messages.slice(-50).map(m => ({ from: m.from, content: m.content, time: m.time, status: m.status, hasPhoto: !!m.photo })),
        history: history.slice(-20),
        confirmations_count: Object.keys(cfsRef.current).length,
        inventory_items: Object.values(invRef.current).reduce((a, b) => a + b.length, 0),
      };
      fetch(`/api/jobs/${activeTemplate.id}/progress`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tech_name: techName, club_number: club, results: fullState, started_at: sessionStart,
          photo_ids: photoIds.map(p => p.id),
          pm_name: assignedPm || actingPm || activeTemplate?.created_by_pm || null,
          ticket_number: ticketNumber || null,
        }),
      }).then(r => r.json()).then(d => {
        if (d.session_id) setCurrentSessionId(d.session_id);
      }).catch(() => {});
    }, 3000); // Save every 3s after last change
  }, [saveKey, screen, stepIdx, cfs, sRes, inventoryData, messages, history, techName, club, ticketNumber, activeTemplate, steps, sessionStart, photoIds, sessionCompleted, assignedPm]);

  // Poll for live template updates — PM can edit the checklist while tech is working
  const templateHashRef = useRef(null);
  useEffect(() => {
    if (screen !== "chat" || !activeTemplate?.id) return;
    // Normalize a step to only meaningful fields for comparison
    const normalize = (steps) => JSON.stringify((steps || []).map(s => ({
      id: s.id, title: s.title, type: s.type, instruction: s.instruction,
      confirms: s.confirms || null,
      requiresPhoto: !!s.requiresPhoto, photoLabel: s.photoLabel || null,
      aiCheck: !!s.aiCheck, aiPrompt: s.aiPrompt || null,
      hardGate: !!s.hardGate, expectedIP: s.expectedIP || null,
    })));
    // Initialize hash on first run with current state
    if (templateHashRef.current === null) {
      templateHashRef.current = normalize(activeTemplate.steps);
    }
    const iv = setInterval(async () => {
      try {
        const res = await fetch(`/api/jobs/${activeTemplate.id}`);
        const data = await res.json();
        if (!data || data.error || !data.steps) return;
        const newHash = normalize(data.steps);
        if (templateHashRef.current !== newHash) {
          templateHashRef.current = newHash;
          setActiveTemplate(prev => ({ ...prev, steps: data.steps, title: data.title, description: data.description }));
          addMsg("bot", "📝 **Checklist updated** — PM made changes to the checklist.", { status: "pass" });
        }
      } catch {}
    }, 15000); // Poll every 15s (was 10s — reduces noise)
    return () => clearInterval(iv);
  }, [screen, activeTemplate?.id]);

  // Restore session on mount if navigating to a job URL (localStorage fallback)
  // Primary resume path is D1 via startChat. This handles same-device offline cases.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    const path = window.location.pathname;
    if (!path.startsWith("/job/")) return;
    const jobId = path.split("/job/")[1]?.split("/")[0];
    if (!jobId) return;
    const key = `cbops_session_${jobId}`;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const saved = JSON.parse(raw);
      // 7 days instead of 24h
      if (Date.now() - saved.savedAt > 604800000 || saved.screen !== "chat") return;
      setActiveTemplate(saved.activeTemplate);
      setTechName(saved.techName);
      setClub(saved.club);
      if (saved.techPhone) setTechPhone(saved.techPhone);
      if (saved.ticketNumber) setTicketNumber(saved.ticketNumber);
      setStepIdx(saved.stepIdx); siRef.current = saved.stepIdx;
      setCfs(saved.cfs); cfsRef.current = saved.cfs;
      setSRes(saved.sRes); srRef.current = saved.sRes;
      if (saved.inventoryData) { setInventoryData(saved.inventoryData); invRef.current = saved.inventoryData; }
      setMessages(saved.messages || []);
      setHistory(saved.history || []);
      setScreen("chat");
    } catch {}
  }, []);

  // Load job from URL on mount
  useState(() => {
    const path = window.location.pathname;
    if (path.startsWith("/job/")) {
      const jobId = path.split("/job/")[1]?.split("/")[0];
      if (jobId) {
        fetchWithRetry(`/api/jobs/${jobId}`, {}).then(r => r.json()).then(data => {
          if (data.error) { setScreen("home"); return; }
          const template = { title: data.title, description: data.description, steps: data.steps, id: data.id, ticket_number: data.ticket_number || "" };
          setActiveTemplate(template);
          setTicketNumber(template.ticket_number || "");
          setTemplates(prev => [...prev, template]);
          setScreen("start");
        }).catch(() => setScreen("home"));
      }
    }
  });

  const buildSys = useCallback(() => {
    const s = steps[siRef.current];
    if (!s) return "All steps complete. You are CB-OPS — a Bostonian through and through. Congratulate the tech in your thick Boston accent. Wicked proud of 'em.";
    const done = (s.confirms || []).filter(x => cfsRef.current[`${s.id}_${x}`]);
    const pend = (s.confirms || []).filter(x => !cfsRef.current[`${s.id}_${x}`]);
    const photoOk = !s.requiresPhoto || !!cfsRef.current[`${s.id}_photo`];

    let inventoryContext = "";
    if (s.type === "inventory") {
      const items = invRef.current[s.id] || [];
      inventoryContext = `\nINVENTORY STEP: The tech needs to list items. Current count: ${items.length} items captured.
Fields per item: ${JSON.stringify(s.inventoryFields)}
Min items: ${s.minItems || 1}
Current items: ${JSON.stringify(items)}
The tech can use the form below OR describe items conversationally. When the tech mentions items in chat, extract them and output:
|||INVENTORY:${JSON.stringify([Object.fromEntries(s.inventoryFields.map(f => [f.key, "example"]))])}|||
Each item must be a JSON object with the exact field keys: ${s.inventoryFields.map(f => f.key).join(", ")}.
You can extract multiple items at once as an array. Fill in values from context if the tech gives partial info — ask only if truly ambiguous.
When the tech says they're done listing, acknowledge the inventory.`;
    }
    if (s.type === "verify_inventory") {
      const refStep = steps.find(st => st.id === s.referencesStep);
      const items = invRef.current[s.referencesStep] || [];
      inventoryContext = `\nVERIFICATION STEP: Verifying ${items.length} items from "${refStep?.title || "inventory"}".
Items to verify: ${JSON.stringify(items)}
Checks per item: ${JSON.stringify(s.confirms_per_item)}
The tech will use checkboxes below to confirm each item. Guide them through verification. ALL items must be fully verified.`;
    }

    // Include all inventory data for context
    let allInventory = "";
    const invKeys = Object.keys(invRef.current);
    if (invKeys.length > 0) {
      allInventory = "\nCAPTURED INVENTORIES:";
      for (const key of invKeys) {
        const refStep = steps.find(st => st.id === parseInt(key));
        allInventory += `\n- ${refStep?.title || `Step ${key}`}: ${invRef.current[key].length} items`;
      }
    }

    return `You are CB-OPS, a helpful assistant chat for Code Blue Technology Operations field techs.

YOUR ROLE: You are a HELP CHAT ONLY. You answer questions, explain steps, troubleshoot issues, and coach the tech. You CANNOT advance the checklist — that is controlled entirely by the tech tapping checkboxes, photo uploads, and the Mark Step Complete button.

JOB: ${activeTemplate?.title || "Project Closeout"}
SESSION: Tech: ${techName} | Club: ${club} | ${new Date().toLocaleDateString()}
PM: ${assignedPm || actingPm || activeTemplate?.created_by_pm || "unknown"} (this is the Project Manager assigned to this job — use their name when referring to the PM)

STEP ${s.id}/${steps.length}: ${s.title}
Type: ${s.type}
Instructions: ${s.instruction}
${s.requiresPhoto ? `📷 Photo required on this step — ${photoOk ? "already uploaded" : "tech must tap the UPLOAD PHOTO button"}` : ""}
${s.hardGate ? `\n🚫 HARD GATE — ${s.expectedIP ? `IP must match ${s.expectedIP}` : "AI verification required"}` : ""}
${s.aiCheck ? `🤖 AI verification required on photo: "${s.aiPrompt || "verify the work"}"` : ""}
${s.confirms ? `Checklist items: Done: ${done.join(", ") || "None"} | Pending: ${pend.join(", ") || "None"}` : ""}
${inventoryContext}${allInventory}

STRICT RULES:
1. You have NO ability to mark items complete, check boxes, or advance steps. None. Those are UI-only actions.
2. Do NOT output |||CONFIRM:...||| tokens — they will be stripped and ignored. The form controls progression, not you.
3. Do NOT say "step complete", "moving to the next step", "all done", or anything implying you advanced the form. You did not.
4. When the tech says "done" or "yes" — acknowledge what they did, explain anything they're confused about, and remind them to tap the checkboxes and Mark Step Complete button.
5. If they ask about progression, tell them: "Tap each checklist item to mark it done, then tap Mark Step Complete at the bottom."
6. Your job is to HELP with the work, not control the form.

AUTO-NOTIFY THE PM:
When the tech encounters something the Project Manager needs to know about — an unexpected camera system, a wrong IP, equipment missing, a blocker, a decision that needs their input — DO NOT tell the tech to "let the PM know". Instead, YOU push a notification to the PM directly by emitting this token anywhere in your response:

|||NOTIFY:severity|short message to PM|||

Severity must be one of: "info", "medium", "high"
- "high" for blockers, wrong equipment, urgent decisions needed
- "medium" for flags, surprises, missing info
- "info" for FYI updates, successful unusual cases

Example: You realize the cameras are Walmart Camera 3000 instead of Eagle Eye. Emit:
|||NOTIFY:high|Tech onsite found Walmart Camera 3000 system (not Eagle Eye). Needs manual IP configuration. Decision needed.|||

The PM will receive a push notification immediately. Tell the tech "I've already notified your PM — they'll reach out." Do NOT tell the tech to contact the PM themselves. You handle it.

Use NOTIFY whenever the tech would otherwise need to pause and call/text the PM.

SUGGEST ACTIONS (guided walkthrough mode):
When the tech asks you to walk them through a step, OR when they describe completing work, you can propose actions they can apply with a single tap. Emit suggestion tokens inline in your response:

|||SUGGEST:confirm|Exact Confirm Label|||
  → Proposes marking a checklist item done. Label must EXACTLY match one of the confirm labels for the current step.
  → Renders as a green "Tap to check: X" button in chat.

|||SUGGEST:inventory|{"device_name":"Cisco 2960","role":"POS Switch"}|||
  → Proposes adding an item to the inventory form (only on inventory steps). Use the exact field keys from the step's inventoryFields.
  → Renders as a "Tap to add to inventory" button.

The tech must tap the suggestion button to apply it. You are NOT checking anything off — you are suggesting. The tech stays in control.

When to use SUGGEST:
- Tech says "cable is run" → emit |||SUGGEST:confirm|Cable management complete||| so they can apply with one tap
- Tech lists devices they found → emit one |||SUGGEST:inventory|{...}||| per device
- During guided walkthrough — propose each item as the tech confirms work is done

When NOT to use SUGGEST:
- Don't auto-suggest everything at once. Ask the tech what they've done first, then suggest based on their actual answers.
- Don't suggest items the tech hasn't verbally confirmed.
- Never suggest a confirm label that isn't in the step's confirm list.

GUIDED WALKTHROUGH:
When the tech says "guide me through this step" or taps the guide button, walk them through interactively:
1. Briefly summarize what the step needs
2. Ask the first action: "Have you done X yet?"
3. Based on their answer, either troubleshoot OR emit a SUGGEST token for that item
4. Move to the next item one at a time
5. When all items are addressed, remind them to tap "Mark Step Complete"

WHAT YOU SHOULD DO:
- Answer technical questions ("what IP should the printer be on?")
- Troubleshoot problems ("the switch won't power on, what do I do?")
- Explain unclear instructions
- Coach them through tricky parts
- Celebrate real progress warmly
- Remind them to check boxes and hit the advance button

TONE & PERSONALITY: You're a born-and-raised Bostonian. Thick Boston accent in your writing — drop your R's ("cah", "pahk", "wicked"), use "wicked" as an intensifier ("wicked good", "wicked smaht"), say "no worrehs" instead of "no worries", throw in "kid" or "bud" or "pal" casually. You're direct, no-nonsense, but got a big heart under the tough exterior. Think local guy at the pub who also happens to be a network engineering genius. Sarcastic but supportive — you bust chops but you'd also take a bullet for the tech. When something goes right, it's "wicked awesome." When something's wrong, "ah ya kiddin me?" Keep it fun but never let the accent get in the way of clear technical instructions. You still know your stuff cold.

KNOWLEDGE: You know Planet Fitness infrastructure — SonicWall, Logically (866-735-4381), VLANs (200 general, 226 Genetec cameras), Rockbot, Radiance, typical IP 192.168.200.x range.`;
  }, [activeTemplate, techName, club, steps, assignedPm, actingPm]);

  const checkDone = useCallback((curCfs) => {
    const s = steps[siRef.current]; if (!s) return;

    // Inventory step: done when items captured and marked complete
    if (s.type === "inventory") {
      const items = invRef.current[s.id] || [];
      if (items.length < (s.minItems || 1)) return;
      if (!curCfs[`${s.id}_inventory_complete`]) return;
    }
    // Verify_inventory step: all items must have all confirms checked
    else if (s.type === "verify_inventory") {
      const items = invRef.current[s.referencesStep] || [];
      if (items.length > 0) {
        // Has inventory items — require per-item verification
        const allVerified = items.every((_, idx) =>
          (s.confirms_per_item || []).every(label => curCfs[`${s.id}_item${idx}_${label}`])
        );
        if (!allVerified) return;
      }
      // If no inventory items but step has regular confirms, fall through to check those
      const allCf = (s.confirms || []).length === 0 || (s.confirms || []).every(x => curCfs[`${s.id}_${x}`]);
      const photoOk = !s.requiresPhoto || curCfs[`${s.id}_photo`];
      if (!allCf || !photoOk) return;
    }
    // Standard steps
    else {
      const allCf = (s.confirms || []).every(x => curCfs[`${s.id}_${x}`]);
      const photoOk = !s.requiresPhoto || curCfs[`${s.id}_photo`];
      // Hard gate: require that AI analysis ran AND passed (only if aiCheck is also enabled)
      if (s.hardGate && s.aiCheck && (!srRef.current[s.id] || !srRef.current[s.id].pass)) return;
      if (!allCf || !photoOk) return;
    }

    {
      if (siRef.current < steps.length - 1) {
        setTimeout(() => {
          const next = siRef.current + 1;
          addMsg("bot", `**Step ${s.id}/${steps.length} complete** ✓`, { status: "pass" });
          setStepIdx(next); siRef.current = next;
          // Notify PM of step completion for critical steps (photo, hardgate, aiCheck)
          if (s.requiresPhoto || s.hardGate || s.aiCheck) {
            sendNotification("step_complete", `${techName} completed step ${s.id}/${steps.length}: ${s.title}`, {
              severity: "info", step_index: siRef.current, club_number: club,
            });
          }
          setTimeout(() => {
            const ns = steps[next];
            const refNote = ns.referenceImages?.length ? `\n\n📐 *${ns.referenceImages.length} reference image${ns.referenceImages.length !== 1 ? "s" : ""} from SOW — see below*` : "";
            addMsg("bot", `**${ns.title}**\n\n${ns.instruction}${refNote}`); scroll();
          }, 700);
        }, 500);
      } else {
        setTimeout(() => {
          const results = {
            steps: steps.map(st => ({ id: st.id, title: st.title, pass: srRef.current[st.id]?.pass !== false, ai_result: srRef.current[st.id] ? { pass: srRef.current[st.id].pass, message: srRef.current[st.id].message } : null })),
            inventory: invRef.current,
            confirmations: cfsRef.current,
            chat_history: messages.map(m => ({ from: m.from, content: m.content, time: m.time, status: m.status, photo: m.photo ? "[photo]" : undefined })),
            tech_phone: techPhone || null,
          };
          // Save to D1
          if (activeTemplate?.id) {
            fetch(`/api/jobs/${activeTemplate.id}/complete`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ tech_name: techName, club_number: club, results, started_at: sessionStart, photo_ids: photoIds.map(p => p.id), pm_name: assignedPm || actingPm || activeTemplate?.created_by_pm || null, ticket_number: ticketNumber || null }),
            }).then(r => r.json()).then(d => {
              if (d.error) console.error("Save failed:", d.error);
              else loadCompletions();
            }).catch(e => {
              console.error("Save error:", e);
              addMsg("bot", "Warning: completion may not have saved. Check your connection.", { status: "fail" });
            });
          }
          setAuditLog(prev => [...prev, { tech: techName, club, template: activeTemplate?.title, date: new Date().toLocaleString(), ...results }]);
          addMsg("bot", `🎉 **ALL ${steps.length} STEPS VERIFIED**\n\nJob complete for **${club}**\nTech: **${techName}**\nTemplate: **${activeTemplate?.title}**\n\nAudit record saved. You're clear to leave site.`, { status: "pass" });
          sendNotification("job_complete", `Job completed: ${activeTemplate?.title}`, { severity: "info" });
          setSessionCompleted(true); // stop auto-saves from creating ghost sessions
          if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
          // Clear autosaved session on completion
          if (activeTemplate?.id) try { localStorage.removeItem(`cbops_session_${activeTemplate.id}`); } catch {}
        }, 600);
      }
    }
  }, [steps, techName, club, activeTemplate, addMsg, scroll]);

  // Manual save — techs can save progress (in_progress) or force-complete at any time
  const manualSave = useCallback(async (forceComplete = false) => {
    if (!activeTemplate?.id || saving) return;
    setSaving(true);
    // Full state — same shape as auto-save so resume works either way
    const fullState = {
      step_idx: stepIdx,
      current_step: stepIdx + 1, total_steps: steps.length,
      step_title: steps[stepIdx]?.title || "",
      steps: steps.map(st => ({ id: st.id, title: st.title, pass: srRef.current[st.id]?.pass !== false })),
      cfs: cfsRef.current,
      sRes: Object.fromEntries(Object.entries(srRef.current).map(([k, v]) => [k, { pass: v.pass, message: v.message }])),
      inventory: invRef.current,
      confirmations: cfsRef.current,
      chat_history: messages.map(m => ({ from: m.from, content: m.content, time: m.time, status: m.status, photo: m.photo ? "[photo]" : undefined })),
      messages: messages.slice(-50).map(m => ({ from: m.from, content: m.content, time: m.time, status: m.status, hasPhoto: !!m.photo })),
      history: history.slice(-20),
      tech_phone: techPhone || null,
    };
    try {
      // forceComplete goes to /complete (marks as completed)
      // regular save goes to /progress (keeps as in_progress)
      const endpoint = forceComplete ? "complete" : "progress";
      const res = await fetch(`/api/jobs/${activeTemplate.id}/${endpoint}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tech_name: techName, club_number: club, results: fullState, started_at: sessionStart, photo_ids: photoIds.map(p => p.id), pm_name: assignedPm || actingPm || activeTemplate?.created_by_pm || null }),
      });
      const d = await res.json();
      if (d.session_id) {
        setCurrentSessionId(d.session_id);
        addMsg("bot", forceComplete ? "✅ **Job saved and completed.** You're clear to leave site." : `💾 **Progress saved** (step ${stepIdx + 1}/${steps.length})`, { status: "pass" });
        if (forceComplete) {
          sendNotification("job_complete", `Job completed: ${activeTemplate?.title}`, { severity: "info" });
          setSessionCompleted(true);
          if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
          if (activeTemplate?.id) try { localStorage.removeItem(`cbops_session_${activeTemplate.id}`); } catch {}
        }
      } else {
        addMsg("bot", `Save failed: ${d.error || "unknown error"}`, { status: "fail" });
      }
    } catch (e) {
      addMsg("bot", `Save error: ${e.message}. Check your connection.`, { status: "fail" });
    }
    setSaving(false);
  }, [activeTemplate, steps, stepIdx, messages, history, techName, techPhone, club, sessionStart, photoIds, saving, addMsg, sendNotification]);

  const buildCallContext = useCallback(() => {
    const completedSteps = steps.slice(0, stepIdx).map(s => s.title);
    const flags = Object.entries(sRes).filter(([, v]) => v && !v.pass).map(([k, v]) => `Step ${k}: ${v.message || "flagged"}`);
    return `Job: ${activeTemplate?.title || "?"} | Club: ${club} | Tech: ${techName}${techPhone ? ` (${techPhone})` : ""} | Step ${stepIdx + 1}/${steps.length}: ${step?.title || "?"} | Completed: ${completedSteps.join(", ") || "None"} | Flags: ${flags.join("; ") || "None"}`;
  }, [activeTemplate, club, techName, techPhone, stepIdx, steps, step, sRes]);

  // Phase 1: tap Call Tink → open intent modal to capture what they want to discuss
  const callPM = useCallback(() => {
    setCallIntent("");
    setShowCallIntent(true);
  }, []);

  // Phase 2: confirm intent → log call, send notification, dial the phone
  const placeCall = useCallback(async (intent) => {
    setShowCallIntent(false);
    const context = buildCallContext();
    const intentText = intent?.trim() || "";

    // Send context notification to PM (includes intent if provided)
    sendNotification("call_pm",
      `Tech ${techName} calling PM — Step ${stepIdx + 1}/${steps.length}: ${step?.title || ""}${intentText ? ` — "${intentText}"` : ""}`,
      {
        flags: Object.entries(sRes).filter(([, v]) => v && !v.pass).map(([k, v]) => `Step ${k}: ${v.message || "flagged"}`),
        step_index: stepIdx, club_number: club, tech_phone: techPhone, context_summary: context,
        intent: intentText,
        severity: "high",
      }
    );

    // Log the call and remember the ID for post-call notes
    try {
      const res = await fetchWithRetry("/api/calls", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tech_name: techName, club_number: club, template_id: activeTemplate?.id,
          step_id: step?.id, step_title: step?.title,
          direction: "tech_to_pm", tech_phone: techPhone,
          context_summary: context, intent: intentText,
        }),
      });
      const data = await res.json();
      if (data.id) {
        setPendingCallLog({ id: data.id, startedAt: Date.now() });
      }
    } catch {}

    addMsg("bot", `📞 **Calling ${pmName}...** ${intentText ? `I've let them know you want to discuss: "${intentText}". ` : ""}Context has been sent. We'll ask for a quick recap when you come back.`, { status: "pass" });
    if (pmPhone) window.location.href = `tel:${pmPhone}`;
  }, [techName, club, techPhone, sRes, stepIdx, step, steps, activeTemplate, sendNotification, addMsg, buildCallContext, pmPhone, pmName]);

  // When the user returns from the phone app (tab becomes visible again),
  // prompt them to log what was discussed if a call is pending.
  useEffect(() => {
    const handleVis = () => {
      if (document.visibilityState !== "visible") return;
      if (!pendingCallLog) return;
      // Only prompt if the call was started within the last 30 minutes
      if (Date.now() - pendingCallLog.startedAt > 30 * 60 * 1000) {
        setPendingCallLog(null);
        return;
      }
      // Require the tab to have been hidden for at least 15 seconds (actual call)
      // Otherwise the tab just lost focus briefly — don't prompt
      setShowCallNotes(true);
    };
    document.addEventListener("visibilitychange", handleVis);
    return () => document.removeEventListener("visibilitychange", handleVis);
  }, [pendingCallLog]);

  // Save the post-call notes to the call_log entry
  const saveCallNotes = useCallback(async () => {
    if (!pendingCallLog?.id) return;
    const notes = callNotesDraft.trim();
    const duration = Math.round((Date.now() - pendingCallLog.startedAt) / 1000);
    try {
      await fetch(`/api/calls/${pendingCallLog.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ discussion_notes: notes || null, duration_seconds: duration }),
      });
      if (notes) {
        addMsg("user", `📞 Call notes: ${notes}`);
      }
    } catch {}
    setPendingCallLog(null);
    setShowCallNotes(false);
    setCallNotesDraft("");
  }, [pendingCallLog, callNotesDraft, addMsg]);

  // Voice input for call notes — uses the existing Web Speech API setup
  const toggleCallNotesListening = useCallback(() => {
    if (!SpeechRecognition) return;
    if (callNotesListening) {
      recognitionRef.current?.stop();
      setCallNotesListening(false);
      return;
    }
    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = "en-US";
    rec.onresult = (e) => {
      let transcript = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        transcript += e.results[i][0].transcript + " ";
      }
      setCallNotesDraft(prev => (prev ? prev + " " : "") + transcript.trim());
    };
    rec.onerror = () => setCallNotesListening(false);
    rec.onend = () => setCallNotesListening(false);
    recognitionRef.current = rec;
    rec.start();
    setCallNotesListening(true);
  }, [callNotesListening, SpeechRecognition]);

  // ─── Override Request ──────────────────────────────────────
  const requestOverride = useCallback(async (reason) => {
    if (!reason.trim() || !step) return;
    const aiResult = sRes[step.id];
    try {
      const res = await fetchWithRetry("/api/overrides", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          template_id: activeTemplate?.id, template_title: activeTemplate?.title,
          tech_name: techName, club_number: club,
          step_id: step.id, step_title: step.title,
          ai_concern: aiResult?.message || "Step blocked",
          tech_reason: reason,
          photo_id: photoIds.find(p => p.step_id === step.id)?.id || null,
        }),
      });
      const data = await res.json();
      if (data.id) {
        setPendingOverride({ id: data.id, status: "pending" });
        setShowOverrideForm(false);
        setOverrideReason("");
        addMsg("bot", "⏳ **Override requested.** PM has been notified with your photo, the AI concern, and your reason. Waiting for approval...", { status: "warn" });
      }
    } catch {
      addMsg("bot", "Failed to submit override request. Try again.", { status: "fail" });
    }
  }, [step, sRes, activeTemplate, techName, club, photoIds, addMsg]);

  // Poll for override resolution
  useEffect(() => {
    if (!pendingOverride || pendingOverride.status !== "pending") return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/overrides/${pendingOverride.id}`);
        const data = await res.json();
        if (data.status === "approved") {
          setPendingOverride({ ...pendingOverride, status: "approved" });
          const nc = { ...cfsRef.current, [`${step?.id}_photo`]: true };
          setCfs(nc); cfsRef.current = nc;
          addMsg("bot", `✅ **Override APPROVED** by ${data.resolved_by || "PM"}. Step unblocked — continue.`, { status: "pass" });
          setTimeout(() => checkDone(nc), 300);
        } else if (data.status === "rejected") {
          setPendingOverride({ ...pendingOverride, status: "rejected" });
          addMsg("bot", `❌ **Override REJECTED** by ${data.resolved_by || "PM"}. Fix the issue and re-upload.`, { status: "fail" });
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [pendingOverride, step, addMsg, checkDone]);

  const sendText = async () => {
    const txt = input.trim(); if (!txt || thinking) return;
    setInput(""); addMsg("user", txt);
    const nh = [...history, { role: "user", content: txt }]; setHistory(nh);
    setThinking(true); scroll();
    const res = await callClaude(nh.slice(-20), buildSys());
    setThinking(false);
    let clean = res;
    // AI is a HELP CHAT — it cannot mark checklist items complete.
    // Strip any |||CONFIRM:...||| tokens the AI emits (ignore them entirely).
    clean = clean.replace(/\|\|\|CONFIRM:\[.*?\]\|\|\|/g, "").trim();

    // Parse AI-triggered PM notifications: |||NOTIFY:severity|message|||
    // When the AI detects something the PM needs to know, it fires a notification
    // so the tech doesn't have to manually reach out.
    const notifyMatches = [...res.matchAll(/\|\|\|NOTIFY:([a-z]+)\|([^|]+?)\|\|\|/gi)];
    for (const nm of notifyMatches) {
      const severity = (nm[1] || "medium").toLowerCase();
      const message = nm[2].trim();
      sendNotification("ai_alert", `${step?.title || "Step"}: ${message}`, {
        severity, step_index: stepIdx, club_number: club, tech_phone: techPhone,
      });
    }
    if (notifyMatches.length > 0) {
      clean = clean.replace(/\|\|\|NOTIFY:[^|]+\|[^|]+?\|\|\|/gi, "").trim();
      // Add a subtle indicator that PM was notified
      clean += `\n\n✉️ _PM has been notified automatically._`;
    }
    // Parse inventory tokens from conversational capture
    const invMatch = res.match(/\|\|\|INVENTORY:(\[.*?\])\|\|\|/s);
    if (invMatch) {
      clean = clean.replace(/\|\|\|INVENTORY:\[.*?\]\|\|\|/gs, "").trim();
      try {
        const items = JSON.parse(invMatch[1]);
        const s = steps[siRef.current];
        if (s?.type === "inventory" && Array.isArray(items)) {
          const current = invRef.current[s.id] || [];
          const updated = { ...invRef.current, [s.id]: [...current, ...items] };
          setInventoryData(updated); invRef.current = updated;
        }
      } catch {}
    }

    // Parse SUGGEST tokens — AI proposes actions the tech can apply with one tap.
    // These do NOT auto-apply. They render as buttons below the AI message.
    const suggestions = [];
    const suggestRegex = /\|\|\|SUGGEST:(confirm|inventory)\|([^|]+?)\|\|\|/gi;
    let sm;
    while ((sm = suggestRegex.exec(res)) !== null) {
      const kind = sm[1].toLowerCase();
      const raw = sm[2].trim();
      if (kind === "confirm") {
        // Validate it's actually a confirm label on the current step
        const s = steps[siRef.current];
        if (s?.confirms?.includes(raw)) {
          suggestions.push({ kind: "confirm", label: raw });
        }
      } else if (kind === "inventory") {
        try {
          const item = JSON.parse(raw);
          if (item && typeof item === "object") suggestions.push({ kind: "inventory", item });
        } catch {}
      }
    }
    clean = clean.replace(/\|\|\|SUGGEST:[^|]+\|[^|]+?\|\|\|/gi, "").trim();

    addMsg("bot", clean, suggestions.length > 0 ? { suggestions } : {});
    setHistory(prev => [...prev, { role: "assistant", content: res }]);
    // Auto-listen in hands-free mode after AI responds
    if (handsFree && SpeechRecognition && !listening) {
      setTimeout(() => {
        // Wait for TTS to finish before listening again
        const waitForSpeech = () => {
          if (window.speechSynthesis?.speaking) { setTimeout(waitForSpeech, 300); return; }
          toggleListening();
        };
        waitForSpeech();
      }, 500);
    }
  };

  // Upload photo to KV storage in background
  const uploadPhoto = useCallback(async (b64, stepId, aiPass, aiMessage) => {
    try {
      const ct = b64.includes("image/png") ? "image/png" : "image/jpeg";
      const res = await fetchWithRetry("/api/photos", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: currentSessionId, // link immediately if we have a session
          step_id: stepId, data: b64, content_type: ct,
          ai_pass: aiPass, ai_message: aiMessage,
          tech_name: techName, club_number: club,
          template_id: activeTemplate?.id,
        }),
      });
      const data = await res.json();
      if (data.id) {
        setPhotoIds(prev => [...prev, { id: data.id, step_id: stepId, url: `/api/photos/${data.id}`, ai_pass: aiPass === 1 ? true : aiPass === 0 ? false : null }]);
        return data;
      }
      // Surface error to tech so we never have silent upload failures
      if (data.error) {
        addMsg("bot", `⚠️ **Photo upload failed**: ${data.error}. Try again or check your connection.`, { status: "fail" });
      }
      return null;
    } catch (e) {
      addMsg("bot", `⚠️ **Photo upload failed**: ${e.message || "network error"}. Try again.`, { status: "fail" });
      return null;
    }
  }, [currentSessionId, techName, club, activeTemplate, addMsg]);

  // Process a single photo file — supports batch upload
  const processPhoto = useCallback(async (file, isLastInBatch) => {
    if (!file || !step) return;
    const rawB64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (ev) => resolve(ev.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    // Compress — handles Android full-res JPEGs (4-8MB) down to ~200-400KB
    const b64 = await compressImage(rawB64);
    addMsg("user", null, { photo: b64 });
    scroll();

    if (step.aiCheck && step.aiPrompt) {
      setThinking(true); scroll();
      const result = await analyzePhoto(b64, step.aiPrompt);
      setThinking(false);
      // Accumulate AI results — each photo's analysis is stored, and the step passes
      // if ANY photo passed (tech can upload multiple angles/retries)
      setSRes(prev => {
        const existing = prev[step.id] || { results: [], pass: false };
        const newResults = [...(existing.results || []), { ...result, photo: b64 }];
        const anyPassed = newResults.some(r => r.pass);
        const n = { ...prev, [step.id]: { ...result, results: newResults, pass: anyPassed, message: result.message } };
        srRef.current = n;
        return n;
      });
      uploadPhoto(b64, step.id, result.pass ? 1 : 0, result.message || "");
      if (result.pass) {
        const nc = { ...cfsRef.current, [`${step.id}_photo`]: true };
        setCfs(nc); cfsRef.current = nc;
        addMsg("bot", `✅ **PASS** — ${result.message}`, { status: "pass" });
        if (isLastInBatch) setTimeout(() => checkDone(nc), 300);
      } else if (step.hardGate) {
        addMsg("bot", `🚫 **BLOCKED** — ${result.message}\n\n${step.expectedIP ? `IP must be **${step.expectedIP}**. ` : ""}Fix it and re-upload, or request a PM override.`, { status: "fail" });
        sendNotification("hard_gate", `Hard gate blocked: ${result.message}`, { severity: "high" });
        if (isLastInBatch) { setShowOverrideForm(true); setPendingOverride(null); }
      } else {
        addMsg("bot", `⚠️ **FLAG** — ${result.message}\n\nUpload another photo, let me know if this looks right, or request a PM override.`, { status: "fail" });
        sendNotification("ai_flag", `AI flagged photo: ${result.message}`, { severity: "medium" });
        // Still mark photo as uploaded even if flagged (non-hard-gate)
        const nc = { ...cfsRef.current, [`${step.id}_photo`]: true };
        setCfs(nc); cfsRef.current = nc;
        if (isLastInBatch) setTimeout(() => checkDone(nc), 300);
      }
    } else {
      uploadPhoto(b64, step.id, null, null);
      const nc = { ...cfsRef.current, [`${step.id}_photo`]: true };
      setCfs(nc); cfsRef.current = nc;
      addMsg("bot", "📸 Photo received.", { status: "pass" });
      if (isLastInBatch) setTimeout(() => checkDone(nc), 300);
    }
  }, [step, addMsg, scroll, uploadPhoto, sendNotification]);

  const handlePhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !step) return;
    e.target.value = "";
    if (files.length > 1) {
      addMsg("bot", `📸 Processing ${files.length} photos...`, { status: "pass" });
    }
    // Process photos sequentially to avoid race conditions with state updates
    for (let i = 0; i < files.length; i++) {
      await processPhoto(files[i], i === files.length - 1);
    }
  };

  const handleConfirm = (label) => {
    const key = `${step.id}_${label}`; if (cfsRef.current[key]) return;
    // Block checkbox if photo required but not uploaded
    if (step?.requiresPhoto && !cfsRef.current[`${step.id}_photo`]) {
      addMsg("bot", "⚠️ Please upload the required photo first before checking off items.", { status: "fail" });
      return;
    }
    // Block checkbox if hard gate not passed
    if (step?.hardGate && (!srRef.current[step.id] || !srRef.current[step.id].pass)) {
      addMsg("bot", "⚠️ This step has a hard gate. You need to upload a photo that passes AI verification first.", { status: "fail" });
      return;
    }
    const nc = { ...cfsRef.current, [key]: new Date().toISOString() }; setCfs(nc); cfsRef.current = nc;
    addMsg("user", `✓ ${label}`);
    setTimeout(() => checkDone(nc), 200);
  };


  // Load completions from D1
  const loadCompletions = useCallback(() => {
    fetch("/api/completions").then(r => r.json()).then(data => {
      if (Array.isArray(data)) setCompletions(data);
    }).catch(() => {});
  }, []);

  const loadSavedTemplates = useCallback(() => {
    fetch("/api/jobs").then(r => r.json()).then(data => {
      if (Array.isArray(data)) setTemplates(data);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadCompletions(); loadSavedTemplates(); }, [loadCompletions, loadSavedTemplates]);

  // Load roster from dashboard via CB-OPS cache
  useEffect(() => {
    fetch("/api/resources").then(r => r.json()).then(d => {
      if (d.roster && Array.isArray(d.roster)) setRoster(d.roster);
    }).catch(() => {});
  }, []);

  // Persist acting PM to localStorage
  useEffect(() => {
    try {
      if (actingPm) localStorage.setItem("cb_ops_acting_pm", actingPm);
      else localStorage.removeItem("cb_ops_acting_pm");
    } catch {}
  }, [actingPm]);

  // On initial load, if user is already signed in but has no contact info,
  // prompt them once per session to fill it in.
  const contactCheckedRef = useRef(false);
  useEffect(() => {
    if (!actingPm || contactCheckedRef.current) return;
    contactCheckedRef.current = true;
    const dismissed = sessionStorage.getItem(`cb_ops_contact_dismissed_${actingPm}`);
    if (dismissed) return;
    fetch(`/api/pm-contacts/${encodeURIComponent(actingPm)}`).then(r => r.json()).then(data => {
      if (!data.email && !data.phone && !data.teams_webhook) {
        setPmContactEdit({ name: actingPm, email: "", phone: "", teams_webhook: "", isWelcome: true });
      }
    }).catch(() => {});
  }, [actingPm]);

  // Capture PM from URL when tech opens a job link (/job/:id?pm=Kelsey%20Rosa)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const pmFromUrl = params.get("pm");
      if (pmFromUrl) setAssignedPm(pmFromUrl);
    } catch {}
  }, []);

  // If URL has ?edit_template=xxx, open that template in the editor
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get("edit_template");
    if (editId) {
      fetch(`/api/jobs/${editId}`).then(r => r.json()).then(tpl => {
        if (tpl && !tpl.error) {
          setPendingTemplate(tpl);
          setScreen("templateReview");
          // Clear query param
          window.history.replaceState({}, "", "/");
        }
      }).catch(() => {});
    }
  }, []);
  useEffect(() => {
    fetch("/api/config").then(r => r.json()).then(d => {
      if (d.pm_phone) setPmPhone(d.pm_phone);
      if (d.pm_name) setPmName(d.pm_name);
    }).catch(() => {});
  }, []);

  const startChat = async () => {
    // Check D1 for an existing in-progress session for this tech+club+template
    let resumed = false;
    if (activeTemplate?.id && techName && club) {
      try {
        const effectivePm = assignedPm || actingPm || activeTemplate?.created_by_pm || "";
        const res = await fetch(`/api/jobs/${activeTemplate.id}/resume?tech=${encodeURIComponent(techName)}&club=${encodeURIComponent(club)}&pm=${encodeURIComponent(effectivePm)}`);
        const data = await res.json();
        if (data.found && data.state) {
          const s = data.state;
          setScreen("chat");
          if (data.session_id) setCurrentSessionId(data.session_id);
          const resumedStepIdx = s.step_idx || 0;
          setStepIdx(resumedStepIdx); siRef.current = resumedStepIdx;
          setCfs(s.cfs || {}); cfsRef.current = s.cfs || {};
          setSRes(s.sRes || {}); srRef.current = s.sRes || {};
          setInventoryData(s.inventory || {}); invRef.current = s.inventory || {};
          if (s.tech_phone && !techPhone) setTechPhone(s.tech_phone);
          // Restore messages
          const restoredMessages = (s.messages || []).map((m, i) => ({ ...m, id: Date.now() + i }));
          setMessages(restoredMessages);
          setHistory(s.history || []);
          setPendingOverride(null); setShowOverrideForm(false); setOverrideReason("");
          setTimeout(() => {
            addMsg("bot", `👋 **Welcome back, ${techName}!** Resuming your checklist for **${club}**.\n\nYou were on step ${resumedStepIdx + 1}/${steps.length}: **${steps[resumedStepIdx]?.title || ""}**`, { status: "pass" });
          }, 300);
          resumed = true;
        }
      } catch {}
    }

    if (!resumed) {
      setScreen("chat"); setMessages([]); setStepIdx(0); siRef.current = 0;
      setCfs({}); cfsRef.current = {}; setSRes({}); srRef.current = {}; setHistory([]);
      setInventoryData({}); invRef.current = {};
      setPhotoIds([]);
      setCurrentSessionId(null);
      setSessionCompleted(false);
      setPendingOverride(null); setShowOverrideForm(false); setOverrideReason("");
      setTimeout(() => {
        addMsg("bot", `Hey ${techName} — starting **${activeTemplate?.title}** for **${club}**.\n\n${steps.length} steps. Ask me anything along the way.`);
        setTimeout(() => {
          const s = steps[0];
          const refNote = s.referenceImages?.length ? `\n\n📐 *${s.referenceImages.length} reference image${s.referenceImages.length !== 1 ? "s" : ""} from SOW — see below*` : "";
          addMsg("bot", `**${s.title}**\n\n${s.instruction}${refNote}`);
        }, 1000);
      }, 300);
    }
  };

  // Reusable PM picker modal — autocomplete-style name search.
  // Sort roster alphabetically by name, then filter.
  const sortedRoster = [...roster].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  const filteredRoster = pmPickerSearch.trim()
    ? sortedRoster.filter(p => p.name.toLowerCase().includes(pmPickerSearch.trim().toLowerCase()))
    : sortedRoster;

  const pmPickerModal = showPmPicker && (
    <div onClick={() => { setShowPmPicker(false); setPmPickerSearch(""); }} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: c.surface, border: `1px solid ${c.border}`, borderRadius: 16,
        padding: 20, maxWidth: 400, width: "100%", maxHeight: "85vh", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.text, marginBottom: 6 }}>Who are you?</div>
        <div style={{ fontSize: 12, color: c.textDim, marginBottom: 12 }}>Start typing your name.</div>

        {/* Search input */}
        <input autoFocus value={pmPickerSearch} onChange={e => setPmPickerSearch(e.target.value)}
          placeholder="Type a name..."
          style={{
            width: "100%", padding: "12px 14px", background: c.inputBg,
            border: `1.5px solid ${c.border}`, borderRadius: 10, color: c.text,
            fontSize: 15, outline: "none", marginBottom: 10, boxSizing: "border-box",
          }} />

        {/* Filtered list */}
        <div style={{ flex: 1, overflow: "auto", minHeight: 100 }}>
          {roster.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: c.textDim, fontSize: 13 }}>Loading roster...</div>
          ) : filteredRoster.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: c.textDim, fontSize: 13 }}>No matches for "{pmPickerSearch}"</div>
          ) : (
            filteredRoster.slice(0, 30).map(p => (
              <button key={p.name} onClick={async () => {
                setActingPm(p.name);
                setShowPmPicker(false);
                setPmPickerSearch("");
                // Check if they have contact info; if not, prompt immediately
                try {
                  const res = await fetch(`/api/pm-contacts/${encodeURIComponent(p.name)}`);
                  const data = await res.json();
                  if (!data.email && !data.phone && !data.teams_webhook) {
                    setPmContactEdit({ name: p.name, email: "", phone: "", teams_webhook: "", isWelcome: true });
                  }
                } catch {}
              }} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
                padding: "12px 14px", borderRadius: 8, marginBottom: 4,
                border: `1px solid ${actingPm === p.name ? c.accentBorder : c.border}`,
                background: actingPm === p.name ? c.accentDim : c.inputBg,
                color: c.text, cursor: "pointer", textAlign: "left",
              }}>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{p.name}</span>
                {actingPm === p.name && <span style={{ color: c.accent, fontSize: 14 }}>✓</span>}
              </button>
            ))
          )}
        </div>

        {actingPm && (
          <div style={{ fontSize: 11, color: c.textMuted, marginTop: 10, padding: "8px 10px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, lineHeight: 1.4 }}>
            💡 Manage all contact info (email, phone, Teams webhook) in <strong>Admin → 👥 Team Directory</strong>.
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          {actingPm && (
            <button onClick={() => { setActingPm(""); setShowPmPicker(false); setPmPickerSearch(""); }} style={{
              padding: "10px 14px", borderRadius: 8, border: `1px solid ${c.border}`,
              background: c.inputBg, color: c.textDim, fontSize: 12, cursor: "pointer",
            }}>Sign out</button>
          )}
          <button onClick={() => { setShowPmPicker(false); setPmPickerSearch(""); }} style={{
            flex: 1, padding: "10px 14px", borderRadius: 8, border: "none",
            background: c.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>Done</button>
        </div>
      </div>
    </div>
  );

  // Contact info editor modal
  const pmContactModal = pmContactEdit && (
    <div onClick={() => setPmContactEdit(null)} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 10000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: c.surface, border: `1px solid ${c.border}`, borderRadius: 16,
        padding: 20, maxWidth: 420, width: "100%",
        boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.text, marginBottom: 4 }}>
          {pmContactEdit.isWelcome ? `👋 Welcome, ${pmContactEdit.name.split(" ")[0]}!` : "Contact Info"}
        </div>
        {!pmContactEdit.isWelcome && (
          <div style={{ fontSize: 13, color: c.accent, fontWeight: 600, marginBottom: 12 }}>{pmContactEdit.name}</div>
        )}
        <div style={{ fontSize: 12, color: c.textDim, marginBottom: 14, lineHeight: 1.5 }}>
          {pmContactEdit.isWelcome
            ? "Before you dive in, let's set up how you want to be notified when something needs your attention. You can add any combination — email, SMS, or Microsoft Teams. You can update these any time in the Team Directory."
            : "Notifications for jobs you own will be sent here. Leave any field blank to skip that channel."}
        </div>

        <label style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1, display: "block", marginBottom: 4 }}>EMAIL</label>
        <input value={pmContactEdit.email} onChange={e => setPmContactEdit({ ...pmContactEdit, email: e.target.value })}
          placeholder="you@codeblue.com" type="email"
          style={{
            width: "100%", padding: "10px 12px", background: c.inputBg,
            border: `1px solid ${c.border}`, borderRadius: 8, color: c.text,
            fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box",
          }} />

        <label style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1, display: "block", marginBottom: 4 }}>PHONE (FOR SMS)</label>
        <input value={pmContactEdit.phone} onChange={e => setPmContactEdit({ ...pmContactEdit, phone: e.target.value })}
          placeholder="+1 555 123 4567" type="tel"
          style={{
            width: "100%", padding: "10px 12px", background: c.inputBg,
            border: `1px solid ${c.border}`, borderRadius: 8, color: c.text,
            fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box",
          }} />

        <label style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1, display: "block", marginBottom: 4 }}>TEAMS WEBHOOK URL</label>
        <input value={pmContactEdit.teams_webhook} onChange={e => setPmContactEdit({ ...pmContactEdit, teams_webhook: e.target.value })}
          placeholder="https://...webhook.office.com/..."
          style={{
            width: "100%", padding: "10px 12px", background: c.inputBg,
            border: `1px solid ${c.border}`, borderRadius: 8, color: c.text,
            fontSize: 12, fontFamily: mono, outline: "none", marginBottom: 6, boxSizing: "border-box",
          }} />
        <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 14 }}>
          In Teams: channel ⋯ → Manage channel → Connectors → Incoming Webhook → paste URL here
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => {
            if (pmContactEdit.isWelcome) {
              // Remember they dismissed so we don't re-prompt until next session
              try { sessionStorage.setItem(`cb_ops_contact_dismissed_${pmContactEdit.name}`, "1"); } catch {}
            }
            setPmContactEdit(null);
          }} style={{
            padding: "10px 14px", borderRadius: 8, border: `1px solid ${c.border}`,
            background: c.inputBg, color: c.textDim, fontSize: 13, cursor: "pointer",
          }}>{pmContactEdit.isWelcome ? "Skip for now" : "Cancel"}</button>
          <button onClick={async () => {
            try {
              const res = await fetch(`/api/pm-contacts/${encodeURIComponent(pmContactEdit.name)}`, {
                method: "PUT", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: pmContactEdit.email || null,
                  phone: pmContactEdit.phone || null,
                  teams_webhook: pmContactEdit.teams_webhook || null,
                }),
              });
              if (res.ok) {
                try { sessionStorage.setItem(`cb_ops_contact_dismissed_${pmContactEdit.name}`, "1"); } catch {}
                setPmContactEdit(null);
              }
            } catch {}
          }} style={{
            flex: 1, padding: "10px 14px", borderRadius: 8, border: "none",
            background: c.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>Save</button>
        </div>
      </div>
    </div>
  );

  // ─── ADMIN SCREEN ─────────────────────────────────────────────
  if (screen === "admin") {
    return (
      <ThemeCtx.Provider value={c}>
        {pmPickerModal}{pmContactModal}
        <AdminDashboard theme={c} mode={mode} onToggleMode={() => setMode(m => m === "dark" ? "light" : "dark")}
          actingPm={actingPm} onChangeActingPm={() => setShowPmPicker(true)} />
      </ThemeCtx.Provider>
    );
  }

  if (screen === "home") {
    const selTemplate = templates.find(t => t.id === selectedTemplateId);
    return (
      <ThemeCtx.Provider value={c}>
      {pmPickerModal}{pmContactModal}
      <div style={{ minHeight: "100dvh", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: sans }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
        <div style={{ width: "100%", maxWidth: 440 }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: c.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", fontWeight: 700 }}>⚡</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: c.text }}>CB-OPS</div>
                <div style={{ fontSize: 11, color: c.textMuted, fontFamily: mono, letterSpacing: 2.5, textTransform: "uppercase" }}>Field Operations</div>
              </div>
            </div>
            <ThemeToggle mode={mode} onToggle={() => setMode(m => m === "dark" ? "light" : "dark")} />
          </div>

          {/* Signed-in-as banner */}
          <button onClick={() => setShowPmPicker(true)} style={{
            width: "100%", padding: "10px 14px", borderRadius: 10, marginBottom: 16, cursor: "pointer",
            border: `1px solid ${actingPm ? c.accentBorder : c.border}`,
            background: actingPm ? c.accentDim : c.surface,
            color: actingPm ? c.accent : c.textDim,
            fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span>{actingPm ? `👤 Signed in as ${actingPm}` : "👤 Who are you? (pick your name)"}</span>
            <span style={{ fontSize: 11, fontFamily: mono, opacity: 0.7 }}>{actingPm ? "CHANGE" : "SELECT"}</span>
          </button>

          {/* Select Template */}
          <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, marginBottom: 12, boxShadow: c.shadow }}>
            <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 500, letterSpacing: 1.5, marginBottom: 10, textTransform: "uppercase" }}>Select Template</div>
            <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)}
              style={{ width: "100%", padding: "12px 16px", background: c.inputBg, border: `1.5px solid ${c.border}`, borderRadius: 12, color: c.text, fontSize: 15, fontFamily: sans, outline: "none", appearance: "none", boxSizing: "border-box", cursor: "pointer",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(c.textDim)}' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center",
              }}>
              <option value="">Choose a job template...</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.ticket_number ? `[${t.ticket_number}] ` : ""}{t.title} — {t.step_count || t.steps?.length || 0} steps{t.session_count > 0 ? ` (${t.session_count} done)` : ""}</option>
              ))}
            </select>

            {/* Selected template details + actions */}
            {selTemplate && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, color: c.textDim, lineHeight: 1.5, marginBottom: 12 }}>
                  {selTemplate.description || "No description"}
                  {selTemplate.ticket_number && <span style={{ display: "block", fontSize: 12, color: c.accent, fontFamily: mono, fontWeight: 500, marginTop: 4 }}>Ticket: {selTemplate.ticket_number}</span>}
                  {selTemplate.sow_filename && <span style={{ display: "block", fontSize: 11, color: c.textMuted, fontFamily: mono, marginTop: 2 }}>SOW: {selTemplate.sow_filename}</span>}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => { setActiveTemplate(selTemplate); setTicketNumber(selTemplate.ticket_number || ""); setScreen("start"); }} style={{
                    flex: 1, padding: "12px 16px", borderRadius: 12, border: "none",
                    background: c.accent, color: "#fff",
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                  }}>Start Job</button>
                  <button onClick={() => { setPendingTemplate(selTemplate); setScreen("templateReview"); }} style={{
                    padding: "12px 16px", borderRadius: 12, border: `1px solid ${c.border}`,
                    background: c.surface, color: c.textDim,
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                  }}>Edit</button>
                  <button onClick={() => {
                    const pmParam = actingPm ? `?pm=${encodeURIComponent(actingPm)}` : "";
                    const link = `${window.location.origin}/job/${selTemplate.id}${pmParam}`;
                    navigator.clipboard.writeText(link).catch(() => {});
                    setJobLink(link); setActiveTemplate(selTemplate); setScreen("linkReady");
                  }} style={{
                    padding: "12px 16px", borderRadius: 12, border: `1px solid ${c.border}`,
                    background: c.surface, color: c.textDim,
                    fontSize: 14, fontWeight: 600, cursor: "pointer",
                  }}>Share</button>
                </div>
              </div>
            )}
          </div>

          {/* Upload New SOW */}
          <button onClick={() => setScreen("sowUpload")}
            style={{ width: "100%", padding: 16, borderRadius: 12, border: `1.5px dashed ${c.border}`, background: c.surface, cursor: "pointer", textAlign: "center", boxShadow: c.shadow }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>📄</div>
            <div style={{ fontSize: 14, color: c.text, fontWeight: 600 }}>Upload New SOW</div>
            <div style={{ fontSize: 12, color: c.textMuted }}>Create a job template from a Scope of Work</div>
          </button>

          {/* Bottom actions */}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={() => { loadCompletions(); setScreen("completions"); }}
              style={{ flex: 1, padding: 14, borderRadius: 12, border: `1px solid ${c.border}`, background: c.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: c.shadow }}>
              <span style={{ fontSize: 16 }}>✅</span>
              <span style={{ fontSize: 13, color: c.text, fontWeight: 600 }}>Jobs</span>
              {completions.length > 0 && <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 9999, background: c.accentDim, color: c.accent, fontFamily: mono, fontWeight: 700 }}>{completions.length}</span>}
            </button>
            <button onClick={() => { setScreen("admin"); window.history.pushState({}, "", "/admin"); }}
              style={{ flex: 1, padding: 14, borderRadius: 12, border: `1px solid ${c.border}`, background: c.surface, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, boxShadow: c.shadow }}>
              <span style={{ fontSize: 16 }}>📊</span>
              <span style={{ fontSize: 13, color: c.text, fontWeight: 600 }}>Admin</span>
            </button>
          </div>
        </div>
      </div>
      </ThemeCtx.Provider>
    );
  }

  // ─── COMPLETIONS SCREEN ─────────────────────────────────────
  if (screen === "completions") {
    const active = completions.filter(c => c.status === "in_progress");
    const done = completions.filter(c => c.status !== "in_progress");
    return (
      <ThemeCtx.Provider value={c}>
      <div style={{ minHeight: "100dvh", background: c.bg, fontFamily: sans }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
        <div style={{ maxWidth: 700, margin: "0 auto", padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <button onClick={() => setScreen("home")} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: c.textDim, padding: 0 }}>←</button>
            <div style={{ fontSize: 18, fontFamily: mono, color: c.accent, fontWeight: 800, flex: 1 }}>Jobs</div>
            <button onClick={() => { setScreen("admin"); window.history.pushState({}, "", "/admin"); }}
              style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${c.border}`, background: c.surface, color: c.textDim, fontSize: 12, fontWeight: 700, fontFamily: mono, cursor: "pointer" }}>FULL ADMIN →</button>
            <ThemeToggle mode={mode} onToggle={() => setMode(m => m === "dark" ? "light" : "dark")} />
          </div>

          {/* Active/In-Progress Jobs */}
          {active.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: c.accent, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>IN PROGRESS ({active.length})</div>
              {active.map((comp) => {
                const prog = comp.results || {};
                const stepNum = prog.current_step || (prog.step_idx != null ? prog.step_idx + 1 : "?");
                const totalNum = prog.total_steps || "?";
                return (
                  <button key={comp.id} onClick={() => { setScreen("admin"); window.history.pushState({}, "", `/admin?session=${comp.id}`); }}
                    style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "14px 16px", background: c.surface, border: `1px solid ${c.accentBorder}`, borderRadius: 14, cursor: "pointer", marginBottom: 8, textAlign: "left" }}>
                    <div style={{ width: 44, height: 44, borderRadius: 10, background: c.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "#fff", fontWeight: 700, fontFamily: mono, flexShrink: 0 }}>
                      {stepNum}/{totalNum}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: c.text, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{comp.job_title || "Untitled"}</div>
                      <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono }}>
                        {comp.tech_name || "—"} • Club {comp.club_number || "—"}
                        {prog.step_title ? ` • ${prog.step_title}` : ""}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, color: c.accent, fontFamily: mono, fontWeight: 700, padding: "3px 8px", background: c.accentDim, border: `1px solid ${c.accentBorder}`, borderRadius: 6, flexShrink: 0 }}>LIVE</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Completed Jobs */}
          <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>COMPLETED ({done.length})</div>
          {done.length === 0 && active.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: c.textDim }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 15 }}>No jobs yet</div>
            </div>
          ) : done.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: c.textDim, fontSize: 13 }}>No completed jobs yet</div>
          ) : (
            done.map((comp) => (
              <button key={comp.id} onClick={() => { setScreen("admin"); window.history.pushState({}, "", `/admin?session=${comp.id}`); }}
                style={{ display: "block", width: "100%", textAlign: "left", background: c.surface, border: `1px solid ${c.border}`, borderRadius: 14, padding: 16, marginBottom: 10, cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, color: c.text, fontWeight: 700 }}>{comp.job_title || "Untitled Job"}</div>
                    <div style={{ fontSize: 12, color: c.textDim, fontFamily: mono, marginTop: 2 }}>Club: {comp.club_number || "—"}</div>
                  </div>
                  <div style={{ fontSize: 11, color: c.accent, fontFamily: mono, fontWeight: 600, padding: "3px 8px", background: c.accentDim, borderRadius: 6, flexShrink: 0 }}>COMPLETE</div>
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 12, color: c.textDim, fontFamily: mono }}>
                  <span>Tech: {comp.tech_name || "—"}</span>
                  <span>{comp.completed_at ? new Date(comp.completed_at).toLocaleDateString() + " " + new Date(comp.completed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                </div>
                {comp.results?.steps && (
                  <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {comp.results.steps.map((st, i) => (
                      <span key={i} style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, fontFamily: mono, background: st.pass !== false ? c.accentDim : c.failDim, color: st.pass !== false ? c.accent : c.fail }}>{st.title}</span>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 11, color: c.accent, fontFamily: mono, textAlign: "right" }}>Tap to view full details →</div>
              </button>
            ))
          )}
        </div>
      </div>
      </ThemeCtx.Provider>
    );
  }

  // ─── SOW UPLOAD ───────────────────────────────────────────────
  if (screen === "sowUpload") {
    return (
      <ThemeCtx.Provider value={c}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
        <SOWUpload theme={c} mode={mode} onToggleMode={() => setMode(m => m === "dark" ? "light" : "dark")}
          onTemplateParsed={(t) => { setPendingTemplate(t); setScreen("templateReview"); }} />
      </ThemeCtx.Provider>
    );
  }

  // ─── TEMPLATE EDITOR ──────────────────────────────────────────
  if (screen === "templateReview" && pendingTemplate) {
    return (
      <ThemeCtx.Provider value={c}>
        <TemplateEditor template={pendingTemplate} theme={c}
          onBack={() => setScreen(pendingTemplate?.id ? "home" : "sowUpload")}
          onAccept={async (t) => {
            // If template already has an ID (editing existing), PUT to update in place
            // Otherwise POST to create new
            try {
              if (pendingTemplate?.id) {
                const putRes = await fetch(`/api/jobs/${pendingTemplate.id}`, {
                  method: "PUT", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ title: t.title, description: t.description, steps: t.steps, ticket_number: t.ticket_number || null }),
                });
                if (!putRes.ok) { const e = await putRes.json().catch(() => ({})); throw new Error(e.error || `Save failed (${putRes.status})`); }
                t.id = pendingTemplate.id;
                setJobLink(`${window.location.origin}/job/${t.id}`);
              } else {
                const res = await fetch("/api/jobs", {
                  method: "POST", headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ title: t.title, description: t.description, steps: t.steps, sow_filename: t.sow_filename || "", sow_content: t.sow_content || null, ticket_number: t.ticket_number || null, created_by_pm: actingPm || null }),
                });
                const data = await res.json();
                if (data.id) {
                  t.id = data.id;
                  setJobLink(data.url || `${window.location.origin}/job/${data.id}`);
                }
              }
              // Upload pending reference images to KV
              if (t._pendingRefImages?.length > 0 && t.id) {
                const refUploads = await Promise.allSettled(
                  t._pendingRefImages.map(async (img) => {
                    const resp = await fetch("/api/ref-images", {
                      method: "POST", headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ template_id: t.id, page: img.page, description: img.description, step_ids: img.step_ids, data: img.dataUrl }),
                    });
                    return resp.json();
                  })
                );
                // Update step referenceImages with actual URLs
                const uploaded = refUploads.filter(r => r.status === "fulfilled" && r.value?.id).map(r => r.value);
                for (const u of uploaded) {
                  for (const sid of u.step_ids || []) {
                    const step = t.steps.find(s => s.id === sid);
                    if (step) {
                      if (!step.referenceImages) step.referenceImages = [];
                      const existing = step.referenceImages.find(r => r.page === u.page);
                      if (existing) { existing.url = u.url; existing.id = u.id; }
                      else step.referenceImages.push({ page: u.page, description: u.description, url: u.url, id: u.id });
                    }
                  }
                }
                // Re-save steps with reference image URLs
                if (uploaded.length > 0) {
                  await fetch(`/api/jobs/${t.id}`, {
                    method: "PUT", headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ title: t.title, description: t.description, steps: t.steps, ticket_number: t.ticket_number || null }),
                  });
                }
              }
            } catch (e) { console.error("Failed to save job:", e); alert("Failed to save template: " + e.message); }
            loadSavedTemplates(); // refresh from D1
            setActiveTemplate(t);
            setPendingTemplate(null);
            setScreen("linkReady");
          }} />
      </ThemeCtx.Provider>
    );
  }

  // ─── LINK READY SCREEN (shareable link for tech) ───────────────
  if (screen === "linkReady" && activeTemplate) {
    const pmParam = actingPm ? `?pm=${encodeURIComponent(actingPm)}` : "";
    const link = jobLink || `${window.location.origin}/job/${activeTemplate.id}${pmParam}`;
    return (
      <ThemeCtx.Provider value={c}>
      <div style={{ minHeight: "100dvh", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: sans }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
        <div style={{ width: "100%", maxWidth: 420, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h2 style={{ fontSize: 22, color: c.text, fontFamily: sans, fontWeight: 700, margin: "0 0 8px" }}>Job Template Ready</h2>
          <p style={{ fontSize: 14, color: c.textDim, margin: "0 0 24px" }}>
            **{activeTemplate.title}** — {activeTemplate.steps?.length} steps
          </p>

          <div style={{ background: c.surface, borderRadius: 16, padding: 20, border: `1px solid ${c.border}`, marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: c.textDim, fontFamily: mono, fontWeight: 700, letterSpacing: 1.5, marginBottom: 10 }}>SEND THIS LINK TO THE TECH</div>
            <div style={{ background: c.inputBg, borderRadius: 10, padding: "12px 14px", border: `1px solid ${c.border}`, fontSize: 14, color: c.accent, fontFamily: mono, wordBreak: "break-all", marginBottom: 12 }}>
              {link}
            </div>
            <button onClick={() => { navigator.clipboard.writeText(link); }}
              style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})`, color: "#fff", fontSize: 16, fontWeight: 700, fontFamily: mono, cursor: "pointer", boxShadow: c.shadow }}>
              📋 COPY LINK
            </button>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setScreen("start")}
              style={{ flex: 1, padding: 14, borderRadius: 12, border: `1.5px solid ${c.border}`, background: c.inputBg, color: c.text, fontSize: 14, fontWeight: 600, fontFamily: sans, cursor: "pointer" }}>
              Start Job Myself
            </button>
            <button onClick={() => { setJobLink(null); setScreen("home"); }}
              style={{ flex: 1, padding: 14, borderRadius: 12, border: `1.5px solid ${c.border}`, background: c.inputBg, color: c.textDim, fontSize: 14, fontWeight: 600, fontFamily: sans, cursor: "pointer" }}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
      </ThemeCtx.Provider>
    );
  }

  // ─── LOADING SCREEN (while fetching job from URL) ──────────────
  if (screen === "loading") {
    return (
      <ThemeCtx.Provider value={c}>
      <div style={{ minHeight: "100dvh", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: sans }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 42, height: 42, borderRadius: 11, background: `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, margin: "0 auto 16px", boxShadow: c.shadow }}>⚡</div>
          <div style={{ fontSize: 15, color: c.textDim }}>Loading job...</div>
        </div>
      </div>
      </ThemeCtx.Provider>
    );
  }

  // ─── START SCREEN (tech name + club) ──────────────────────────
  if (screen === "start") {
    return (
      <ThemeCtx.Provider value={c}>
      <div style={{ minHeight: "100dvh", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: sans }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
        <div style={{ width: "100%", maxWidth: 420 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button onClick={() => setScreen("home")} style={{ background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.textDim, padding: "6px 12px", cursor: "pointer", fontSize: 13 }}>←</button>
              <div>
                <div style={{ fontSize: 16, fontFamily: mono, color: c.accent, fontWeight: 800 }}>{activeTemplate?.title}</div>
                <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono }}>{activeTemplate?.steps?.length} STEPS</div>
              </div>
            </div>
            <ThemeToggle mode={mode} onToggle={() => setMode(m => m === "dark" ? "light" : "dark")} />
          </div>
          <div style={{ background: c.surface, borderRadius: 20, padding: 24, border: `1px solid ${c.border}` }}>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: c.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, display: "block", marginBottom: 8, fontFamily: mono }}>Your Name</label>
              <input value={techName} onChange={e => setTechName(e.target.value)} placeholder="Enter your name"
                style={{ width: "100%", padding: "14px 16px", background: c.inputBg, border: `1.5px solid ${c.border}`, borderRadius: 12, color: c.text, fontSize: 17, fontFamily: sans, outline: "none", boxSizing: "border-box" }}
                onFocus={e => e.target.style.borderColor = c.accentBorder} onBlur={e => e.target.style.borderColor = c.border} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: c.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, display: "block", marginBottom: 8, fontFamily: mono }}>Club # / Location</label>
              <input value={club} onChange={e => setClub(e.target.value)} placeholder="e.g. PF-1234"
                style={{ width: "100%", padding: "14px 16px", background: c.inputBg, border: `1.5px solid ${c.border}`, borderRadius: 12, color: c.text, fontSize: 17, fontFamily: sans, outline: "none", boxSizing: "border-box" }}
                onFocus={e => e.target.style.borderColor = c.accentBorder} onBlur={e => e.target.style.borderColor = c.border} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, color: c.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, display: "block", marginBottom: 8, fontFamily: mono }}>Ticket # <span style={{ fontWeight: 400, opacity: 0.6 }}>(Autotask ticket for this job)</span></label>
              <input value={ticketNumber} onChange={e => setTicketNumber(e.target.value)} placeholder="e.g. T20260331.0001"
                style={{ width: "100%", padding: "14px 16px", background: c.inputBg, border: `1.5px solid ${c.border}`, borderRadius: 12, color: c.text, fontSize: 17, fontFamily: sans, outline: "none", boxSizing: "border-box" }}
                onFocus={e => e.target.style.borderColor = c.accentBorder} onBlur={e => e.target.style.borderColor = c.border} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, color: c.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700, display: "block", marginBottom: 8, fontFamily: mono }}>Your Phone # <span style={{ fontWeight: 400, opacity: 0.6 }}>(so PM can call you back)</span></label>
              <input value={techPhone} onChange={e => setTechPhone(e.target.value)} placeholder="e.g. 555-123-4567" type="tel"
                style={{ width: "100%", padding: "14px 16px", background: c.inputBg, border: `1.5px solid ${c.border}`, borderRadius: 12, color: c.text, fontSize: 17, fontFamily: sans, outline: "none", boxSizing: "border-box" }}
                onFocus={e => e.target.style.borderColor = c.accentBorder} onBlur={e => e.target.style.borderColor = c.border} />
            </div>
            <button onClick={() => {
              const errors = [];
              if (!techName.trim()) errors.push("Your Name is required");
              if (!club.trim()) errors.push("Club # is required");
              if (!ticketNumber.trim()) errors.push("Ticket # is required");
              else if (!/^T/i.test(ticketNumber.trim())) errors.push("Ticket # must start with 'T'");
              if (errors.length > 0) { alert(errors.join("\n")); return; }
              startChat();
            }} disabled={!techName.trim() || !club.trim() || !ticketNumber.trim() || !/^T/i.test(ticketNumber.trim())} style={{
              width: "100%", padding: 16, borderRadius: 14, border: "none",
              background: (techName.trim() && club.trim() && ticketNumber.trim() && /^T/i.test(ticketNumber.trim())) ? `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})` : c.inputBg,
              color: (techName.trim() && club.trim() && ticketNumber.trim() && /^T/i.test(ticketNumber.trim())) ? "#fff" : c.textMuted,
              fontSize: 17, fontWeight: 700, fontFamily: mono, cursor: (techName.trim() && club.trim() && ticketNumber.trim() && /^T/i.test(ticketNumber.trim())) ? "pointer" : "not-allowed",
              boxShadow: (techName.trim() && club.trim() && ticketNumber.trim() && /^T/i.test(ticketNumber.trim())) ? c.shadow : "none",
            }}>START JOB →</button>
          </div>
        </div>
      </div>
      </ThemeCtx.Provider>
    );
  }

  // ─── CHAT SCREEN ──────────────────────────────────────────────
  const progress = steps.length > 0 ? ((stepIdx + ((step?.confirms || []).every(x => cfs[`${step?.id}_${x}`]) && (!step?.requiresPhoto || cfs[`${step?.id}_photo`]) ? 1 : 0)) / steps.length) * 100 : 0;

  // Pre-call intent modal
  const callIntentModal = showCallIntent && (
    <div onClick={() => setShowCallIntent(false)} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 10002,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: c.surface, border: `1px solid ${c.border}`, borderRadius: 16,
        padding: 20, maxWidth: 440, width: "100%",
        boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.text, marginBottom: 6 }}>📞 Call {pmName}</div>
        <div style={{ fontSize: 12, color: c.textDim, marginBottom: 14, lineHeight: 1.5 }}>
          Quick heads-up — what do you want to discuss? We'll send {pmName} the context before the call connects, and we'll ask you for a quick recap when you come back.
        </div>
        <textarea value={callIntent} onChange={e => setCallIntent(e.target.value)} rows={3} autoFocus
          placeholder="e.g. Camera system is Walmart 3000, not Eagle Eye — need to confirm IP config approach"
          style={{ width: "100%", padding: "10px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 10, color: c.text, fontSize: 14, outline: "none", resize: "none", marginBottom: 12, boxSizing: "border-box", fontFamily: sans }} />
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowCallIntent(false)} style={{
            padding: "10px 14px", borderRadius: 10, border: `1px solid ${c.border}`,
            background: c.inputBg, color: c.textDim, fontSize: 13, cursor: "pointer",
          }}>Cancel</button>
          <button onClick={() => placeCall("")} style={{
            padding: "10px 14px", borderRadius: 10, border: `1px solid ${c.border}`,
            background: c.inputBg, color: c.textDim, fontSize: 13, cursor: "pointer",
          }}>Skip & call</button>
          <button onClick={() => placeCall(callIntent)} style={{
            flex: 1, padding: "10px 14px", borderRadius: 10, border: "none",
            background: c.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <span>📞</span>
            <span>Call now</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Post-call notes modal — appears when returning from phone app
  const callNotesModal = showCallNotes && pendingCallLog && (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 10003,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{
        background: c.surface, border: `1px solid ${c.accentBorder}`, borderRadius: 16,
        padding: 20, maxWidth: 440, width: "100%",
        boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.text, marginBottom: 4 }}>📝 Welcome back!</div>
        <div style={{ fontSize: 12, color: c.textDim, marginBottom: 14, lineHeight: 1.5 }}>
          What did you and {pmName} discuss? This gets saved to the job audit trail so there's a record of the decision. Type it out or tap the mic to dictate.
        </div>
        <textarea value={callNotesDraft} onChange={e => setCallNotesDraft(e.target.value)} rows={5} autoFocus
          placeholder="e.g. Confirmed IP config needs manual setup. Kelsey will email the new IPs by EOD. Proceed with physical install."
          style={{ width: "100%", padding: "10px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 10, color: c.text, fontSize: 14, outline: "none", resize: "vertical", marginBottom: 10, boxSizing: "border-box", fontFamily: sans }} />
        {hasSpeech && (
          <button onClick={toggleCallNotesListening} style={{
            width: "100%", marginBottom: 12, padding: "10px 14px", borderRadius: 10,
            border: `1px solid ${callNotesListening ? c.accentBorder : c.border}`,
            background: callNotesListening ? c.accentDim : c.inputBg,
            color: callNotesListening ? c.accent : c.textDim,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>{callNotesListening ? "🔴" : "🎙️"}</span>
            <span>{callNotesListening ? "Recording... tap to stop" : "Dictate with voice"}</span>
          </button>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => {
            // Skip without notes — still mark the call as seen
            setPendingCallLog(null);
            setShowCallNotes(false);
            setCallNotesDraft("");
          }} style={{
            padding: "10px 14px", borderRadius: 10, border: `1px solid ${c.border}`,
            background: c.inputBg, color: c.textDim, fontSize: 13, cursor: "pointer",
          }}>Skip</button>
          <button onClick={saveCallNotes} style={{
            flex: 1, padding: "10px 14px", borderRadius: 10, border: "none",
            background: c.accent, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer",
          }}>Save notes</button>
        </div>
      </div>
    </div>
  );

  return (
    <ThemeCtx.Provider value={c}>
    {callIntentModal}
    {callNotesModal}
    <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: c.bg, fontFamily: sans, overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap" rel="stylesheet" />
      {/* Header */}
      <div style={{ padding: "12px 16px 8px", borderBottom: `1px solid ${c.border}`, background: c.surface, flexShrink: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>⚡</div>
            <div>
              <div style={{ fontSize: 12, fontFamily: mono, color: c.accent, fontWeight: 700 }}>{activeTemplate?.title || "CB-OPS"}</div>
              <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono }}>{club} • {techName}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={() => manualSave(false)} disabled={saving} title="Save progress" style={{ padding: "4px 10px", height: 30, borderRadius: 8, border: `1px solid ${c.border}`, background: c.inputBg, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: mono, color: c.textDim, display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>{saving ? "..." : "💾"}</button>
            <button onClick={callPM} disabled={!pmPhone} title={`Call ${pmName}`} style={{ padding: "4px 12px", height: 30, borderRadius: 8, border: `1px solid ${pmPhone ? "rgba(99,102,241,0.3)" : c.border}`, background: pmPhone ? "rgba(99,102,241,0.08)" : c.inputBg, cursor: pmPhone ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700, fontFamily: mono, color: pmPhone ? "rgba(99,102,241,0.9)" : c.textMuted, display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>📞 {pmName.toUpperCase()}</button>
            <button onClick={() => { setHandsFree(h => !h); if (handsFree) window.speechSynthesis?.cancel(); }} title={handsFree ? "Disable hands-free" : "Enable hands-free"} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${handsFree ? c.accentBorder : c.border}`, background: handsFree ? c.accentDim : c.inputBg, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{handsFree ? "🔊" : "🔇"}</button>
            <ThemeToggle mode={mode} onToggle={() => setMode(m => m === "dark" ? "light" : "dark")} />
            <div style={{ fontSize: 19, fontFamily: mono, color: c.text, fontWeight: 800 }}>{step ? stepIdx + 1 : "✓"}<span style={{ color: c.textMuted, fontSize: 12, fontWeight: 500 }}>/{steps.length}</span></div>
          </div>
        </div>
        <div style={{ height: 3, background: c.inputBg, borderRadius: 2, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: `linear-gradient(90deg, ${c.accent}, ${c.accentCyan})`, borderRadius: 2, transition: "width 0.5s ease" }} />
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflow: "auto", padding: "14px 14px 6px", WebkitOverflowScrolling: "touch" }}>
        {messages.map(msg => {
          if (msg.photo) return <PhotoMsg key={msg.id} src={msg.photo} time={msg.time} />;
          return (
            <div key={msg.id}>
              <Bubble from={msg.from} status={msg.status} time={msg.time}>{msg.content}</Bubble>
              {/* Suggestion action buttons attached to bot messages */}
              {msg.suggestions && msg.suggestions.length > 0 && (
                <div style={{ marginLeft: 8, marginRight: 40, marginBottom: 10, marginTop: -4, display: "flex", flexDirection: "column", gap: 6 }}>
                  {msg.suggestions.map((sg, si) => {
                    // Check if this suggestion was already applied
                    const alreadyApplied = sg.kind === "confirm"
                      ? !!cfs[`${step?.id}_${sg.label}`]
                      : false;
                    return (
                      <button key={si} disabled={alreadyApplied} onClick={() => {
                        if (sg.kind === "confirm") {
                          // Apply via handleConfirm which respects photo/gate requirements
                          handleConfirm(sg.label);
                        } else if (sg.kind === "inventory") {
                          const s = steps[siRef.current];
                          if (s?.type === "inventory") {
                            const current = invRef.current[s.id] || [];
                            const updated = { ...invRef.current, [s.id]: [...current, sg.item] };
                            setInventoryData(updated); invRef.current = updated;
                            addMsg("user", `Added: ${Object.values(sg.item).filter(Boolean).join(" / ")}`);
                          }
                        }
                      }} style={{
                        padding: "10px 14px", borderRadius: 10, cursor: alreadyApplied ? "default" : "pointer",
                        border: `1px solid ${alreadyApplied ? c.border : c.accentBorder}`,
                        background: alreadyApplied ? c.inputBg : c.accentDim,
                        color: alreadyApplied ? c.textMuted : c.accent,
                        fontSize: 13, fontWeight: 600, textAlign: "left",
                        display: "flex", alignItems: "center", gap: 8,
                      }}>
                        <span style={{ fontSize: 14 }}>{alreadyApplied ? "✓" : sg.kind === "inventory" ? "➕" : "→"}</span>
                        <span style={{ flex: 1 }}>
                          {alreadyApplied
                            ? (sg.kind === "confirm" ? `Already checked: ${sg.label}` : "Already added")
                            : (sg.kind === "confirm" ? `Tap to check: ${sg.label}` : `Tap to add: ${Object.values(sg.item).filter(Boolean).join(" / ")}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {thinking && <Bubble from="bot" isThinking />}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ borderTop: `1px solid ${c.border}`, background: c.surface, padding: "10px 14px", paddingBottom: "max(10px, env(safe-area-inset-bottom))", flexShrink: 0 }}>
        {step?.type === "inventory" && (
          <InventoryForm
            step={step}
            items={inventoryData[step.id] || []}
            isComplete={!!cfs[`${step.id}_inventory_complete`]}
            onAdd={(item) => {
              const updated = { ...invRef.current, [step.id]: [...(invRef.current[step.id] || []), item] };
              setInventoryData(updated); invRef.current = updated;
              addMsg("user", `Added: ${Object.values(item).filter(Boolean).join(" / ")}`);
              scroll();
            }}
            onRemove={(idx) => {
              const items = [...(invRef.current[step.id] || [])];
              const removed = items.splice(idx, 1)[0];
              const updated = { ...invRef.current, [step.id]: items };
              setInventoryData(updated); invRef.current = updated;
              addMsg("user", `Removed item ${idx + 1}: ${Object.values(removed).filter(Boolean).join(" / ")}`);
            }}
            onComplete={() => {
              const nc = { ...cfsRef.current, [`${step.id}_inventory_complete`]: true };
              setCfs(nc); cfsRef.current = nc;
              const count = (invRef.current[step.id] || []).length;
              addMsg("bot", `**Inventory locked** — ${count} item${count !== 1 ? "s" : ""} captured. Moving on.`, { status: "pass" });
              setTimeout(() => checkDone(nc), 300);
            }}
          />
        )}
        {step?.type === "verify_inventory" && (
          <VerifyInventoryUI
            step={step}
            items={inventoryData[step.referencesStep] || []}
            cfs={cfs}
            onConfirm={(key) => {
              const nc = { ...cfsRef.current, [key]: true };
              setCfs(nc); cfsRef.current = nc;
              setTimeout(() => checkDone(nc), 200);
            }}
          />
        )}
        {/* Override request form */}
        {showOverrideForm && !pendingOverride && (
          <div style={{ marginBottom: 8, padding: 12, background: c.failDim, border: `1px solid rgba(239,68,68,0.2)`, borderRadius: 12 }}>
            <div style={{ fontSize: 11, color: c.fail, fontFamily: mono, fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>REQUEST PM OVERRIDE</div>
            <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)} placeholder="Why should this step be overridden? Explain what you see..."
              rows={2} style={{ width: "100%", padding: "10px 12px", background: c.inputBg, border: `1px solid ${c.border}`, borderRadius: 8, color: c.text, fontSize: 14, fontFamily: sans, outline: "none", resize: "none", boxSizing: "border-box" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button onClick={() => requestOverride(overrideReason)} disabled={!overrideReason.trim()} style={{
                flex: 1, padding: "10px 14px", borderRadius: 10, border: "none",
                background: overrideReason.trim() ? c.fail : c.inputBg, color: overrideReason.trim() ? "#fff" : c.textMuted,
                fontSize: 13, fontWeight: 700, fontFamily: mono, cursor: overrideReason.trim() ? "pointer" : "not-allowed",
              }}>SUBMIT OVERRIDE REQUEST</button>
              <button onClick={() => { setShowOverrideForm(false); setOverrideReason(""); }} style={{
                padding: "10px 14px", borderRadius: 10, border: `1px solid ${c.border}`,
                background: c.inputBg, color: c.textDim, fontSize: 13, cursor: "pointer",
              }}>Cancel</button>
            </div>
          </div>
        )}
        {/* Pending override indicator */}
        {pendingOverride?.status === "pending" && (
          <div style={{ marginBottom: 8, padding: 12, background: "rgba(245,158,11,0.06)", border: `1px solid rgba(245,158,11,0.2)`, borderRadius: 12, display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ display: "flex", gap: 4 }}>{[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: c.warn, opacity: .3, animation: `bk 1.2s ease ${i*.15}s infinite` }} />)}</div>
            <span style={{ fontSize: 13, color: c.warn, fontWeight: 600 }}>Waiting for PM approval...</span>
          </div>
        )}
        {/* Reference images from SOW for current step */}
        {step?.referenceImages?.length > 0 && (
          <div style={{ marginBottom: 8, padding: 12, background: "rgba(99,102,241,0.06)", borderRadius: 12, border: `1px solid rgba(99,102,241,0.2)` }}>
            <div style={{ fontSize: 11, color: "rgba(99,102,241,0.9)", fontFamily: mono, fontWeight: 700, letterSpacing: 1, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <span>📐</span> SOW REFERENCE — {step.referenceImages.length} image{step.referenceImages.length !== 1 ? "s" : ""} for this step
            </div>
            <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
              {step.referenceImages.map((ref, ri) => (
                <div key={ri} style={{ flexShrink: 0, maxWidth: 220 }}>
                  {ref.url ? (
                    <img src={ref.url} alt={ref.description || `Page ${ref.page}`}
                      onClick={() => window.open(ref.url, "_blank")}
                      style={{ width: 220, height: 140, objectFit: "contain", borderRadius: 10, border: `1px solid rgba(99,102,241,0.25)`, cursor: "pointer", background: c.surface }} />
                  ) : (
                    <div style={{ width: 220, height: 140, borderRadius: 10, border: `1px dashed ${c.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: c.textMuted }}>Image unavailable</div>
                  )}
                  <div style={{ fontSize: 10, color: c.textDim, marginTop: 4, lineHeight: 1.3 }}>{ref.description || `SOW page ${ref.page}`}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Guide me button — asks AI to walk the tech through this step interactively */}
        {step && !thinking && (
          <button onClick={() => {
            const msg = `Guide me through this step — walk me through what I need to do for "${step.title}". Ask me one question at a time, explain anything unclear, and if I tell you something is done, propose to mark it off using SUGGEST tokens.`;
            setInput(msg);
            setTimeout(() => sendText(), 50);
          }} style={{
            width: "100%", marginBottom: 8, padding: "10px 14px", borderRadius: 12,
            border: `1px solid ${c.accentBorder}`, background: c.accentDim, color: c.accent,
            fontSize: 13, fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <span>🤖</span>
            <span>Guide me through this step</span>
          </button>
        )}

        {/* Mark Complete button — always shown so tech can force advance when ready.
            Disabled if there are unmet requirements (missing photo, unchecked confirms). */}
        {step && step.type !== "inventory" && (() => {
          const missingPhoto = step.requiresPhoto && !cfs[`${step.id}_photo`];
          const pendingConfirms = (step.confirms || []).filter(x => !cfs[`${step.id}_${x}`]);
          const blockedByGate = step.hardGate && step.aiCheck && (!sRes[step.id] || !sRes[step.id].pass);
          // For verify_inventory with actual items, also check per-item confirms
          let pendingVerify = false;
          if (step.type === "verify_inventory") {
            const items = inventoryData[step.referencesStep] || [];
            if (items.length > 0) {
              pendingVerify = !items.every((_, idx) =>
                (step.confirms_per_item || []).every(label => cfs[`${step.id}_item${idx}_${label}`])
              );
            }
          }
          const canAdvance = !missingPhoto && pendingConfirms.length === 0 && !blockedByGate && !pendingVerify;
          return (
            <button onClick={() => {
              if (!canAdvance) return;
              const nc = { ...cfsRef.current, [`${step.id}_done`]: new Date().toISOString() };
              setCfs(nc); cfsRef.current = nc;
              addMsg("user", "✓ Step complete");
              setTimeout(() => checkDone(nc), 200);
            }} disabled={!canAdvance} style={{
              width: "100%", marginBottom: 8, padding: "12px 16px", borderRadius: 12, border: "none",
              background: canAdvance ? c.accent : c.inputBg,
              color: canAdvance ? "#fff" : c.textMuted,
              fontSize: 14, fontWeight: 700, cursor: canAdvance ? "pointer" : "not-allowed",
            }}>
              {canAdvance
                ? "✓ MARK STEP COMPLETE"
                : missingPhoto
                  ? "📷 Photo required first"
                  : blockedByGate
                    ? "🚫 Hard gate not passed"
                    : `⏳ ${pendingConfirms.length} item${pendingConfirms.length !== 1 ? "s" : ""} to check off`}
            </button>
          );
        })()}
        {step?.confirms?.length > 0 && (
          <div style={{ marginBottom: 8, maxHeight: 160, overflow: "auto" }}>
            {step.confirms.map(label => <ConfirmBtn key={label} label={label} done={!!cfs[`${step.id}_${label}`]} onClick={() => handleConfirm(label)} />)}
          </div>
        )}
        {/* Prominent photo upload banner — first upload (camera + gallery) */}
        {step?.requiresPhoto && !cfs[`${step.id}_photo`] && (
          <>
            {step.photoLabel && (
              <div style={{ fontSize: 12, color: c.textDim, fontWeight: 600, marginBottom: 6, textAlign: "center" }}>
                Upload photo — {step.photoLabel}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <button onClick={() => cameraRef.current?.click()} style={{
                flex: 1, padding: "14px 12px",
                background: `linear-gradient(135deg, ${c.accent}, ${c.accentCyan})`,
                border: "none", borderRadius: 12, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                color: "#fff", fontSize: 14, fontWeight: 700, boxShadow: c.shadow,
              }}>
                <span style={{ fontSize: 20 }}>📷</span>
                <span>TAKE PHOTO</span>
              </button>
              <button onClick={() => fileRef.current?.click()} style={{
                flex: 1, padding: "14px 12px",
                background: c.surface,
                border: `1.5px solid ${c.accentBorder}`, borderRadius: 12, cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                color: c.accent, fontSize: 14, fontWeight: 700,
              }}>
                <span style={{ fontSize: 20 }}>🖼️</span>
                <span>FROM GALLERY</span>
              </button>
            </div>
          </>
        )}
        {/* After first upload — show count and "Add More" buttons (camera + gallery) */}
        {step?.requiresPhoto && cfs[`${step.id}_photo`] && (() => {
          const stepPhotoCount = photoIds.filter(p => p.step_id === step.id).length;
          return (
            <div style={{ width: "100%", marginBottom: 8, padding: "10px 14px", background: c.accentDim, border: `1px solid ${c.accentBorder}`, borderRadius: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 16 }}>✅</span>
              <span style={{ fontSize: 13, color: c.accent, fontWeight: 600, flex: 1, minWidth: 120 }}>{stepPhotoCount} photo{stepPhotoCount !== 1 ? "s" : ""} uploaded</span>
              <button onClick={() => cameraRef.current?.click()} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${c.accentBorder}`, background: c.surface, color: c.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: mono }}>📷 CAMERA</button>
              <button onClick={() => fileRef.current?.click()} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${c.accentBorder}`, background: c.surface, color: c.accent, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: mono }}>🖼️ GALLERY</button>
            </div>
          );
        })()}
        {/* Photo gallery for current step — shows all uploaded photos as thumbnails */}
        {step && (() => {
          const stepPhotos = photoIds.filter(p => p.step_id === step.id);
          if (stepPhotos.length === 0) return null;
          return (
            <div style={{ marginBottom: 8, padding: "8px 10px", background: c.inputBg, borderRadius: 10, border: `1px solid ${c.border}` }}>
              <div style={{ fontSize: 10, color: c.textMuted, fontFamily: mono, fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>📷 STEP PHOTOS ({stepPhotos.length})</div>
              <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
                {stepPhotos.map((p, i) => (
                  <div key={p.id} style={{ position: "relative", flexShrink: 0 }}>
                    <img
                      src={p.url || `/api/photos/${p.id}`}
                      alt={`Photo ${i + 1}`}
                      onClick={() => window.open(p.url || `/api/photos/${p.id}`, "_blank")}
                      style={{ width: 80, height: 60, objectFit: "cover", borderRadius: 6, border: `2px solid ${p.ai_pass === true ? c.accent : p.ai_pass === false ? c.fail : c.border}`, cursor: "pointer" }}
                    />
                    {p.ai_pass !== undefined && p.ai_pass !== null && (
                      <div style={{ position: "absolute", top: 2, right: 2, width: 16, height: 16, borderRadius: 8, background: p.ai_pass ? c.accent : c.fail, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", fontWeight: 700 }}>{p.ai_pass ? "✓" : "✗"}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        {/* Photo buttons for steps that don't require a photo but the tech wants to add one */}
        {step && !step.requiresPhoto && (
          <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
            <button onClick={() => cameraRef.current?.click()} style={{
              flex: 1, padding: "8px 10px", borderRadius: 10,
              border: `1px dashed ${c.border}`, background: "transparent",
              color: c.textDim, fontSize: 12, fontWeight: 500, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <span>📷</span>
              <span>Take photo</span>
            </button>
            <button onClick={() => fileRef.current?.click()} style={{
              flex: 1, padding: "8px 10px", borderRadius: 10,
              border: `1px dashed ${c.border}`, background: "transparent",
              color: c.textDim, fontSize: 12, fontWeight: 500, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            }}>
              <span>🖼️</span>
              <span>From gallery</span>
            </button>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhoto} />
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple style={{ display: "none" }} onChange={handlePhoto} />
          {hasSpeech && (
            <button onClick={toggleListening} style={{
              width: 46, height: 46, borderRadius: 13, flexShrink: 0,
              border: `1.5px solid ${listening ? c.accentBorder : c.border}`,
              background: listening ? c.accentDim : c.inputBg,
              color: listening ? c.accent : c.textDim,
              fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
              animation: listening ? "pulse 1.5s ease infinite" : "none",
            }}>🎙️</button>
          )}
          {listening && <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.5}}`}</style>}
          <div style={{ flex: 1, display: "flex", background: c.inputBg, borderRadius: 13, border: `1.5px solid ${listening ? c.accentBorder : c.border}`, overflow: "hidden", transition: "border-color 0.2s" }}>
            <input value={input} onChange={e => setInput(e.target.value)} placeholder={listening ? "Listening..." : "Message CB-OPS..."}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); }}}
              style={{ flex: 1, padding: "13px 14px", background: "transparent", border: "none", color: c.text, fontSize: 16, fontFamily: sans, outline: "none" }} />
            <button onClick={sendText} disabled={!input.trim() || thinking} style={{
              padding: "0 15px", background: "transparent", border: "none",
              color: input.trim() && !thinking ? c.accent : c.textMuted,
              fontSize: 20, cursor: input.trim() && !thinking ? "pointer" : "default", fontWeight: 700,
            }}>↑</button>
          </div>
        </div>
      </div>
    </div>
    </ThemeCtx.Provider>
  );
}
